import React, { useState, useEffect, useMemo } from 'react'
import { 
  ShoppingCartIcon, 
  PlusIcon, 
  MinusIcon, 
  XMarkIcon,
  CreditCardIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  UserIcon,
  // TableCellsIcon,
  ClockIcon,
  CheckIcon,
  StarIcon,
  // AdjustmentsHorizontalIcon,
  SparklesIcon,
  ArrowPathIcon,
  BellIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  DevicePhoneMobileIcon,
  FireIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  PrinterIcon,
  TrashIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline'
import { useCategories } from '../contexts/CategoryContext'
import { useAuth } from '../contexts/AuthContext'
import { apiService, resolveImageUrl, formatItemName, consolidateOrderItems, deduplicateOrderItems } from '../services/api'
import NumericKeypad from '../components/UI/NumericKeypad'
import AllergenIcons, { type AllergenInfo } from '../components/UI/AllergenIcons'
import ThermalReceipt from '../components/Receipt/ThermalReceipt'
import { CreditDinnerModal } from '../components/CreditDinnerModal'
import { calculateOrderTotals, splitVatInclusive } from '../utils/tax'
import { compareProductCode } from '../utils/productCodeSort'

interface Product {
  id: string
  name: string
  basePrice: number
  categoryId: string
  code?: string
  image?: string
  description?: string
  isActive: boolean
  isPopular?: boolean
  variants?: MenuVariant[]
  addons?: Addon[]
  upsells?: Upsell[]
  crossSells?: CrossSell[]
  tags?: string[]
  preparationTime?: number
  calories?: number
  stepsToBurn?: number
  allergens?: string[]
  kdsStationId?: string
}

interface MenuVariant {
  id: string
  name: string
  priceModifier: number
  isDefault: boolean
  allergen_ids?: string[]
}

interface Addon {
  id: string
  name: string
  price: number
  category: string
  isRequired: boolean
  maxSelection?: number
  allergen_ids?: string[]
}

interface Upsell {
  id: string
  name: string
  discount: number
  triggerAmount: number
}

interface CrossSell {
  id: string
  name: string
  discount: number
  relatedItems: string[]
}

interface CartItem {
  id: string
  name: string
  description?: string
  basePrice: number
  finalPrice: number
  kdsStationId?: string
  product: Product
  quantity: number
  notes?: string
  selectedVariant?: MenuVariant | null
  selectedAddons?: Addon[]
  modifiers?: any[]
  isComboItem?: boolean
  comboPrice?: number
  originalPrice?: number
  isBOGOItem?: boolean
  bogoDiscountType?: 'free' | 'percentage' | 'fixed'
  bogoDiscountValue?: number
  paidQuantity?: number
  freeQuantity?: number
  isFreeItem?: boolean
  visitBasedOfferId?: string
  discountOfferId?: string
  discountOriginalPrice?: number
  discountType?: 'percentage' | 'fixed'
  discountValue?: number
}

interface Customer {
  id: string
  name?: string
  mobileNumber?: string
  phone?: string
  firstName?: string
  lastName?: string
  email?: string
  age?: number
  gender?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  dateOfBirth?: string
  notes?: string
  companyName?: string
  vatNumber?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface CustomerField {
  id: string
  fieldName: string
  fieldLabel: string
  fieldType: 'text' | 'email' | 'number' | 'date' | 'select' | 'textarea'
  isRequired: boolean
  isActive: boolean
  options?: string[]
  validationRules?: any
  sortOrder: number
}

interface RestaurantSettings {
  restaurant_name: string
  phone_number: string
  address: string
  email: string
  website: string
  vat_number?: string
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
  timezone: string
  language: string
  tax_rate: number
  service_charge: number
  dual_invoicing_enabled?: boolean
  zatca_phase1_enabled?: boolean
}

interface POSSettings {
  guestCustomerName: string
  guestCustomerStartingNumber: string
  enableGuestOrdering: boolean
  autoIncrementGuestNumber: boolean
  requireMobileNumber: boolean
  enableDiscountSystem: boolean
  maxDiscountPercentage: number
  maxDiscountAmount: number
  returnDurationHours: number
  allowZeroCashRegisterOpening?: boolean
  allowDineInFromPOS?: boolean
  allowCateringOrders?: boolean
  enableTokenSystem?: boolean
  tokenStartNumber?: number
  tokenResetFrequency?: 'never' | 'daily'
  tokenMode?: 'shared' | 'separate' | 'pos_only' | 'waiter_only'
  enableAutoPrint?: boolean
  printerType?: 'tcp_ip' | 'usb' | 'network' | 'system' | 'system'
  printerName?: string
  printerIp?: string
  printerPort?: number
  printerModel?: string
  printerWidth?: number
  receiptLogoUrl?: string
  receiptLayout?: any[]
  creditDinnerEnabled?: boolean
}

interface Offer {
  id: string
  name: string
  description: string
  type: 'percentage' | 'fixed' | 'bogo' | 'combo'
  value: number
  isActive: boolean
  applicableItems: string[]
  minOrderAmount?: number
  maxDiscount?: number
}

const POS: React.FC = () => {
  const { categories, getAllCategoriesFlat: _getAllCategoriesFlat, getCategoryById } = useCategories()
  const { user } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [allergenMap, setAllergenMap] = useState<Record<string, AllergenInfo>>({})
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState<CartItem[]>([])
  const [selectedTable, setSelectedTable] = useState<string>('')
  const [selectedTableId, setSelectedTableId] = useState<string>('')
  const [tables, setTables] = useState<any[]>([])
  const [showTableSelector, setShowTableSelector] = useState(false)
  const [showClearCartModal, setShowClearCartModal] = useState(false)
  const [orderType, setOrderType] = useState<'dine-in' | 'takeaway' | 'catering'>('takeaway')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [discount, setDiscount] = useState(0)
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage')
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'credit' | ''>('')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false)
  const [deliveryDate, setDeliveryDate] = useState<string>('')
  const [deliveryTime, setDeliveryTime] = useState<string>('')
  const [deliveryAddress, setDeliveryAddress] = useState<string>('')
  const [receivedAmount, setReceivedAmount] = useState('')
  const [showCashKeypad, setShowCashKeypad] = useState(false)
  const [showCustomizationModal, setShowCustomizationModal] = useState(false)
  const [showUpsellModal, setShowUpsellModal] = useState(false)
  const [showOffersModal, setShowOffersModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<MenuVariant | null>(null)
  const [selectedAddons, setSelectedAddons] = useState<Addon[]>([])
  const [orderNotes, setOrderNotes] = useState('')
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [availableOffers, setAvailableOffers] = useState<Offer[]>([])
  const [appliedOffers, setAppliedOffers] = useState<Offer[]>([])
  const [availableVisitBasedOffers, setAvailableVisitBasedOffers] = useState<any[]>([])
  const [appliedVisitBasedOffers, setAppliedVisitBasedOffers] = useState<any[]>([])
  const [appliedUpsells, setAppliedUpsells] = useState<Upsell[]>([])
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all')
  const [productSearchTerm, setProductSearchTerm] = useState<string>('')
  const [productsToShowCount, setProductsToShowCount] = useState(15)
  const [orderNotesExpanded, setOrderNotesExpanded] = useState(false)
  const [discountExpanded, setDiscountExpanded] = useState(false)
  const [lastOrderToken, setLastOrderToken] = useState<number | null>(null)
  const [receiptData, setReceiptData] = useState<any>(null)
  const [pendingPaymentOrders, setPendingPaymentOrders] = useState<any[]>([])
  const [showPendingPayments, setShowPendingPayments] = useState(false)
  const [loadingPendingPayments, setLoadingPendingPayments] = useState(false)
  const [showPendingPaymentModal, setShowPendingPaymentModal] = useState(false)
  const [selectedPendingOrder, setSelectedPendingOrder] = useState<any>(null)
  const [pendingPaymentReceivedAmount, setPendingPaymentReceivedAmount] = useState('')
  const [showPendingPaymentKeypad, setShowPendingPaymentKeypad] = useState(false)
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState<'cash' | 'card'>('cash')
  
  // Split Payment State
  const [isSplitPayment, setIsSplitPayment] = useState(false)
  const [splitPayments, setSplitPayments] = useState<Array<{
    id: string
    payment_method: 'cash' | 'card' | 'digital'
    amount: number
    received_amount?: number
    change_amount?: number
    reference_number?: string
  }>>([])
  const [currentSplitMethod, setCurrentSplitMethod] = useState<'cash' | 'card' | 'digital'>('cash')
  const [currentSplitAmount, setCurrentSplitAmount] = useState('')
  const [currentSplitReceived, setCurrentSplitReceived] = useState('')
  const [showSplitKeypad, setShowSplitKeypad] = useState(false)
  const [splitKeypadTarget, setSplitKeypadTarget] = useState<'amount' | 'received'>('amount')
  
  // Customer Management State
  const [customerFields, setCustomerFields] = useState<CustomerField[]>([])
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    restaurant_name: 'Mufaza Restaurant',
    phone_number: '+966 11 123 4567',
    address: '123 King Fahd Road, Riyadh, Saudi Arabia',
    email: 'info@mufaza.com',
    website: 'www.mufaza.com',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: '',
    timezone: 'Asia/Riyadh',
    language: 'English',
    tax_rate: 15,
    service_charge: 10
  })
  const [posSettings, setPOSSettings] = useState<POSSettings>({
    guestCustomerName: 'Guest',
    guestCustomerStartingNumber: '10001',
    enableGuestOrdering: true,
    autoIncrementGuestNumber: true,
    requireMobileNumber: true,
    enableDiscountSystem: true,
    maxDiscountPercentage: 50,
    maxDiscountAmount: 100,
    creditDinnerEnabled: false,
    returnDurationHours: 24
  })
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showPhoneKeypad, setShowPhoneKeypad] = useState(true)
  const [customerFormData, setCustomerFormData] = useState<Partial<Customer>>({})
  const [isGuestCustomer, setIsGuestCustomer] = useState(true)
  const [customerVisitCount, setCustomerVisitCount] = useState<number | null>(null)

  // Cash Register State
  const [registerStatus, setRegisterStatus] = useState<{ isOpen: boolean; needsOpening: boolean; session?: any } | null>(null)
  const [showOpeningModal, setShowOpeningModal] = useState(false)
  const [showClosingModal, setShowClosingModal] = useState(false)
  const [openingCash, setOpeningCash] = useState('')
  const [closingCash, setClosingCash] = useState('')
  const [isProcessingRegister, setIsProcessingRegister] = useState(false)
  const [isPrintingClosingReport, setIsPrintingClosingReport] = useState(false)
  const [isRefreshingRegisterStatus, setIsRefreshingRegisterStatus] = useState(false)
  
  // Returns/Refunds State
  const [showReturnsModal, setShowReturnsModal] = useState(false)
  const [orderSearchInput, setOrderSearchInput] = useState('')
  const [searchedOrder, setSearchedOrder] = useState<any>(null)
  const [isSearchingOrder, setIsSearchingOrder] = useState(false)
  const [_existingReturns, setExistingReturns] = useState<any[]>([])
  const [refundedItemsMap, setRefundedItemsMap] = useState<Map<string, number>>(new Map()) // Map of order_item_id -> quantity already refunded
  const [selectedReturnItems, setSelectedReturnItems] = useState<Array<{ order_item_id: string; product_id?: string; product_name: string; quantity: number; unit_price: number; total_price: number; return_reason?: string }>>([])
  const [returnQuantities, setReturnQuantities] = useState<Map<string, number>>(new Map()) // Map of order_item_id -> return quantity
  const [returnReason, setReturnReason] = useState('')
  const [refundMethod, setRefundMethod] = useState<'cash' | 'card' | 'bank_transfer' | 'store_credit'>('cash')
  const [showRefundAuthModal, setShowRefundAuthModal] = useState(false)
  const [refundAuthCode, setRefundAuthCode] = useState('')
  const [isVerifyingRefundAuth, setIsVerifyingRefundAuth] = useState(false)
  const [isProcessingReturn, setIsProcessingReturn] = useState(false)
  const [currentReturnId, setCurrentReturnId] = useState<string | null>(null)
  const [returnToInventory, setReturnToInventory] = useState(false)

  // Order Approval State
  const [pendingOrders, setPendingOrders] = useState<any[]>([])
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [_selectedOrderForApproval, setSelectedOrderForApproval] = useState<any>(null)
  const [_showEditOrderModal, _setShowEditOrderModal] = useState(false)
  const [isApprovingOrder, setIsApprovingOrder] = useState(false)
  
  // Waiter Draft Orders State
  const [draftOrders, setDraftOrders] = useState<any[]>([])
  const [draftOrdersCount, setDraftOrdersCount] = useState(0)
  const [showDraftOrdersModal, setShowDraftOrdersModal] = useState(false)
  const [_selectedDraftOrder, setSelectedDraftOrder] = useState<any>(null)
  const [_isEditingDraft, setIsEditingDraft] = useState(false)
  const [_editedDraftItems, _setEditedDraftItems] = useState<any[]>([])
  const [_editedDraftData, _setEditedDraftData] = useState<any>({})
  
  // Modification/Cancellation Requests State
  const [modificationRequests, setModificationRequests] = useState<any[]>([])
  const [cancellationRequests, setCancellationRequests] = useState<any[]>([])
  const [modificationRequestsCount, setModificationRequestsCount] = useState(0)
  const [cancellationRequestsCount, setCancellationRequestsCount] = useState(0)
  const [showModificationRequestsModal, setShowModificationRequestsModal] = useState(false)
  const [showCancellationRequestsModal, setShowCancellationRequestsModal] = useState(false)
  // const [selectedModificationRequest, setSelectedModificationRequest] = useState<any>(null)
  // const [selectedCancellationRequest, setSelectedCancellationRequest] = useState<any>(null)
  const [isProcessingRequest, setIsProcessingRequest] = useState(false)
  
  // Bill Requests State
  const [billRequests, setBillRequests] = useState<any[]>([])
  const [billRequestsCount, setBillRequestsCount] = useState(0)
  const [showBillRequestsModal, setShowBillRequestsModal] = useState(false)
  const [isProcessingBill, setIsProcessingBill] = useState(false)
  const [showCreditDinnerModal, setShowCreditDinnerModal] = useState(false)
  const [creditDinnerCoName, setCreditDinnerCoName] = useState('')
  const [creditDinnerCustomerName, setCreditDinnerCustomerName] = useState('')
  const [creditDinnerModalContext, setCreditDinnerModalContext] = useState<
    null | 'payment' | 'bill_duplicate' | 'bill_final'
  >(null)
  const [pendingFinalInvoicePayload, setPendingFinalInvoicePayload] = useState<{
    payment_method: string
    paid_amount: number
    change_amount?: number
    is_split_payment?: boolean
    split_payments?: Array<{ payment_method: string; amount: number; received_amount?: number; change_amount?: number }>
    split_payment_details?: Record<string, unknown>
  } | null>(null)
  const [pendingDuplicateRequestId, setPendingDuplicateRequestId] = useState<string | null>(null)
  const [pendingFinalRequestId, setPendingFinalRequestId] = useState<string | null>(null)
  const [billRequestSearchTerm, setBillRequestSearchTerm] = useState('')
  // Bill Request Discount State (per request)
  const [billRequestDiscounts, setBillRequestDiscounts] = useState<Record<string, {
    value: number
    type: 'percentage' | 'fixed'
    authorizedBy?: string
    authorizedBySecret?: { id: string; name: string }
  }>>({})
  const [billRequestDiscountInputs, setBillRequestDiscountInputs] = useState<Record<string, string>>({})
  const [billRequestDiscountTypes, setBillRequestDiscountTypes] = useState<Record<string, 'percentage' | 'fixed'>>({})
  const [showBillRequestDiscountModal, setShowBillRequestDiscountModal] = useState<string | null>(null)
  const [showBillRequestDiscountAuthModal, setShowBillRequestDiscountAuthModal] = useState<string | null>(null)
  const [pendingBillRequestDiscount, setPendingBillRequestDiscount] = useState<{ requestId: string; value: number; type: 'percentage' | 'fixed' } | null>(null)
  const [billRequestAuthCode, setBillRequestAuthCode] = useState('')
  const [isVerifyingBillRequestAuth, setIsVerifyingBillRequestAuth] = useState(false)
  // Final invoice payment modal: ask payment method before generating final invoice
  const [finalInvoicePaymentModal, setFinalInvoicePaymentModal] = useState<{ requestId: string; orderTotal: number; tableNumber?: string } | null>(null)
  const [finalInvoicePaymentMethod, setFinalInvoicePaymentMethod] = useState<'cash' | 'card' | 'split'>('card')
  const [finalInvoicePaidAmount, setFinalInvoicePaidAmount] = useState('')
  // Split payment for final invoice (separate from main POS split)
  const [finalInvoiceSplitPayments, setFinalInvoiceSplitPayments] = useState<Array<{ id: string; payment_method: 'cash' | 'card' | 'digital'; amount: number; received_amount?: number; change_amount?: number }>>([])
  const [finalInvoiceSplitCurrentMethod, setFinalInvoiceSplitCurrentMethod] = useState<'cash' | 'card' | 'digital'>('cash')
  const [finalInvoiceSplitCurrentAmount, setFinalInvoiceSplitCurrentAmount] = useState('')
  const [finalInvoiceSplitCurrentReceived, setFinalInvoiceSplitCurrentReceived] = useState('')

  // Discount Authorization State (for POS cart)
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [showDiscountAuthModal, setShowDiscountAuthModal] = useState(false)
  const [pendingDiscount, setPendingDiscount] = useState<{ value: number; type: 'percentage' | 'fixed' } | null>(null)
  const [discountInputValue, setDiscountInputValue] = useState('')
  const [authCode, setAuthCode] = useState('')
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(false)
  const [discountAuthorizedBy, setDiscountAuthorizedBy] = useState<string | null>(null) // User ID who authorized the discount
  const [discountAuthorizedBySecret, setDiscountAuthorizedBySecret] = useState<{ id: string; name: string } | null>(null) // TOTP secret ID and name that authorized the discount

  useEffect(() => {
    loadData()
    checkRegisterStatus()
  }, [])

  // Prevent leaving POS if register is open (for closing register)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (registerStatus?.isOpen) {
        e.preventDefault()
        e.returnValue = 'Please close the cash register before leaving the page.'
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [registerStatus])

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Show phone keypad by default when customer modal opens
  useEffect(() => {
    if (showCustomerModal) {
      setShowPhoneKeypad(true)
    }
  }, [showCustomerModal])

  const loadPendingPaymentOrders = async () => {
    setLoadingPendingPayments(true)
    try {
      const response = await apiService.getPendingPaymentOrders()
      if (response.success && response.data) {
        setPendingPaymentOrders(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading pending payment orders:', error)
      setPendingPaymentOrders([])
    } finally {
      setLoadingPendingPayments(false)
    }
  }

  const handleMarkPaymentReceived = async (orderId: string, paymentMethod: 'cash' | 'card', receivedAmount: number) => {
    try {
      const order = pendingPaymentOrders.find(o => o.id === orderId)
      if (!order) return

      const changeAmount = paymentMethod === 'cash' ? Math.max(0, receivedAmount - order.total_amount) : 0

      const response = await apiService.markPaymentReceived(orderId, {
        payment_method: paymentMethod,
        paid_amount: receivedAmount,
        change_amount: changeAmount
      })

      if (response.success) {
        setToast({
          message: 'Payment marked as received successfully',
          type: 'success'
        })
        // Close modal and reset state
        setShowPendingPaymentModal(false)
        setSelectedPendingOrder(null)
        setPendingPaymentReceivedAmount('')
        setShowPendingPaymentKeypad(false)
        // Reload pending payment orders
        await loadPendingPaymentOrders()
      } else {
        setToast({
          message: response.error || 'Failed to mark payment as received',
          type: 'error'
        })
      }
    } catch (error) {
      console.error('Error marking payment as received:', error)
      setToast({
        message: 'Error marking payment as received',
        type: 'error'
      })
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        loadProducts(),
        loadAllergens(),
        loadOffers(),
        loadRestaurantSettings(),
        loadCustomerFields(),
        loadPOSSettings(),
        loadPendingOrders(),
        loadDraftOrders(),
        loadTables()
      ])
    } catch (error) {
      console.error('Error loading POS data:', error)
    } finally {
      setLoading(false)
    }
  }
  
  // Load draft orders (waiter orders pending approval)
  const loadDraftOrders = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/orders/drafts`)
      const data = await response.json()
      if (data.success) {
        const orders = data.data || []
        setDraftOrders(orders)
        setDraftOrdersCount(orders.length)
      } else {
        console.error('Failed to load draft orders:', data.error)
      }
    } catch (error) {
      console.error('Error loading draft orders:', error)
    }
  }

  // Load modification requests
  const loadModificationRequests = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/orders/modification-requests/pending`)
      const data = await response.json()
      if (data.success) {
        setModificationRequests(data.data || [])
        setModificationRequestsCount(data.data?.length || 0)
      }
    } catch (error) {
      console.error('Error loading modification requests:', error)
    }
  }

  // Load cancellation requests
  const loadCancellationRequests = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/orders/cancellation-requests/pending`)
      const data = await response.json()
      if (data.success) {
        setCancellationRequests(data.data || [])
        setCancellationRequestsCount(data.data?.length || 0)
      }
    } catch (error) {
      console.error('Error loading cancellation requests:', error)
    }
  }

  // Load bill requests
  const loadBillRequests = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/bill-requests/pending`)
      const data = await response.json()
      if (data.success) {
        const raw = data.data || []
        const byId = new Map()
        raw.forEach((r: any) => {
          if (r.invoice_status === 'final_generated') return
          if (r.id && !byId.has(r.id)) byId.set(r.id, r)
        })
        const list = Array.from(byId.values())
        setBillRequests(list)
        setBillRequestsCount(list.length)
      }
    } catch (error) {
      console.error('Error loading bill requests:', error)
    }
  }
  
  // Poll for draft orders and requests every 5 seconds
  useEffect(() => {
    const loadAll = () => {
      loadDraftOrders()
      loadModificationRequests()
      loadCancellationRequests()
      loadBillRequests()
    }
    
    loadAll()
    const interval = setInterval(loadAll, 5000) // Poll every 5 seconds
    
    return () => clearInterval(interval)
  }, [])

  const loadPendingOrders = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/orders/pending-approval`)
      const data = await response.json()
      if (data.success) {
        setPendingOrders(data.data || [])
        setPendingOrdersCount(data.data?.length || 0)
      }
    } catch (error) {
      console.error('Error loading pending orders:', error)
    }
  }

  const handleApproveOrder = async (orderId: string) => {
    setIsApprovingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      // Only send approved_by if user.id is a valid UUID, otherwise send null
      const approvedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      const response = await fetch(`${apiUrl}/orders/${orderId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approvedBy })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Order approved successfully', type: 'success' })
        await loadPendingOrders()
        setShowApprovalModal(false)
        setSelectedOrderForApproval(null)
      } else {
        setToast({ message: data.error || 'Failed to approve order', type: 'error' })
      }
    } catch (error) {
      console.error('Error approving order:', error)
      setToast({ message: 'Failed to approve order', type: 'error' })
    } finally {
      setIsApprovingOrder(false)
    }
  }

  const handleRejectOrder = async (orderId: string) => {
    setIsApprovingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      // Only send rejected_by if user.id is a valid UUID, otherwise send null
      const rejectedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      const response = await fetch(`${apiUrl}/orders/${orderId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejected_by: rejectedBy })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Order rejected', type: 'success' })
        await loadPendingOrders()
        setShowApprovalModal(false)
        setSelectedOrderForApproval(null)
      } else {
        setToast({ message: data.error || 'Failed to reject order', type: 'error' })
      }
    } catch (error) {
      console.error('Error rejecting order:', error)
      setToast({ message: 'Failed to reject order', type: 'error' })
    } finally {
      setIsApprovingOrder(false)
    }
  }
  
  // Handle approve draft order
  const handleApproveDraftOrder = async (orderId: string) => {
    setIsApprovingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      // Only send approved_by if user.id is a valid UUID, otherwise send null
      const approvedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      // Check if this is a pending additional items approval
      const order = draftOrders.find((o: any) => o.id === orderId)
      const isPendingAdditionalItems = order?.has_pending_additional_items
      
      const endpoint = isPendingAdditionalItems 
        ? `${apiUrl}/orders/${orderId}/approve-additional-items`
        : `${apiUrl}/orders/drafts/${orderId}/approve`
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approvedBy })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ 
          message: isPendingAdditionalItems 
            ? 'Additional items approved and sent to KDS' 
            : 'Draft order approved and sent to KDS', 
          type: 'success' 
        })
        await loadDraftOrders()
        setShowDraftOrdersModal(false)
        setSelectedDraftOrder(null)
        setIsEditingDraft(false)
      } else {
        setToast({ message: data.error || 'Failed to approve', type: 'error' })
      }
    } catch (error) {
      console.error('Error approving:', error)
      setToast({ message: 'Failed to approve', type: 'error' })
    } finally {
      setIsApprovingOrder(false)
    }
  }

  // Handle modification request approval
  const handleApproveModificationRequest = async (requestId: string) => {
    setIsProcessingRequest(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const approvedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      const response = await fetch(`${apiUrl}/orders/modification-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approvedBy })
      })
      
      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Modification request approved successfully', type: 'success' })
        await loadModificationRequests()
        await loadDraftOrders()
      } else {
        setToast({ message: data.error || 'Failed to approve modification request', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error approving modification request:', error)
      setToast({ message: 'Failed to approve modification request', type: 'error' })
    } finally {
      setIsProcessingRequest(false)
    }
  }

  // Handle cancellation request approval
  const handleApproveCancellationRequest = async (requestId: string) => {
    setIsProcessingRequest(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const approvedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      const response = await fetch(`${apiUrl}/orders/cancellation-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: approvedBy })
      })
      
      const data = await response.json()
      if (data.success) {
        const wastageCount = data.data?.wastage_count || 0
        setToast({ 
          message: `Cancellation approved. ${wastageCount} item(s) marked as wastage.`, 
          type: 'success' 
        })
        await loadCancellationRequests()
        await loadDraftOrders()
      } else {
        setToast({ message: data.error || 'Failed to approve cancellation request', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error approving cancellation request:', error)
      setToast({ message: 'Failed to approve cancellation request', type: 'error' })
    } finally {
      setIsProcessingRequest(false)
    }
  }

  // Handle bill request processing
  // const handleProcessBillRequest = async (requestId: string) => {
  //   setIsProcessingBill(true)
  //   try {
  //     const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
  //     const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
  //     const processedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
  //     
  //     const response = await fetch(`${apiUrl}/bill-requests/${requestId}/process`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ processed_by: processedBy })
  //     })
  //     
  //     const data = await response.json()
  //     if (data.success) {
  //       setToast({ message: 'Bill request marked as processing', type: 'success' })
  //       await loadBillRequests()
  //     } else {
  //       setToast({ message: data.error || 'Failed to process bill request', type: 'error' })
  //     }
  //   } catch (error: any) {
  //     console.error('Error processing bill request:', error)
  //     setToast({ message: 'Failed to process bill request', type: 'error' })
  //   } finally {
  //     setIsProcessingBill(false)
  //   }
  // }

  // Handle bill request completion
  // const handleCompleteBillRequest = async (requestId: string) => {
  //   setIsProcessingBill(true)
  //   try {
  //     const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
  //     const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
  //     const processedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
  //     
  //     const response = await fetch(`${apiUrl}/bill-requests/${requestId}/complete`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ processed_by: processedBy })
  //     })
  //     
  //     const data = await response.json()
  //     if (data.success) {
  //       setToast({ message: 'Bill request marked as completed', type: 'success' })
  //       await loadBillRequests()
  //     } else {
  //       setToast({ message: data.error || 'Failed to complete bill request', type: 'error' })
  //     }
  //   } catch (error: any) {
  //     console.error('Error completing bill request:', error)
  //     setToast({ message: 'Failed to complete bill request', type: 'error' })
  //   } finally {
  //     setIsProcessingBill(false)
  //   }
  // }

  // Handle duplicate invoice generation
  const handleGenerateDuplicateInvoice = async (
    requestId: string,
    credit?: { coName: string; customerName: string }
  ) => {
    const requestRow = billRequests.find(r => r.id === requestId)
    const orderRow = requestRow?.orders || requestRow?.order || {}
    const brTotals = calculateBillRequestTotals(orderRow, requestId)
    const d = billRequestDiscounts[requestId]
    let brFullComp = false
    if (d && posSettings.creditDinnerEnabled) {
      if (d.type === 'percentage' && d.value >= 99.99) brFullComp = true
      else brFullComp = brTotals.subtotal > 0 && brTotals.discountAmount >= brTotals.subtotal - 0.05
    }
    if (posSettings.creditDinnerEnabled && brFullComp && !credit) {
      setCreditDinnerModalContext('bill_duplicate')
      setPendingDuplicateRequestId(requestId)
      setCreditDinnerCustomerName(orderRow.customer_name || 'Guest')
      setShowCreditDinnerModal(true)
      return
    }

    setIsProcessingBill(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const generatedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      const discount = billRequestDiscounts[requestId]
      const discountData = discount ? { discount_amount: discount.value, discount_type: discount.type } : {}
      const token = localStorage.getItem('pos-auth-token') || sessionStorage.getItem('pos-auth-token')
      const body: Record<string, unknown> = { generated_by: generatedBy, ...discountData }
      if (credit && posSettings.creditDinnerEnabled && brFullComp) {
        body.credit_dinner_co_name = credit.coName
        body.credit_dinner_customer_display = credit.customerName || orderRow.customer_name || 'Guest'
        body.credit_dinner_amount = brTotals.subtotal
      }
      const response = await fetch(`${apiUrl}/bill-requests/${requestId}/generate-duplicate-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      })
      
      const data = await response.json()
      if (data.success) {
        const d = data.data || {}
        if (d.print_attempted === true) {
          console.log('[Duplicate Invoice Print] Attempted:', d.print_success ? 'OK' : 'Failed', d.printer_ip ? `@ ${d.printer_ip}` : '', d.print_message || '')
        } else if (d.print_attempted === false || d.print_message) {
          console.log('[Duplicate Invoice Print] Not attempted:', d.print_message || 'No printer IP in Settings. Set Printer IP in Settings → POS → Invoice Printer.')
        } else {
          console.log('[Duplicate Invoice Print] Backend did not return print status. Restart backend to get printer IP from Settings.')
        }
        setToast({ message: `Duplicate invoice generated: ${d.invoice_number}`, type: 'success' })
        await loadBillRequests()
      } else {
        setToast({ message: data.error || 'Failed to generate duplicate invoice', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error generating duplicate invoice:', error)
      setToast({ message: 'Failed to generate duplicate invoice', type: 'error' })
    } finally {
      setIsProcessingBill(false)
    }
  }

  const handleCancelBillRequest = async (requestId: string) => {
    if (!window.confirm('Cancel this bill request? The waiter will be able to request the bill again.')) return
    setIsProcessingBill(true)
    try {
      const data = await apiService.cancelBillRequest(requestId)
      if (data.success) {
        setToast({ message: (data as { message?: string }).message || 'Bill request cancelled', type: 'success' })
        setBillRequestDiscounts(prev => {
          const next = { ...prev }
          delete next[requestId]
          return next
        })
        setBillRequestDiscountInputs(prev => {
          const next = { ...prev }
          delete next[requestId]
          return next
        })
        setBillRequestDiscountTypes(prev => {
          const next = { ...prev }
          delete next[requestId]
          return next
        })
        setShowBillRequestDiscountModal(prev => (prev === requestId ? null : prev))
        await loadBillRequests()
      } else {
        setToast({ message: data.error || 'Failed to cancel bill request', type: 'error' })
      }
    } catch (error: unknown) {
      console.error('Cancel bill request error:', error)
      setToast({ message: 'Failed to cancel bill request', type: 'error' })
    } finally {
      setIsProcessingBill(false)
    }
  }

  // Handle final invoice generation (paymentMethod, paidAmount, changeAmount from payment modal when provided)
  const handleGenerateFinalInvoice = async (
    requestId: string,
    paymentPayload?: {
      payment_method: string
      paid_amount: number
      change_amount?: number
      is_split_payment?: boolean
      split_payments?: Array<{ payment_method: string; amount: number; received_amount?: number; change_amount?: number }>
      split_payment_details?: Record<string, unknown>
    },
    credit?: { coName: string; customerName: string }
  ) => {
    const requestRow = billRequests.find(r => r.id === requestId)
    const orderRow = requestRow?.orders || requestRow?.order || {}
    const brTotals = calculateBillRequestTotals(orderRow, requestId)
    const d = billRequestDiscounts[requestId]
    let brFullComp = false
    if (d && posSettings.creditDinnerEnabled) {
      if (d.type === 'percentage' && d.value >= 99.99) brFullComp = true
      else brFullComp = brTotals.subtotal > 0 && brTotals.discountAmount >= brTotals.subtotal - 0.05
    }
    if (posSettings.creditDinnerEnabled && brFullComp && !credit) {
      setCreditDinnerModalContext('bill_final')
      setPendingFinalRequestId(requestId)
      setPendingFinalInvoicePayload(paymentPayload || null)
      setCreditDinnerCustomerName(orderRow.customer_name || 'Guest')
      setShowCreditDinnerModal(true)
      return
    }

    setIsProcessingBill(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const generatedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      // Get discount for this request if applied
      const discount = billRequestDiscounts[requestId]
      const discountData = discount ? {
        discount_amount: discount.value,
        discount_type: discount.type,
        discount_authorized_by: discount.authorizedBy || generatedBy,
        discount_authorized_by_secret_id: discount.authorizedBySecret?.id || null,
        discount_authorized_by_secret_name: discount.authorizedBySecret?.name || null
      } : {}
      
      const body: Record<string, unknown> = {
        generated_by: generatedBy,
        ...discountData
      }
      if (credit && posSettings.creditDinnerEnabled && brFullComp) {
        body.credit_dinner_co_name = credit.coName
        body.credit_dinner_customer_display = credit.customerName || orderRow.customer_name || 'Guest'
        body.credit_dinner_amount = brTotals.subtotal
      }
      if (paymentPayload) {
        body.payment_method = paymentPayload.payment_method
        body.paid_amount = paymentPayload.paid_amount
        if (paymentPayload.change_amount != null) body.change_amount = paymentPayload.change_amount
        if (paymentPayload.is_split_payment != null) body.is_split_payment = paymentPayload.is_split_payment
        if (paymentPayload.split_payments != null) body.split_payments = paymentPayload.split_payments
        if (paymentPayload.split_payment_details != null) body.split_payment_details = paymentPayload.split_payment_details
      }
      
      const token = localStorage.getItem('pos-auth-token') || sessionStorage.getItem('pos-auth-token')
      const response = await fetch(`${apiUrl}/bill-requests/${requestId}/generate-final-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      })
      
      const data = await response.json()
      if (data.success) {
        // Phase 7: Check table clearing status and show appropriate message
        // Fix 5: Use nullish coalescing to ensure we always have a boolean value
        const tableCleared = data.data?.table_cleared ?? false
        const tableVerification = data.data?.table_verification || 'failed'  // 'success', 'partial', or 'failed'
        const tableNumber = data.data?.table_number
        
        let message = `Final invoice generated: ${data.data.invoice_number}`
        if (tableCleared && tableVerification === 'success') {
          message += `. Table ${tableNumber || ''} reset to available.`
          setToast({ message, type: 'success' })
        } else if (tableVerification === 'partial') {
          // Partial clear - show warning but not as severe
          message += `. Table partially cleared. ${data.warnings && data.warnings.length > 0 ? data.warnings.join('; ') : 'Run migrations to fully clear.'}`
          setToast({ message, type: 'warning' })
        } else if (data.warnings && data.warnings.length > 0) {
          message += `. Warning: ${data.warnings.join('; ')}`
          setToast({ message, type: 'warning' })
        } else {
          message += `. Table clearing status unknown.`
          setToast({ message, type: 'warning' })
        }
        
        // Clear discount for this request
        const newDiscounts = { ...billRequestDiscounts }
        delete newDiscounts[requestId]
        setBillRequestDiscounts(newDiscounts)
        await loadBillRequests()
        
        // Phase 7: Note for waiter module refresh
        // Fix 4: Frontend - Refresh Waiter Module After Final Invoice
        // Refresh waiter module if table was cleared (fully or partially)
        if (data.success && tableCleared) {
          // Trigger waiter module refresh via window event
          const tableId = data.data?.table_id
          const tableNumber = data.data?.table_number
          
          console.log('📢 [POS] Dispatching tableCleared event for waiter module refresh:', {
            tableId: tableId,
            tableNumber: tableNumber,
            tableCleared: tableCleared,
            tableVerification: tableVerification
          })
          
          window.dispatchEvent(new CustomEvent('tableCleared', { 
            detail: { 
              tableId: tableId, 
              tableNumber: tableNumber,
              tableCleared: tableCleared,
              tableVerified: tableVerification === 'success' || tableVerification === 'partial'  // Accept partial as verified
            } 
          }))
        }
        
        console.log('✅ Final invoice generated:', {
          invoiceNumber: data.data.invoice_number,
          tableCleared: tableCleared,
          tableVerification: tableVerification,
          tableNumber: tableNumber,
          tableId: data.data?.table_id,
          warnings: data.warnings
        })
        const d = data.data || {}
        if (d.print_attempted === true) {
          console.log('[Final Invoice Print] Attempted:', d.print_success ? 'OK' : 'Failed', d.printer_ip ? `@ ${d.printer_ip}` : '', d.print_message || '')
        } else if (d.print_attempted === false || d.print_message) {
          console.log('[Final Invoice Print] Not attempted:', d.print_message || 'No printer IP in Settings. Set Printer IP in Settings → POS → Invoice Printer.')
        } else {
          console.log('[Final Invoice Print] Backend did not return print status. Restart backend to get printer IP from Settings.')
        }
      } else {
        setToast({ message: data.error || 'Failed to generate final invoice', type: 'error' })
      }
    } catch (error: any) {
      console.error('Error generating final invoice:', error)
      setToast({ message: 'Failed to generate final invoice', type: 'error' })
    } finally {
      setIsProcessingBill(false)
    }
  }

  // Bill Request Discount Functions
  const handleBillRequestDiscountKeypadInput = (requestId: string, value: string) => {
    if (value === 'clear') {
      setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
    } else if (value === 'backspace') {
      setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: (prev[requestId] || '').slice(0, -1) }))
    } else {
      if (/^[\d.]$/.test(value)) {
        const current = billRequestDiscountInputs[requestId] || ''
        if (value === '.' && current.includes('.')) return
        setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: (prev[requestId] || '') + value }))
      }
    }
  }

  const handleApplyBillRequestDiscount = async (requestId: string) => {
    const discountValue = parseFloat(billRequestDiscountInputs[requestId] || '0')
    const discountType = billRequestDiscountTypes[requestId] || 'percentage'
    
    if (!billRequestDiscountInputs[requestId] || discountValue <= 0) {
      setToast({ message: 'Please enter a valid discount amount', type: 'error' })
      return
    }

    // Find the bill request to get order subtotal
    const request = billRequests.find(r => r.id === requestId)
    const order = request?.orders || {}
    const subtotal = order.subtotal || 0

    // Check limits
    if (discountType === 'percentage') {
      if (discountValue > posSettings.maxDiscountPercentage) {
        setToast({ 
          message: `Maximum discount percentage is ${posSettings.maxDiscountPercentage}%`, 
          type: 'error' 
        })
        return
      }
    } else {
      if (discountValue > posSettings.maxDiscountAmount) {
        setToast({ 
          message: `Maximum discount amount is ${restaurantSettings.currency_symbol}${posSettings.maxDiscountAmount}`, 
          type: 'error' 
        })
        return
      }
      if (discountValue > subtotal) {
        setToast({ 
          message: `Discount amount cannot exceed subtotal of ${restaurantSettings.currency_symbol}${subtotal.toFixed(2)}`, 
          type: 'error' 
        })
        return
      }
    }

    if (!user?.id) {
      setToast({ message: 'User not authenticated', type: 'error' })
      return
    }

    // Check if any user has discount TOTP configured (cashier can use manager's code)
    const statusResponse = await apiService.getTOTPConfigured()
    if (statusResponse.success && (statusResponse.data as any)?.discount?.isConfigured) {
      // Show authorization modal with OTP input
      setPendingBillRequestDiscount({ requestId, value: discountValue, type: discountType })
      setShowBillRequestDiscountAuthModal(requestId)
      setShowBillRequestDiscountModal(null)
    } else {
      // If no discount TOTP configured, show error
      setToast({ 
        message: 'Google Authenticator not configured. Please configure it in Settings > Security', 
        type: 'error' 
      })
      setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
    }
  }

  const handleBillRequestAuthKeypadInput = (value: string) => {
    if (value === 'clear') {
      setBillRequestAuthCode('')
    } else if (value === 'backspace') {
      setBillRequestAuthCode(prev => prev.slice(0, -1))
    } else {
      if (/^\d$/.test(value) && billRequestAuthCode.length < 6) {
        setBillRequestAuthCode(prev => prev + value)
      }
    }
  }

  const handleVerifyBillRequestDiscountAuthorization = async () => {
    if (!billRequestAuthCode || billRequestAuthCode.length !== 6) {
      setToast({ message: 'Please enter a 6-digit verification code', type: 'error' })
      return
    }

    if (!user?.id) {
      setToast({ message: 'User not authenticated', type: 'error' })
      return
    }

    if (!pendingBillRequestDiscount) {
      setToast({ message: 'No pending discount to authorize', type: 'error' })
      return
    }

    setIsVerifyingBillRequestAuth(true)
    try {
      const request = billRequests.find(r => r.id === pendingBillRequestDiscount.requestId)
      const order = request?.orders || {}
      const subtotal = order.subtotal || 0
      
      const discountAmount = pendingBillRequestDiscount.type === 'percentage'
        ? (subtotal * pendingBillRequestDiscount.value) / 100
        : pendingBillRequestDiscount.value

      const response = await apiService.verifyDiscountAuthorization(
        undefined,
        billRequestAuthCode,
        discountAmount,
        pendingBillRequestDiscount.type
      )

      if (response.success && (response as any).verified) {
        // Authorization successful, apply the discount
        setBillRequestDiscounts(prev => ({
          ...prev,
          [pendingBillRequestDiscount.requestId]: {
            value: pendingBillRequestDiscount.value,
            type: pendingBillRequestDiscount.type,
            authorizedBy: user.id,
            authorizedBySecret: {
              id: (response as any).secret_id,
              name: (response as any).secret_name || 'Google Authenticator'
            }
          }
        }))
        setToast({ message: 'Discount authorized and applied successfully', type: 'success' })
        setShowBillRequestDiscountAuthModal(null)
        setPendingBillRequestDiscount(null)
        setBillRequestAuthCode('')
        setBillRequestDiscountInputs(prev => ({ ...prev, [pendingBillRequestDiscount.requestId]: '' }))
      } else {
        setToast({ message: response.error || 'Invalid verification code', type: 'error' })
        setBillRequestAuthCode('')
      }
    } catch (error) {
      console.error('Error verifying authorization:', error)
      setToast({ message: 'Failed to verify authorization code', type: 'error' })
      setBillRequestAuthCode('')
    } finally {
      setIsVerifyingBillRequestAuth(false)
    }
  }

  /** VAT-inclusive line total; matches Orders / invoice math when total_price is set */
  const getBillRequestLineTotal = (item: any): number => {
    const qty = Math.max(1, Number(item?.quantity) || 1)
    const totalPrice = Number(item?.total_price)
    const unitPrice = Number(item?.unit_price)
    const hasValidTotal = Number.isFinite(totalPrice)
    const hasValidUnit = Number.isFinite(unitPrice)
    if (hasValidTotal && totalPrice > 0) return totalPrice
    if (hasValidUnit && unitPrice > 0) return unitPrice * qty
    if (hasValidTotal) return totalPrice
    return 0
  }

  // Calculate bill request totals with discount (menu prices are VAT-inclusive; do not add tax on top)
  // When linesSubtotalInclVat is provided, subtotal matches listed order lines + pending additional (avoids DB mismatch).
  const calculateBillRequestTotals = (
    order: any,
    requestId: string,
    options?: { linesSubtotalInclVat?: number }
  ) => {
    let subtotal = Number(order.subtotal) || 0
    if (options?.linesSubtotalInclVat != null && Number.isFinite(options.linesSubtotalInclVat)) {
      subtotal = Math.max(0, options.linesSubtotalInclVat)
    }
    const discount = billRequestDiscounts[requestId]
    let discountAmount = 0

    if (discount) {
      if (discount.type === 'percentage') {
        discountAmount = (subtotal * Number(discount.value)) / 100
      } else {
        discountAmount = Number(discount.value) || 0
      }
    } else if (order.discount_amount != null && order.discount_amount !== '') {
      discountAmount = Number(order.discount_amount) || 0
    }

    const serviceCharge = Number(order.service_charge) || 0
    const grossAfterDiscount = Math.max(0, subtotal - discountAmount)
    const vatRateDecimal = (restaurantSettings.tax_rate || 15) / 100
    const { vat: taxAmount } = splitVatInclusive(grossAfterDiscount, vatRateDecimal)
    let total = grossAfterDiscount + serviceCharge
    const orderTotal = Number(order.total_amount) || 0
    // Do not replace a legitimate 0 total (e.g. 100% discount) with stale order.total_amount from DB
    const fullCompOrZeroDueToDiscount =
      discountAmount > 0 && grossAfterDiscount <= 0.001
    if (
      !fullCompOrZeroDueToDiscount &&
      (total <= 0 || isNaN(total)) &&
      orderTotal > 0
    ) {
      total = orderTotal
    }

    return {
      subtotal,
      discountAmount,
      taxAmount,
      serviceCharge,
      total
    }
  }

  // Handle request rejection
  const handleRejectRequest = async (type: 'modification' | 'cancellation', requestId: string, rejectionReason?: string) => {
    setIsProcessingRequest(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const rejectedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      
      const response = await fetch(`${apiUrl}/orders/requests/${type}/${requestId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          rejected_by: rejectedBy,
          rejection_reason: rejectionReason || null
        })
      })
      
      const data = await response.json()
      if (data.success) {
        setToast({ message: `${type === 'modification' ? 'Modification' : 'Cancellation'} request rejected`, type: 'success' })
        if (type === 'modification') {
          await loadModificationRequests()
        } else {
          await loadCancellationRequests()
        }
      } else {
        setToast({ message: data.error || `Failed to reject ${type} request`, type: 'error' })
      }
    } catch (error: any) {
      console.error(`Error rejecting ${type} request:`, error)
      setToast({ message: `Failed to reject ${type} request`, type: 'error' })
    } finally {
      setIsProcessingRequest(false)
    }
  }
  
  // Handle reject draft order
  const handleRejectDraftOrder = async (orderId: string) => {
    setIsApprovingOrder(true)
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      // Only send rejected_by if user.id is a valid UUID, otherwise send null
      const rejectedBy = (user?.id && isValidUUID(user.id)) ? user.id : null
      const response = await fetch(`${apiUrl}/orders/${orderId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejected_by: rejectedBy })
      })

      const data = await response.json()
      if (data.success) {
        setToast({ message: 'Draft order rejected', type: 'success' })
        await loadDraftOrders()
        setShowDraftOrdersModal(false)
        setSelectedDraftOrder(null)
        setIsEditingDraft(false)
      } else {
        setToast({ message: data.error || 'Failed to reject draft order', type: 'error' })
      }
    } catch (error) {
      console.error('Error rejecting draft order:', error)
      setToast({ message: 'Failed to reject draft order', type: 'error' })
    } finally {
      setIsApprovingOrder(false)
    }
  }
  
  // Handle update draft order
  // const handleUpdateDraftOrder = async () => {
  //   if (!selectedDraftOrder) return
  //   
  //   setIsApprovingOrder(true)
  //   try {
  //     const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
  //     const updateData = {
  //       ...editedDraftData,
  //       draft_items: editedDraftItems.length > 0 ? editedDraftItems : selectedDraftOrder.draft_items
  //     }
  //     
  //     const response = await fetch(`${apiUrl}/orders/drafts/${selectedDraftOrder.id}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(updateData)
  //     })

  //     const data = await response.json()
  //     if (data.success) {
  //       setToast({ message: 'Draft order updated', type: 'success' })
  //       await loadDraftOrders()
  //       setIsEditingDraft(false)
  //       setSelectedDraftOrder(data.data)
  //     } else {
  //       setToast({ message: data.error || 'Failed to update draft order', type: 'error' })
  //     }
  //   } catch (error) {
  //     console.error('Error updating draft order:', error)
  //     setToast({ message: 'Failed to update draft order', type: 'error' })
  //   } finally {
  //     setIsApprovingOrder(false)
  //   }
  // }
  
  /* TODO: Enable in future when Edit Order feature is activated
  const handleEditDraftOrder = (order: any) => {
    setSelectedDraftOrder(order)
    setEditedDraftItems(order.draft_items || [])
    setEditedDraftData({
      subtotal: order.subtotal,
      tax_amount: order.tax_amount,
      service_charge: order.service_charge,
      discount_amount: order.discount_amount,
      total_amount: order.total_amount,
      notes: order.notes,
      customer_name: order.customer_name,
      customer_phone: order.customer_phone
    })
    setIsEditingDraft(true)
  }
  */

  const loadAllergens = async () => {
    try {
      const res = await apiService.getAllergens()
      if (res.success && res.data && Array.isArray(res.data)) {
        const map: Record<string, AllergenInfo> = {}
        ;(res.data as Array<{ id: string; name: string; icon: string | null }>).forEach((a) => {
          map[a.id] = { id: a.id, name: a.name, icon: a.icon }
        })
        setAllergenMap(map)
      }
    } catch (_) {}
  }

  const loadTables = async () => {
    try {
      const res = await apiService.getTables()
      if (res.success && res.data && Array.isArray(res.data)) {
        setTables(res.data)
      }
    } catch (error) {
      console.error('Error loading tables:', error)
    }
  }

  const loadProducts = async () => {
    try {
      const response = await apiService.getMenuItems()
      if (response.success && response.data && Array.isArray(response.data)) {
        const transformedProducts = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          basePrice: Number(item.base_price || 0),
          categoryId: item.category_id,
          code: item.code ?? '',
          image: resolveImageUrl(item.image),
          description: item.description,
          isActive: item.is_active,
          isPopular: item.is_popular,
          variants: item.variants || [],
          addons: item.addons || [],
          upsells: item.upsells || [],
          crossSells: item.cross_sells || [],
          tags: item.tags || [],
          preparationTime: item.preparation_time,
          calories: item.calories,
          stepsToBurn: item.steps_to_burn,
          allergens: item.allergens || []
        }))
        setProducts(transformedProducts)
      } else {
        console.error('Failed to load products:', response.error)
        setProducts([])
      }
    } catch (error) {
      console.error('Error loading products:', error)
      setProducts([])
    }
  }

  const loadOffers = async () => {
    try {
      const response = await apiService.getOffers()
      if (response.success && response.data && Array.isArray(response.data)) {
        const transformedOffers = (response.data as any[]).map((offer: any) => ({
          id: offer.id,
          name: offer.name,
          description: offer.description,
          type: offer.type,
          value: Number(offer.value || 0),
          isActive: offer.is_active,
          applicableItems: offer.applicable_items || [],
          minOrderAmount: offer.min_order_amount,
          maxDiscount: offer.max_discount,
          usageLimit: offer.usage_limit,
          usedCount: offer.used_count,
          startDate: new Date(offer.start_date),
          endDate: new Date(offer.end_date),
          comboItems: offer.combo_items || [],
          bogoItems: offer.bogo_items || []
        }))
        setAvailableOffers(transformedOffers)
      } else {
        console.error('Failed to load offers:', response.error)
        setAvailableOffers([])
      }
    } catch (error) {
      console.error('Error loading offers:', error)
      setAvailableOffers([])
    }
  }

  // Load restaurant settings from database
  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        const data = response.data as any
        setRestaurantSettings(data as RestaurantSettings)
      } else {
        console.error('❌ Failed to load restaurant settings:', response.error)
      }
    } catch (error) {
      console.error('❌ Error loading restaurant settings:', error)
    }
  }

  const loadCustomerFields = async () => {
    try {
      const response = await apiService.getCustomerFields()
      if (response.success && response.data) {
        setCustomerFields(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading customer fields:', error)
    }
  }

  // Reset order type to takeaway if dine-in is not allowed
  useEffect(() => {
    if (!posSettings?.allowDineInFromPOS && orderType === 'dine-in') {
      setOrderType('takeaway')
    }
    if (!posSettings?.allowCateringOrders && orderType === 'catering') {
      setOrderType('takeaway')
    }
  }, [posSettings?.allowDineInFromPOS, posSettings?.allowCateringOrders, orderType])

  // Clear table selection when order type changes from dine-in
  useEffect(() => {
    if (orderType !== 'dine-in') {
      setSelectedTable('')
      setSelectedTableId('')
    }
  }, [orderType])

  // Populate customer form data when modal opens (only on initial open, not when customer changes)
  useEffect(() => {
    if (showCustomerModal) {
      // Only populate if customer has an ID (registered customer) and form is empty
      if (selectedCustomer && selectedCustomer.id && !isGuestCustomer) {
        // Only populate if form data is empty or doesn't match (to avoid overwriting user input)
        const hasFormData = customerFormData.mobileNumber || customerFormData.firstName || customerFormData.email
        if (!hasFormData) {
          setCustomerFormData({
            mobileNumber: selectedCustomer.mobileNumber || selectedCustomer.phone,
            phone: selectedCustomer.mobileNumber || selectedCustomer.phone,
            firstName: selectedCustomer.firstName,
            lastName: selectedCustomer.lastName,
            email: selectedCustomer.email,
            age: selectedCustomer.age,
            gender: selectedCustomer.gender,
            address: selectedCustomer.address,
            city: selectedCustomer.city,
            state: selectedCustomer.state,
            postalCode: selectedCustomer.postalCode,
            country: selectedCustomer.country,
            dateOfBirth: selectedCustomer.dateOfBirth,
            notes: selectedCustomer.notes,
            companyName: selectedCustomer.companyName,
            vatNumber: selectedCustomer.vatNumber
          })
        }
      }
    }
  }, [showCustomerModal])

  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()
      if (response.success && response.data) {
        const posData = response.data as POSSettings
        
        // Ensure receiptLayout is properly parsed if it's a string
        if (posData.receiptLayout && typeof posData.receiptLayout === 'string') {
          try {
            posData.receiptLayout = JSON.parse(posData.receiptLayout)
          } catch (e) {
            console.error('Error parsing receipt layout:', e)
            posData.receiptLayout = []
          }
        }
        
        setPOSSettings(posData)
        
        // Debug logging
        // Ensure receiptLayout is properly set
        if (!posData.receiptLayout || (Array.isArray(posData.receiptLayout) && posData.receiptLayout.length === 0)) {
          console.warn('Receipt layout is empty or not set. Using default layout.');
        }
        
        // Initialize guest customer if guest ordering is enabled
        if (posData.enableGuestOrdering) {
          await initializeGuestCustomer()
        }
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  const initializeGuestCustomer = async () => {
    try {
      const response = await apiService.getNextGuestNumber()
      if (response.success && response.data) {
        const nextGuestNumber = (response.data as any)?.nextGuestNumber || posSettings?.guestCustomerStartingNumber || '10001'
        const guestCustomer: Customer = {
          id: '',
          name: posSettings?.guestCustomerName || 'Guest Customer',
          mobileNumber: nextGuestNumber,
          phone: nextGuestNumber,
          firstName: posSettings.guestCustomerName,
          lastName: '',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        setSelectedCustomer(guestCustomer)
        setIsGuestCustomer(true)
      }
    } catch (error) {
      console.error('Error initializing guest customer:', error)
    }
  }

  // Customer Management Functions
  // Fetch visit count and available visit-based offers when registered customer is selected
  useEffect(() => {
    const fetchVisitCountAndOffers = async () => {
      if (!isGuestCustomer && selectedCustomer?.id) {
        try {
          const response = await apiService.getCustomerVisitCount(selectedCustomer.id)
          if (response.success) {
            // Backend returns visitCount directly in response, not in response.data
            const count = (response as any).visitCount !== undefined ? (response as any).visitCount : ((response.data as any)?.visitCount || 0)
            setCustomerVisitCount(count)
            
            // Fetch available visit-based offers for this customer
            try {
              const offersResponse = await apiService.getAvailableVisitBasedOffers(selectedCustomer.id)
              if (offersResponse.success && offersResponse.data && Array.isArray(offersResponse.data)) {
                setAvailableVisitBasedOffers(offersResponse.data as any[])
              } else {
                setAvailableVisitBasedOffers([])
              }
            } catch (error) {
              console.error('Error fetching visit-based offers:', error)
              setAvailableVisitBasedOffers([])
            }
          } else {
            setCustomerVisitCount(0)
            setAvailableVisitBasedOffers([])
          }
        } catch (error) {
          console.error('Error fetching visit count:', error)
          setCustomerVisitCount(0)
          setAvailableVisitBasedOffers([])
        }
      } else {
        setCustomerVisitCount(null)
        setAvailableVisitBasedOffers([])
      }
    }
    
    fetchVisitCountAndOffers()
  }, [selectedCustomer?.id, isGuestCustomer])

  // Apply visit-based offer
  const handleApplyVisitBasedOffer = async (offer: any) => {
    if (!selectedCustomer?.id) {
      setToast({ message: 'Please select a customer first', type: 'error' })
      return
    }

    // Check minimum order amount
    const subtotal = getSubtotal()
    if (offer.min_order_amount && subtotal < offer.min_order_amount) {
      setToast({ 
        message: `Minimum order amount of ${restaurantSettings.currency_symbol || 'SAR'} ${offer.min_order_amount} is required for this offer`, 
        type: 'error' 
      })
      return
    }

    if (offer.offer_type === 'free_item') {
      // Add free item to cart
      if (!offer.free_item_id || !offer.free_item) {
        setToast({ message: 'Free item not found for this offer', type: 'error' })
        return
      }

      const freeProduct = products.find(p => p.id === offer.free_item_id)
      if (!freeProduct) {
        setToast({ message: 'Free item not found in products', type: 'error' })
        return
      }

      // Add free item to cart with price 0
      const cartItem: CartItem = {
        id: freeProduct.id,
        name: freeProduct.name,
        description: freeProduct.description,
        basePrice: freeProduct.basePrice,
        finalPrice: 0, // Free item
        product: freeProduct,
        quantity: 1,
        selectedVariant: undefined,
        selectedAddons: [],
        notes: '',
        isFreeItem: true,
        visitBasedOfferId: offer.id
      }

      setCart([...cart, cartItem])
      setAppliedVisitBasedOffers([...appliedVisitBasedOffers, offer])
      setToast({ message: `Free item "${offer.free_item.name}" added to cart!`, type: 'success' })

    } else if (offer.offer_type === 'percentage_discount') {
      // Apply percentage discount
      const discountAmount = (subtotal * offer.discount_value) / 100
      setDiscount(discountAmount)
      setDiscountType('percentage')
      setAppliedVisitBasedOffers([...appliedVisitBasedOffers, offer])
      setToast({ message: `${offer.discount_value}% discount applied!`, type: 'success' })

    } else if (offer.offer_type === 'fixed_discount') {
      // Apply fixed discount
      const discountAmount = Math.min(offer.discount_value, subtotal)
      setDiscount(discountAmount)
      setDiscountType('fixed')
      setAppliedVisitBasedOffers([...appliedVisitBasedOffers, offer])
      setToast({ message: `${restaurantSettings.currency_symbol || 'SAR'} ${discountAmount} discount applied!`, type: 'success' })
    }

    // Record offer usage (will be recorded when order is placed)
  }

  const handleCustomerSearch = async (mobileNumber: string) => {
    if (!mobileNumber.trim()) return
    
    try {
      const response = await apiService.getCustomerByMobile(mobileNumber)
      if (response.success && response.data) {
        const customerData = response.data as any
        // Map snake_case to camelCase
        const mappedCustomer: Customer = {
          id: customerData.id,
          name: customerData.name,
          mobileNumber: customerData.mobile_number || customerData.mobileNumber,
          phone: customerData.phone || customerData.mobile_number,
          firstName: customerData.first_name || customerData.firstName,
          lastName: customerData.last_name || customerData.lastName,
          email: customerData.email,
          age: customerData.age,
          gender: customerData.gender,
          address: customerData.address,
          city: customerData.city,
          state: customerData.state,
          postalCode: customerData.postal_code || customerData.postalCode,
          country: customerData.country,
          dateOfBirth: customerData.date_of_birth || customerData.dateOfBirth,
          notes: customerData.notes,
          companyName: customerData.company_name || customerData.companyName,
          vatNumber: customerData.vat_number || customerData.vatNumber,
          isActive: customerData.is_active !== false,
          createdAt: customerData.created_at || customerData.createdAt,
          updatedAt: customerData.updated_at || customerData.updatedAt
        }
        setSelectedCustomer(mappedCustomer)
        setIsGuestCustomer(false)
        // Populate form data immediately when customer is found
        setCustomerFormData({
          mobileNumber: mappedCustomer.mobileNumber || mappedCustomer.phone,
          phone: mappedCustomer.mobileNumber || mappedCustomer.phone,
          firstName: mappedCustomer.firstName,
          lastName: mappedCustomer.lastName,
          email: mappedCustomer.email,
          age: mappedCustomer.age,
          gender: mappedCustomer.gender,
          address: mappedCustomer.address,
          city: mappedCustomer.city,
          state: mappedCustomer.state,
          postalCode: mappedCustomer.postalCode,
          country: mappedCustomer.country,
          dateOfBirth: mappedCustomer.dateOfBirth,
          notes: mappedCustomer.notes,
          companyName: mappedCustomer.companyName,
          vatNumber: mappedCustomer.vatNumber
        })
        // Visit count will be fetched by useEffect
      } else {
        // Customer not found, create new guest or registered customer
        const newCustomer: Customer = {
          id: '',
          name: 'Guest Customer',
          mobileNumber: mobileNumber,
          phone: mobileNumber,
          firstName: '',
          lastName: '',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        setSelectedCustomer(newCustomer)
        setIsGuestCustomer(true)
      }
    } catch (error) {
      console.error('Error searching customer:', error)
    }
  }

  const handleSaveCustomer = async () => {
    if (!selectedCustomer) return
    
    // Validate phone number for catering orders
    if (orderType === 'catering') {
      const phoneNumber = customerFormData.mobileNumber || customerFormData.phone || selectedCustomer?.mobileNumber || selectedCustomer?.phone
      if (!phoneNumber || phoneNumber.trim() === '') {
        setToast({ 
          message: 'Phone number is required for catering orders', 
          type: 'error' 
        })
        return
      }
    }
    
    try {
      let customerData = { ...customerFormData }
      
      // If it's a guest customer, use the mobile number from selectedCustomer
      if (isGuestCustomer) {
        customerData.mobileNumber = selectedCustomer.mobileNumber || selectedCustomer.phone
        customerData.phone = selectedCustomer.mobileNumber || selectedCustomer.phone
      }
      
      let response
      if (selectedCustomer.id) {
        // Update existing customer
        response = await apiService.updateCustomer(selectedCustomer.id, customerData)
      } else {
        // Create new customer
        response = await apiService.createCustomer(customerData)
      }
      
      if (response.success && response.data) {
        setSelectedCustomer(response.data as Customer)
        setIsGuestCustomer(false)
        setShowCustomerModal(false)
        setShowPhoneKeypad(true)
        setCustomerFormData({})
      }
    } catch (error) {
      console.error('Error saving customer:', error)
    }
  }

  const handleCustomerFormChange = (fieldName: string, value: any) => {
    setCustomerFormData(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  const addToCart = (product: Product) => {
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before adding items to cart', 
        type: 'error' 
      })
      return
    }
    
    const hasVariants = !!(product.variants && product.variants.length > 0)
    const hasAddons = !!(product.addons && product.addons.length > 0)

    if (hasVariants || hasAddons) {
      setSelectedProduct(product)
      setSelectedVariant(
        hasVariants
          ? (product.variants!.find(v => v.isDefault) || product.variants![0])
          : null
      )
      setSelectedAddons([])
      setShowCustomizationModal(true)
    } else {
      addItemToCart(product, product.variants?.[0], [])
    }
  }

  const addItemToCart = (product: Product, variant?: MenuVariant, addons: Addon[] = []) => {
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before adding items to cart', 
        type: 'error' 
      })
      return
    }
    
    const existingItem = cart.find(item => 
      item.product.id === product.id && 
      item.selectedVariant?.id === variant?.id &&
      JSON.stringify(item.selectedAddons?.map(a => a.id).sort()) === JSON.stringify(addons.map(a => a.id).sort())
    )
    
    if (existingItem) {
      setCart(cart.map(item => 
        item === existingItem 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      // Calculate base price
      let mainItemPrice = product.basePrice
      if (variant) {
        mainItemPrice += variant.priceModifier
      }
      const addonPrice = addons.reduce((sum, addon) => sum + addon.price, 0)
      const originalPrice = mainItemPrice + addonPrice
      
      // Check if any applied discount offers should apply to this new item
      let finalPrice = originalPrice
      let discountOfferId: string | undefined
      let discountOriginalPrice: number | undefined
      let discountType: 'percentage' | 'fixed' | undefined
      let discountValue: number | undefined
      
      // Check applied discount offers
      for (const offer of appliedOffers) {
        if ((offer.type === 'percentage' || offer.type === 'fixed') && 
            offer.applicableItems && 
            offer.applicableItems.includes(product.id)) {
          // Apply discount to main item only (not addons)
          let discountedMainItemPrice = mainItemPrice
          
          if (offer.type === 'percentage') {
            const discountAmount = (mainItemPrice * offer.value) / 100
            const maxDiscount = offer.maxDiscount ? (mainItemPrice * offer.maxDiscount) / 100 : discountAmount
            discountedMainItemPrice = mainItemPrice - Math.min(discountAmount, maxDiscount)
          } else if (offer.type === 'fixed') {
            discountedMainItemPrice = Math.max(0, mainItemPrice - offer.value)
          }
          
          // Final price = discounted main item + full addon price
          finalPrice = discountedMainItemPrice + addonPrice
          discountOfferId = offer.id
          discountOriginalPrice = originalPrice
          discountType = offer.type as 'percentage' | 'fixed'
          discountValue = offer.value
          break // Apply only the first matching offer
        }
      }
      
      const newItem: CartItem = {
        id: `${product.id}-${Date.now()}-${Math.random()}`,
        name: product.name,
        description: product.description,
        basePrice: product.basePrice,
        finalPrice: finalPrice,
        kdsStationId: (product as any).kdsStationId || undefined,
        product, 
        quantity: 1, 
        selectedVariant: variant,
        selectedAddons: addons,
        notes: '',
        discountOfferId,
        discountOriginalPrice,
        discountType,
        discountValue
      }
      
      setCart([...cart, newItem])
    }
  }

  const removeFromCart = (index: number) => {
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before modifying cart items', 
        type: 'error' 
      })
      return
    }
    
    // const itemToRemove = cart[index]
    const newCart = cart.filter((_, i) => i !== index)
    setCart(newCart)
    
    // Check if any applied offers are no longer valid after removing this item
    const newAppliedOffers = appliedOffers.filter(offer => {
      if (offer.type === 'combo') {
        // For combo offers, ALL combo items must be present - if any is removed, remove the entire offer
        const comboItemIds = (offer as any).comboItems?.map((item: any) => item.menuItemId || item.id || item.productId) || []
        const hasAllComboItems = comboItemIds.every((itemId: string) => newCart.some(cartItem => cartItem.product.id === itemId))
        return hasAllComboItems
      } else if (offer.type === 'bogo') {
        // Check if any BOGO items are still in cart
        const bogoItemIds = (offer as any).bogoItems?.map((item: any) => {
          return item.buyItemId || item.getItemId || item.menuItemId || item.id || item.productId
        }) || []
        const hasBogoItems = bogoItemIds.some((itemId: string) => newCart.some(cartItem => cartItem.product.id === itemId))
        return hasBogoItems
      } else {
        // For percentage/fixed offers, check applicable items
        const hasApplicableItems = offer.applicableItems.some(itemId => newCart.some(cartItem => cartItem.product.id === itemId))
        return hasApplicableItems
      }
    })
    
    // If offers were removed, clean up the cart items
    if (newAppliedOffers.length !== appliedOffers.length) {
      const removedOfferIds = appliedOffers
        .filter(offer => !newAppliedOffers.find(newOffer => newOffer.id === offer.id))
        .map(offer => offer.id)
      
      // Remove offer-specific properties from cart items
      const cleanedCart = newCart.map(item => {
        const hasRemovedOffer = removedOfferIds.some(offerId => {
          const offer = appliedOffers.find(o => o.id === offerId)
          if (!offer) return false
          
          if (offer.type === 'combo') {
            return item.isComboItem
          } else if (offer.type === 'bogo') {
            return item.isBOGOItem
          }
          return false
        })
        
        if (hasRemovedOffer) {
          return {
            ...item,
            isComboItem: false,
            comboPrice: undefined,
            originalPrice: undefined,
            isBOGOItem: false,
            bogoDiscountType: undefined,
            bogoDiscountValue: undefined,
            paidQuantity: undefined,
            freeQuantity: undefined,
            discountOfferId: undefined,
            discountOriginalPrice: undefined,
            discountType: undefined,
            discountValue: undefined,
            finalPrice: item.product.basePrice
          }
        }
        return item
      })
      
      setCart(cleanedCart)
    }
    
    setAppliedOffers(newAppliedOffers)
  }

  const updateQuantity = (index: number, quantity: number) => {
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before modifying cart items', 
        type: 'error' 
      })
      return
    }
    
    if (quantity <= 0) {
      removeFromCart(index)
    } else {
      setCart(cart.map((item, i) => 
        i === index ? { ...item, quantity } : item
      ))
    }
  }

  const getItemPrice = (item: CartItem) => {
    // If it's a free item from visit-based offer, return 0
    if (item.isFreeItem) {
      return 0
    }
    
    // If item has a discount offer applied, use the discounted price (which already includes addons)
    if (item.discountOfferId && item.finalPrice !== undefined) {
      return item.finalPrice
    }
    
    // If it's a combo item, use combo price
    if (item.isComboItem && item.comboPrice !== undefined) {
      return item.comboPrice
    }
    
    // If it's a BOGO item, calculate BOGO pricing
    if (item.isBOGOItem && item.paidQuantity !== undefined && item.freeQuantity !== undefined) {
      let basePrice = item.product.basePrice
      
      // Add variant and addon prices to base price
      if (item.selectedVariant) {
        basePrice += item.selectedVariant.priceModifier
      }
      if (item.selectedAddons) {
        basePrice += item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
      }
      
      // Calculate BOGO pricing
      if (item.bogoDiscountType === 'free') {
        // Pay for paid quantity, free items are $0
        return item.paidQuantity * basePrice
      } else if (item.bogoDiscountType === 'percentage') {
        // Pay full price for paid quantity, discounted price for free quantity
        return item.paidQuantity * basePrice + item.freeQuantity * basePrice * (1 - item.bogoDiscountValue! / 100)
      } else if (item.bogoDiscountType === 'fixed') {
        // Pay full price for paid quantity, fixed discount for free quantity
        return item.paidQuantity * basePrice + item.freeQuantity * Math.max(0, basePrice - item.bogoDiscountValue!)
      }
    }
    
    // Regular pricing logic
    let price = item.product.basePrice
    if (item.selectedVariant) {
      price += item.selectedVariant.priceModifier
    }
    if (item.selectedAddons) {
      price += item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
    }
    return price
  }

  // Helper function to format currency with display options
  const formatCurrency = (amount: number | string | null | undefined) => {
    const num = amount == null || amount === '' ? 0 : Number(amount)
    const formattedAmount = Number.isNaN(num) ? '0.00' : num.toFixed(2)
    
    if (restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url) {
      // Use PNG icon
      if (restaurantSettings.currency_display_position === 'left') {
        return (
          <>
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 mr-1" />
            {formattedAmount}
          </>
        )
      } else {
        return (
          <>
            {formattedAmount}
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 ml-1" />
          </>
        )
      }
    } else {
      // Use text symbol
      if (restaurantSettings.currency_display_position === 'left') {
        return `${restaurantSettings.currency_symbol}${formattedAmount}`
      } else {
        return `${formattedAmount}${restaurantSettings.currency_symbol}`
      }
    }
  }

  // Cash payment helper functions
  const getTotalAmount = () => {
    // Return gross total (VAT-inclusive) - this is what customer pays
    const totals = getCartTotals()
    return totals.grossTotal
  }

  const getBalanceAmount = () => {
    const total = getTotalAmount()
    const received = parseFloat(receivedAmount) || 0
    return received - total
  }

  // Split payment helper functions
  const getSplitPaymentsTotal = () => {
    return splitPayments.reduce((sum, p) => sum + p.amount, 0)
  }

  const getRemainingAmount = () => {
    return Math.max(0, getTotalAmount() - getSplitPaymentsTotal())
  }

  const addSplitPayment = () => {
    const amount = parseFloat(currentSplitAmount) || 0
    if (amount <= 0) {
      setToast({ message: 'Please enter a valid amount', type: 'error' })
      return
    }
    if (amount > getRemainingAmount() + 0.01) { // Small tolerance for rounding
      setToast({ message: 'Amount exceeds remaining balance', type: 'error' })
      return
    }

    const newPayment: typeof splitPayments[0] = {
      id: `split-${Date.now()}`,
      payment_method: currentSplitMethod,
      amount: Math.min(amount, getRemainingAmount())
    }

    if (currentSplitMethod === 'cash') {
      const received = parseFloat(currentSplitReceived) || amount
      if (received < amount) {
        setToast({ message: 'Received amount must be at least the payment amount', type: 'error' })
        return
      }
      newPayment.received_amount = received
      newPayment.change_amount = received - amount
    }

    setSplitPayments([...splitPayments, newPayment])
    setCurrentSplitAmount('')
    setCurrentSplitReceived('')
    setShowSplitKeypad(false)
    
    setToast({ 
      message: `Added ${currentSplitMethod} payment of ${formatCurrency(newPayment.amount)}`, 
      type: 'success' 
    })
  }

  const removeSplitPayment = (id: string) => {
    setSplitPayments(splitPayments.filter(p => p.id !== id))
  }

  const handleSplitKeypadInput = (key: string) => {
    const currentValue = splitKeypadTarget === 'amount' ? currentSplitAmount : currentSplitReceived
    const setValue = splitKeypadTarget === 'amount' ? setCurrentSplitAmount : setCurrentSplitReceived
    
    if (key === 'backspace') {
      setValue(currentValue.slice(0, -1))
    } else if (key === 'clear') {
      setValue('')
    } else if (key === '.' && currentValue.includes('.')) {
      return // Don't allow multiple decimal points
    } else {
      setValue(currentValue + key)
    }
  }

  const resetSplitPayment = () => {
    setIsSplitPayment(false)
    setSplitPayments([])
    setCurrentSplitAmount('')
    setCurrentSplitReceived('')
    setCurrentSplitMethod('cash')
    setShowSplitKeypad(false)
  }

  const handleKeypadInput = (value: string) => {
    if (value === 'clear') {
      setReceivedAmount('')
    } else if (value === 'backspace') {
      setReceivedAmount(prev => prev.slice(0, -1))
    } else if (value === '.') {
      if (!receivedAmount.includes('.')) {
        setReceivedAmount(prev => prev + value)
      }
    } else {
      setReceivedAmount(prev => prev + value)
    }
  }

  const handleFinalInvoiceKeypadInput = (value: string) => {
    if (value === 'clear') {
      setFinalInvoicePaidAmount('')
    } else if (value === 'backspace') {
      setFinalInvoicePaidAmount(prev => prev.slice(0, -1))
    } else if (value === '.') {
      setFinalInvoicePaidAmount(prev => (prev.includes('.') ? prev : prev + value))
    } else {
      setFinalInvoicePaidAmount(prev => prev + value)
    }
  }

  // Helper function to validate UUID format
  const isValidUUID = (str: string | null | undefined): boolean => {
    if (!str) return false
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(str)
  }

  // Cash Register Functions
  const checkRegisterStatus = async () => {
    try {
      const response = await apiService.getCashRegisterStatus()
      if (response.success) {
        const registerData = response as any
        const isOpen = registerData.isOpen ?? registerData.data?.isOpen ?? false
        const needsOpening = registerData.needsOpening ?? registerData.data?.needsOpening ?? false
        const session = registerData.session ?? registerData.data?.session
        setRegisterStatus({
          isOpen,
          needsOpening,
          session: session ?? undefined
        })
        // Only auto-show opening modal when register must be opened (e.g. first visit today).
        // Do not auto-show after closing, so the user sees the POS with "Open Register" button instead of a modal.
        if (needsOpening) {
          setShowOpeningModal(true)
        }
      }
    } catch (error) {
      console.error('Error checking register status:', error)
    }
  }

  const handleRegisterKeypadInput = (value: string, isClosing: boolean = false) => {
    const setter = isClosing ? setClosingCash : setOpeningCash
    const currentValue = isClosing ? closingCash : openingCash
    
    if (value === 'clear') {
      setter('')
    } else if (value === 'backspace') {
      setter(currentValue.slice(0, -1))
    } else if (value === '.') {
      if (!currentValue.includes('.')) {
        setter(currentValue + value)
      }
    } else {
      setter(currentValue + value)
    }
  }

  const handleOpenRegister = async () => {
    const openingAmount = parseFloat(openingCash) || 0
    const allowZero = posSettings?.allowZeroCashRegisterOpening ?? false
    
    if (!openingCash || openingAmount < 0 || (!allowZero && openingAmount <= 0)) {
      setToast({ message: allowZero ? 'Please enter a valid opening cash amount (0 or greater)' : 'Please enter a valid opening cash amount (greater than 0)', type: 'error' })
      return
    }

    setIsProcessingRegister(true)
    try {
      const response = await apiService.openCashRegister({
        opening_cash: parseFloat(openingCash),
        opened_by: (user?.id && isValidUUID(user.id)) ? user.id : null,
        opened_by_name: user?.name || user?.email || 'Unknown User',
      })
      
      if (response.success) {
        setToast({ message: 'Cash register opened successfully', type: 'success' })
        setShowOpeningModal(false)
        setOpeningCash('')
        await checkRegisterStatus()
      } else {
        setToast({ message: response.error || 'Failed to open register', type: 'error' })
      }
    } catch (error) {
      console.error('Error opening register:', error)
      setToast({ message: 'Failed to open register', type: 'error' })
    } finally {
      setIsProcessingRegister(false)
    }
  }

  const handleCloseRegister = async () => {
    if (!closingCash || parseFloat(closingCash) < 0) {
      setToast({ message: 'Please enter a valid closing cash amount', type: 'error' })
      return
    }

    setIsProcessingRegister(true)
    try {
      const userName = user?.name || user?.email || 'Unknown User'
      
      const response = await apiService.closeCashRegister({
        closing_cash: parseFloat(closingCash),
        closed_by: (user?.id && isValidUUID(user.id)) ? user.id : null,
        closed_by_name: userName,
      })
      
      if (response.success) {
        const data = (response.data as any) || {}
        // Always set register to closed state immediately so UI never shows blank
        setRegisterStatus(prev => ({
          ...prev,
          isOpen: false,
          needsOpening: false,
          session: data?.payment_breakdown
            ? { ...prev?.session, ...data, payment_breakdown: data.payment_breakdown }
            : prev?.session ? { ...prev.session, ...data } : data
        }))
        setToast({ message: 'Cash register closed successfully', type: 'success' })
        setShowClosingModal(false)
        setClosingCash('')
        try {
          await checkRegisterStatus()
        } catch (e) {
          // Already updated state above; status refresh is non-critical
        }
      } else {
        setToast({ message: response.error || 'Failed to close register', type: 'error' })
      }
    } catch (error) {
      console.error('Error closing register:', error)
      setToast({ message: 'Failed to close register', type: 'error' })
    } finally {
      setIsProcessingRegister(false)
    }
  }

  const handlePrintClosingReport = async () => {
    if (isPrintingClosingReport) return
    if (!posSettings?.printerIp) {
      setToast({ message: 'Receipt printer IP is not configured in POS settings', type: 'error' })
      return
    }

    setIsPrintingClosingReport(true)
    try {
      const previewResponse = await apiService.getCashRegisterClosingReportPreview()
      if (!previewResponse.success || !previewResponse.data) {
        setToast({ message: previewResponse.error || 'Failed to load closing report data', type: 'error' })
        return
      }

      const reportPayload = previewResponse.data as any
      const userAny = user as any
      const closedByName =
        user?.name ||
        [userAny?.firstName, userAny?.lastName].filter(Boolean).join(' ') ||
        userAny?.email ||
        'Unknown User'
      const printResponse = await apiService.printClosingReport(
        {
          ...reportPayload,
          startAt: reportPayload.openedAt || reportPayload.startISO,
          endAt: reportPayload.endAt || new Date().toISOString(),
          closedByName
        },
        restaurantSettings,
        {
          printerIp: posSettings.printerIp,
          printerPort: posSettings.printerPort || 9100,
          printerName: posSettings.printerName,
          printerModel: posSettings.printerModel
        }
      )

      if (!printResponse.success) {
        setToast({ message: printResponse.error || 'Failed to print closing report', type: 'error' })
        return
      }
      setToast({ message: 'Closing report sent to receipt printer', type: 'success' })
    } catch (error: any) {
      console.error('Error printing closing report:', error)
      setToast({ message: error?.message || 'Failed to print closing report', type: 'error' })
    } finally {
      setIsPrintingClosingReport(false)
    }
  }

  // Discount Modal Functions
  const handleDiscountKeypadInput = (value: string) => {
    if (value === 'clear') {
      setDiscountInputValue('')
    } else if (value === 'backspace') {
      setDiscountInputValue(prev => prev.slice(0, -1))
    } else if (value === '.') {
      if (!discountInputValue.includes('.')) {
        setDiscountInputValue(prev => prev + value)
      }
    } else {
      // Only allow numbers
      if (/^\d$/.test(value)) {
        setDiscountInputValue(prev => prev + value)
      }
    }
  }

  const handleApplyDiscount = async () => {
    const discountValue = parseFloat(discountInputValue)
    
    if (!discountInputValue || discountValue <= 0) {
      setToast({ message: 'Please enter a valid discount amount', type: 'error' })
      return
    }

    // Check limits
    if (discountType === 'percentage') {
      if (discountValue > posSettings.maxDiscountPercentage) {
        setToast({ 
          message: `Maximum discount percentage is ${posSettings.maxDiscountPercentage}%`, 
          type: 'error' 
        })
        return
      }
    } else {
      if (discountValue > posSettings.maxDiscountAmount) {
        setToast({ 
          message: `Maximum discount amount is ${restaurantSettings.currency_symbol}${posSettings.maxDiscountAmount}`, 
          type: 'error' 
        })
        return
      }
    }

    if (!user?.id) {
      setToast({ message: 'User not authenticated', type: 'error' })
      return
    }

    // Check if any user has discount TOTP configured (cashier can use manager's code)
    const statusResponse = await apiService.getTOTPConfigured()
    if (statusResponse.success && (statusResponse.data as any)?.discount?.isConfigured) {
      // Show authorization modal with OTP input
      setPendingDiscount({ value: discountValue, type: discountType })
      setShowDiscountAuthModal(true)
    } else {
      // If no discount TOTP configured, show error
      setToast({ 
        message: 'Google Authenticator not configured. Please configure it in Settings > Security', 
        type: 'error' 
      })
      setDiscountInputValue('')
    }
  }

  // Discount Authorization Functions
  const handleAuthKeypadInput = (value: string) => {
    if (value === 'clear') {
      setAuthCode('')
    } else if (value === 'backspace') {
      setAuthCode(prev => prev.slice(0, -1))
    } else {
      // Only allow numbers and limit to 6 digits
      if (/^\d$/.test(value) && authCode.length < 6) {
        setAuthCode(prev => prev + value)
      }
    }
  }

  const handleVerifyDiscountAuthorization = async () => {
    if (!authCode || authCode.length !== 6) {
      setToast({ message: 'Please enter a 6-digit verification code', type: 'error' })
      return
    }

    if (!user?.id) {
      setToast({ message: 'User not authenticated', type: 'error' })
      return
    }

    if (!pendingDiscount) {
      setToast({ message: 'No pending discount to authorize', type: 'error' })
      return
    }

    setIsVerifyingAuth(true)
    try {
      const subtotal = getSubtotal()
      const discountAmount = pendingDiscount.type === 'percentage'
        ? (subtotal * pendingDiscount.value) / 100
        : pendingDiscount.value

      const response = await apiService.verifyDiscountAuthorization(
        undefined,
        authCode,
        discountAmount,
        pendingDiscount.type
      )

      if (response.success && (response as any).verified) {
        const subForComp = getSubtotal()
        const discAmt =
          pendingDiscount.type === 'percentage'
            ? (subForComp * pendingDiscount.value) / 100
            : Math.min(pendingDiscount.value, posSettings.maxDiscountAmount)
        const fullComp =
          (pendingDiscount.type === 'percentage' && pendingDiscount.value >= 99.99) ||
          (subForComp > 0 && discAmt >= subForComp - 0.05)

        // Authorization successful, apply the discount
        setDiscount(pendingDiscount.value)
        setDiscountType(pendingDiscount.type)
        // Store the user ID and TOTP secret info who authorized the discount
        setDiscountAuthorizedBy(user.id)
        setDiscountAuthorizedBySecret({
          id: (response as any).secret_id,
          name: (response as any).secret_name || 'Google Authenticator'
        })
        setShowDiscountAuthModal(false)
        setShowDiscountModal(false)
        setPendingDiscount(null)
        setAuthCode('')
        setDiscountInputValue('')

        if (posSettings.creditDinnerEnabled && fullComp) {
          setCreditDinnerModalContext('payment')
          setCreditDinnerCustomerName(
            selectedCustomer?.name || (selectedCustomer as any)?.firstName || 'Guest'
          )
          setShowCreditDinnerModal(true)
          setToast({ message: 'Enter Credit Dinner details to continue', type: 'success' })
        } else {
          setToast({ message: 'Discount authorized and applied successfully', type: 'success' })
        }
      } else {
        setToast({ message: response.error || 'Invalid verification code', type: 'error' })
        setAuthCode('')
      }
    } catch (error) {
      console.error('Error verifying authorization:', error)
      setToast({ message: 'Failed to verify authorization code', type: 'error' })
      setAuthCode('')
    } finally {
      setIsVerifyingAuth(false)
    }
  }

  // Returns/Refunds Handlers
  const handleOrderSearchKeypadInput = (value: string) => {
    if (value === 'clear') {
      setOrderSearchInput('')
    } else if (value === 'backspace') {
      setOrderSearchInput(prev => prev.slice(0, -1))
    } else if (value.match(/[0-9]/)) {
      setOrderSearchInput(prev => prev + value)
    }
  }

  const handleSearchOrder = async () => {
    if (!orderSearchInput.trim()) {
      setToast({ message: 'Please enter an order ID', type: 'error' })
      return
    }

    setIsSearchingOrder(true)
    try {
      // Search for order by order_number or id (supports both UUID and order number)
      const response = await apiService.getOrders()
      if (response.success && response.data && Array.isArray(response.data)) {
        const searchTerm = orderSearchInput.trim().toLowerCase()
        const order = (response.data as any[]).find((o: any) => {
          const orderNumber = (o.order_number || '').toLowerCase()
          const orderId = (o.id || '').toLowerCase()
          return orderNumber === searchTerm || 
                 orderId === searchTerm ||
                 orderId.includes(searchTerm) ||
                 orderNumber.includes(searchTerm)
        })
        
        if (order) {
          // Check return duration setting
          const returnDurationHours = posSettings?.returnDurationHours || 0
          
          // Only check duration if it's greater than 0 (0 means no time limit)
          if (returnDurationHours > 0) {
            // Get order time (use order_time if available, otherwise created_at)
            const orderTime = order.order_time ? new Date(order.order_time) : new Date(order.created_at)
            const currentTime = new Date()
            const hoursSinceOrder = (currentTime.getTime() - orderTime.getTime()) / (1000 * 60 * 60) // Convert milliseconds to hours

            if (hoursSinceOrder > returnDurationHours) {
              setToast({ 
                message: `This order cannot be returned. The return window has expired. Returns are only allowed within ${returnDurationHours} hours of order placement. This order was placed ${Math.round(hoursSinceOrder)} hours ago.`, 
                type: 'error' 
              })
              setSearchedOrder(null)
              setIsSearchingOrder(false)
              return
            }
          }
          
          setSearchedOrder(order)
          setSelectedReturnItems([])
          
          // Fetch existing returns for this order to check already refunded items
          // Only count returns that are "authorized" or "processed" (not "pending" or "cancelled")
          try {
            const returnsResponse = await apiService.getReturns()
            if (returnsResponse.success && returnsResponse.data && Array.isArray(returnsResponse.data)) {
              const orderReturns = (returnsResponse.data as any[]).filter((r: any) => 
                r.original_order_id === order.id && 
                (r.status === 'authorized' || r.status === 'processed')
              )
              setExistingReturns(orderReturns)
              
              // Calculate quantity already refunded per order item
              // Only count from authorized or processed returns
              const refundedMap = new Map<string, number>()
              orderReturns.forEach((returnRecord: any) => {
                // Only count if status is authorized or processed
                if ((returnRecord.status === 'authorized' || returnRecord.status === 'processed') && returnRecord.return_items) {
                  returnRecord.return_items.forEach((returnItem: any) => {
                    if (returnItem.original_order_item_id) {
                      const currentRefunded = refundedMap.get(returnItem.original_order_item_id) || 0
                      refundedMap.set(returnItem.original_order_item_id, currentRefunded + (returnItem.quantity || 0))
                    }
                  })
                }
              })
              setRefundedItemsMap(refundedMap)
            }
          } catch (err) {
            console.error('Error fetching existing returns:', err)
            // Continue even if we can't fetch returns
          }
          
          setToast({ message: 'Order found successfully', type: 'success' })
        } else {
          setToast({ message: 'Order not found. Please check the order ID and try again.', type: 'error' })
          setSearchedOrder(null)
          setExistingReturns([])
          setRefundedItemsMap(new Map())
        }
      } else {
        setToast({ message: 'Failed to search for order', type: 'error' })
      }
    } catch (error) {
      console.error('Error searching order:', error)
      setToast({ message: 'Failed to search for order', type: 'error' })
    } finally {
      setIsSearchingOrder(false)
    }
  }

  const calculateItemRefundAmount = (item: any, order: any) => {
    // All prices are VAT-inclusive (gross). Refund = amount customer paid for this item.
    const orderSubtotal = parseFloat(order.subtotal) || 0
    const orderDiscountAmount = parseFloat(order.discount_amount) || 0
    const itemTotalPrice = parseFloat(item.total_price) || 0 // gross (incl. VAT)

    const vatRateDecimal = (restaurantSettings.tax_rate || 15) / 100

    // Proportional order-level discount (amount off the gross)
    let itemDiscount = 0
    if (orderDiscountAmount > 0 && orderSubtotal > 0) {
      itemDiscount = orderDiscountAmount * (itemTotalPrice / orderSubtotal)
    }

    // Gross amount after discount = what the customer paid for this item
    const discountedGross = Math.round((itemTotalPrice - itemDiscount) * 100) / 100
    const { vat: itemVAT } = splitVatInclusive(discountedGross, vatRateDecimal)

    return {
      basePrice: itemTotalPrice,
      vat: itemVAT,
      discount: itemDiscount,
      refundAmount: discountedGross
    }
  }

  // Check if item is part of an offer/promotion
  const isItemInOffer = (item: any) => {
    if (!searchedOrder) return false
    
    // First check if order has applied_offers
    if (searchedOrder.applied_offers && Array.isArray(searchedOrder.applied_offers) && searchedOrder.applied_offers.length > 0) {
      // Check if item is part of any offer
      const isInOffer = searchedOrder.applied_offers.some((offer: any) => {
        if (offer.applicableItems && Array.isArray(offer.applicableItems)) {
          // Check both menu_item_id and id
          const itemId = item.menu_item_id || item.id
          return offer.applicableItems.includes(itemId) || offer.applicableItems.some((id: any) => String(id) === String(itemId))
        }
        // Check BOGO items
        if (offer.bogo_items && Array.isArray(offer.bogo_items)) {
          const itemId = item.menu_item_id || item.id
          return offer.bogo_items.some((bogoItem: any) => 
            String(bogoItem.buy_item_id) === String(itemId) || String(bogoItem.get_item_id) === String(itemId)
          )
        }
        // Check combo items
        if (offer.combo_items && Array.isArray(offer.combo_items)) {
          const itemId = item.menu_item_id || item.id
          return offer.combo_items.some((comboItem: any) => 
            String(comboItem.id) === String(itemId)
          )
        }
        return false
      })
      
      if (isInOffer) return true
    }
    
    // Also check if there's a significant discount on the item that suggests an offer
    // If the order has a discount and the item has a proportionally large discount, it might be from an offer
    const orderDiscountAmount = parseFloat(searchedOrder.discount_amount) || 0
    const orderSubtotal = parseFloat(searchedOrder.subtotal) || 0
    const itemTotalPrice = parseFloat(item.total_price) || 0
    
    if (orderDiscountAmount > 0 && orderSubtotal > 0 && itemTotalPrice > 0) {
      // Calculate proportional discount for this item
      const proportionalDiscount = orderDiscountAmount * (itemTotalPrice / orderSubtotal)
      const discountPercentage = (proportionalDiscount / itemTotalPrice) * 100
      
      // If discount is more than 20%, it's likely from an offer/promotion
      // (Regular discounts are usually smaller, offers are typically 50% off, BOGO, etc.)
      if (discountPercentage >= 20) {
        return true
      }
    }
    
    return false
  }

  const handleToggleReturnItem = (item: any) => {
    // Check if item is already fully refunded
    const alreadyRefunded = refundedItemsMap.get(item.id) || 0
    const itemQuantity = item.quantity || 1
    const availableQuantity = itemQuantity - alreadyRefunded
    
    if (availableQuantity <= 0) {
      setToast({ message: 'This item has already been fully refunded', type: 'error' })
      return
    }
    
    // Check if item is part of an offer
    const hasOffer = isItemInOffer(item)
    
    const existingIndex = selectedReturnItems.findIndex(ri => ri.order_item_id === item.id)
    if (existingIndex >= 0) {
      // Remove item from selection
      setSelectedReturnItems(prev => prev.filter((_, i) => i !== existingIndex))
      setReturnQuantities(prev => {
        const newMap = new Map(prev)
        newMap.delete(item.id)
        return newMap
      })
    } else {
      // Add item to selection
      // For items with offers, must return all available quantities
      // For items without offers and quantity > 1, default to 1 (can be changed)
      const returnQty = hasOffer ? availableQuantity : (availableQuantity > 1 ? 1 : availableQuantity)
      
      // Calculate actual refund amount including VAT and proportional discount
      const refundCalc = calculateItemRefundAmount(item, searchedOrder)
      
      // Calculate proportional refund amount based on return quantity
      const proportionalRefund = (refundCalc.refundAmount / itemQuantity) * returnQty
      
      setSelectedReturnItems(prev => [...prev, {
        order_item_id: item.id,
        product_id: item.product_id || item.menu_item_id || null,
        product_name: item.item_name || item.product_name || 'Unknown Item',
        quantity: returnQty,
        unit_price: parseFloat(item.unit_price) || 0,
        total_price: proportionalRefund,
        return_reason: ''
      }])
      
      setReturnQuantities(prev => {
        const newMap = new Map(prev)
        newMap.set(item.id, returnQty)
        return newMap
      })
    }
  }

  const handleReturnQuantityChange = (itemId: string, newQuantity: number, maxQuantity: number) => {
    // Check if item has an offer - if so, prevent quantity changes
    const item = (searchedOrder?.order_items_for_modification || searchedOrder?.order_items)?.find((oi: any) => oi.id === itemId)
    if (item && isItemInOffer(item)) {
      setToast({ message: 'Items with offers/promotions must be returned in full. Quantity cannot be changed.', type: 'error' })
      return
    }
    
    if (newQuantity < 1) {
      setToast({ message: 'Return quantity must be at least 1', type: 'error' })
      return
    }
    if (newQuantity > maxQuantity) {
      setToast({ message: `Cannot return more than ${maxQuantity} units`, type: 'error' })
      return
    }
    
    setReturnQuantities(prev => {
      const newMap = new Map(prev)
      newMap.set(itemId, newQuantity)
      return newMap
    })
    
    // Update the selected return item
    setSelectedReturnItems(prev => prev.map(ri => {
      if (ri.order_item_id === itemId) {
        const orderItem = (searchedOrder?.order_items_for_modification || searchedOrder?.order_items)?.find((oi: any) => oi.id === itemId)
        if (orderItem) {
          const refundCalc = calculateItemRefundAmount(orderItem, searchedOrder)
          const itemQuantity = orderItem.quantity || 1
          const proportionalRefund = (refundCalc.refundAmount / itemQuantity) * newQuantity
          
          return {
            ...ri,
            quantity: newQuantity,
            total_price: proportionalRefund
          }
        }
      }
      return ri
    }))
  }

  const handleCreateReturn = async () => {
    if (!searchedOrder || selectedReturnItems.length === 0) {
      setToast({ message: 'Please select items to return', type: 'error' })
      return
    }

    // Validate that promotion items are returned in full
    for (const returnItem of selectedReturnItems) {
      const orderItem = (searchedOrder.order_items_for_modification || searchedOrder.order_items)?.find((oi: any) => oi.id === returnItem.order_item_id)
      if (orderItem && isItemInOffer(orderItem)) {
        const alreadyRefunded = refundedItemsMap.get(orderItem.id) || 0
        const itemQuantity = orderItem.quantity || 1
        const availableQuantity = itemQuantity - alreadyRefunded
        const returnQty = returnQuantities.get(orderItem.id) || returnItem.quantity
        
        if (returnQty < availableQuantity) {
          setToast({ 
            message: `Items with promotions/offers must be returned in full. Please return all ${availableQuantity} units.`, 
            type: 'error' 
          })
          return
        }
      }
    }

    setIsProcessingReturn(true)
    try {
      // Use the quantities from returnQuantities map if available, otherwise use the item's quantity
      const returnItems = selectedReturnItems.map(item => {
        const returnQty = returnQuantities.get(item.order_item_id) || item.quantity
        // Recalculate total_price based on the return quantity
        const orderItem = (searchedOrder.order_items_for_modification || searchedOrder.order_items)?.find((oi: any) => oi.id === item.order_item_id)
        let totalPrice = item.total_price
        if (orderItem && returnQty !== item.quantity) {
          const refundCalc = calculateItemRefundAmount(orderItem, searchedOrder)
          const itemQuantity = orderItem.quantity || 1
          totalPrice = (refundCalc.refundAmount / itemQuantity) * returnQty
        }
        
        return {
          order_item_id: item.order_item_id,
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: returnQty,
          unit_price: item.unit_price,
          total_price: totalPrice,
          return_reason: item.return_reason || returnReason
        }
      })
      
      const returnData = {
        original_order_id: searchedOrder.id,
        return_reason: returnReason || null,
        return_items: returnItems,
        refund_method: refundMethod,
        notes: returnReason || null,
        return_to_inventory: returnToInventory
      }

      const response = await apiService.createReturn(returnData)
      if (response.success && response.data) {
        setCurrentReturnId((response.data as any).id)
        setToast({ message: 'Return created. Authorization required.', type: 'success' })
        setShowRefundAuthModal(true)
      } else {
        setToast({ message: response.error || 'Failed to create return', type: 'error' })
      }
    } catch (error) {
      console.error('Error creating return:', error)
      setToast({ message: 'Failed to create return', type: 'error' })
    } finally {
      setIsProcessingReturn(false)
    }
  }

  const handleRefundAuthKeypadInput = (value: string) => {
    if (value === 'clear') {
      setRefundAuthCode('')
    } else if (value === 'backspace') {
      setRefundAuthCode(prev => prev.slice(0, -1))
    } else if (value.match(/[0-9]/) && refundAuthCode.length < 6) {
      setRefundAuthCode(prev => prev + value)
    }
  }

  const handleAuthorizeRefund = async () => {
    if (!currentReturnId || !user?.id) {
      setToast({ message: 'Missing return ID or user information', type: 'error' })
      return
    }

    if (refundAuthCode.length !== 6) {
      setToast({ message: 'Please enter a 6-digit verification code', type: 'error' })
      return
    }

    setIsVerifyingRefundAuth(true)
    try {
      // Calculate actual total refund amount including VAT and discounts
      let totalRefundAmount = 0
      selectedReturnItems.forEach(item => {
        const orderItem = (searchedOrder.order_items_for_modification || searchedOrder.order_items)?.find((oi: any) => oi.id === item.order_item_id)
        if (orderItem) {
          const refundCalc = calculateItemRefundAmount(orderItem, searchedOrder)
          totalRefundAmount += refundCalc.refundAmount
        }
      })
      
      // Get user display name for tracking
      const userDisplayName = user.name || user.email || user.id || 'Unknown User'
      
      const response = await apiService.authorizeRefund(currentReturnId, {
        user_id: user.id,
        code: refundAuthCode,
        refund_amount: totalRefundAmount,
        refund_method: refundMethod,
        processed_by: userDisplayName
      })

      if (response.success && response.data) {
        // In POS flow, authorization is the final user action; process immediately.
        const processResponse = await apiService.processRefund(currentReturnId, {
          processed_by: userDisplayName
        })
        if (!processResponse.success) {
          setToast({ message: processResponse.error || 'Refund authorized but processing failed', type: 'error' })
          return
        }
        setToast({ message: 'Refund processed successfully!', type: 'success' })
        // Reset state
        setShowRefundAuthModal(false)
        setShowReturnsModal(false)
        setOrderSearchInput('')
        setSearchedOrder(null)
        setSelectedReturnItems([])
        setReturnReason('')
        setRefundAuthCode('')
        setCurrentReturnId(null)
      } else {
        setToast({ message: response.error || 'Invalid authorization code', type: 'error' })
        setRefundAuthCode('')
      }
    } catch (error) {
      console.error('Error authorizing refund:', error)
      setToast({ message: 'Failed to authorize refund', type: 'error' })
      setRefundAuthCode('')
    } finally {
      setIsVerifyingRefundAuth(false)
    }
  }

  // Get VAT rate from restaurant settings or use default
  const getVatRate = () => {
    return (restaurantSettings.tax_rate || 15) / 100 // Convert percentage to decimal
  }

  // Calculate cart totals with VAT breakdown
  const getCartTotals = () => {
    const vatRate = getVatRate()
    
    // Calculate line items (all prices are gross/VAT-inclusive)
    // NOTE: Pass unit price as gross and quantity separately - calculateOrderTotals will multiply them
    const lines = cart.map(item => {
      const unitGross = getItemPrice(item)
      const quantity = item.quantity
      // Return unit price as gross, quantity separate - calculateOrderTotals handles multiplication
      return { gross: unitGross, quantity }
    })
    
    // Calculate totals
    const discountGross = getDiscountAmount()
    
    // Use VAT utility to calculate net/VAT breakdown
    const totals = calculateOrderTotals(lines, discountGross, vatRate)
    
    return {
      subtotalNet: totals.subtotalNet,
      subtotalVat: totals.subtotalVat,
      subtotalGross: totals.subtotalGross,
      discountGross,
      discountNet: totals.orderDiscountNet,
      discountVat: totals.orderDiscountVat,
      netTotal: totals.netTotal,
      vatTotal: totals.vatTotal,
      grossTotal: totals.grossTotal
    }
  }

  const getSubtotal = () => {
    // Return gross subtotal for backward compatibility
    // But display should use getCartTotals() for net/VAT breakdown
    const subtotal = cart.reduce((total, item) => total + (getItemPrice(item) * item.quantity), 0)
    return isNaN(subtotal) ? 0 : subtotal
  }

  const getDiscountAmount = () => {
    if (!posSettings.enableDiscountSystem) return 0
    
    const subtotal = getSubtotal()
    let discountAmount = 0

    // Manual discount
    if (discountType === 'percentage') {
      const percentageDiscount = (subtotal * discount) / 100
      const maxDiscountAmount = (subtotal * posSettings.maxDiscountPercentage) / 100
      discountAmount += Math.min(percentageDiscount, maxDiscountAmount)
    } else {
      discountAmount += Math.min(discount, posSettings.maxDiscountAmount)
    }

    // Applied upsells - add upsell discounts
    appliedUpsells.forEach(upsell => {
      discountAmount += upsell.discount
    })

    // Applied offers - only count combo/BOGO discounts in summary
    // Percentage/fixed discount offers are already applied to item prices, so don't count them here
    appliedOffers.forEach(offer => {
      if (offer.type === 'combo' || offer.type === 'bogo') {
        // Combo and BOGO offers - calculate discount from original prices
        cart.forEach(item => {
          if (item.isComboItem && item.originalPrice) {
            discountAmount += (item.originalPrice - getItemPrice(item)) * item.quantity
          } else if (item.isBOGOItem && item.originalPrice) {
            discountAmount += (item.originalPrice - getItemPrice(item)) * item.quantity
          }
        })
      }
      // Don't count percentage/fixed discount offers here as they're already in item prices
    })

    return isNaN(discountAmount) ? 0 : discountAmount
  }

  const getTax = () => {
    // Return VAT amount from cart totals
    const totals = getCartTotals()
    return totals.vatTotal
  }

  const getTotal = () => {
    // Return gross total (VAT-inclusive)
    const totals = getCartTotals()
    return totals.grossTotal
  }
  
  // Get net total (VAT-exclusive) for display
  // const getNetTotal = () => {
  //   const totals = getCartTotals()
  //   return totals.netTotal
  // }

  const calculatePreparationTime = () => {
    if (cart.length === 0) return 0
    
    // Calculate preparation time based on cart items
    let maxPreparationTime = 0
    
    cart.forEach(item => {
      const itemPreparationTime = item.product.preparationTime || 10
      maxPreparationTime = Math.max(maxPreparationTime, itemPreparationTime)
    })
    
    return maxPreparationTime
  }

  const handleCheckout = () => {
    if (cart.length === 0) return
    
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before proceeding to checkout', 
        type: 'error' 
      })
      return
    }
    
    // For catering orders, show delivery details modal first
    if (orderType === 'catering') {
      // Set default delivery date to tomorrow
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      setDeliveryDate(tomorrow.toISOString().split('T')[0])
      // Set default delivery time to 12:00 PM
      setDeliveryTime('12:00')
      setShowDeliveryModal(true)
      return
    }
    
    // Check for upsells
    const subtotal = getSubtotal()
    const upsellItems = products.flatMap(p => p.upsells || [])
      .filter(upsell => subtotal >= upsell.triggerAmount)
    
    if (upsellItems.length > 0) {
      setShowUpsellModal(true)
    } else {
      setShowPaymentModal(true)
    }
  }

  const handleDeliveryDetailsSubmit = () => {
    if (!deliveryDate || !deliveryTime) {
      setToast({
        message: 'Please select delivery date and time',
        type: 'error'
      })
      return
    }
    
    // Close delivery modal and proceed to upsell/payment modal
    setShowDeliveryModal(false)
    
    // Check for upsells
    const subtotal = getSubtotal()
    const upsellItems = products.flatMap(p => p.upsells || [])
      .filter(upsell => subtotal >= upsell.triggerAmount)
    
    if (upsellItems.length > 0) {
      setShowUpsellModal(true)
    } else {
      setShowPaymentModal(true)
    }
  }

  const processPayment = async (creditOverride?: { coName: string; customerName: string }) => {
    // Check if register is open
    if (!registerStatus?.isOpen) {
      setToast({ 
        message: 'Please open the cash register before placing an order', 
        type: 'error' 
      })
      setIsProcessingPayment(false)
      return
    }

    const coFinal = (creditOverride?.coName ?? creditDinnerCoName).trim()
    const custFinal = (creditOverride?.customerName ?? creditDinnerCustomerName).trim()

    if (posSettings.creditDinnerEnabled) {
      const sub = getSubtotal()
      const disc = getDiscountAmount()
      const fullComp =
        (discountType === 'percentage' && discount >= 99.99) ||
        (sub > 0 && disc >= sub - 0.05)
      if (fullComp && !coFinal) {
        setCreditDinnerModalContext('payment')
        setCreditDinnerCustomerName(
          custFinal || selectedCustomer?.name || (selectedCustomer as any)?.firstName || 'Guest'
        )
        setShowCreditDinnerModal(true)
        return
      }
    }
    
    setIsProcessingPayment(true)
    
    try {
      // If it's a guest customer without an ID, create the customer in database first
      let customerId = selectedCustomer?.id
      if (isGuestCustomer && !customerId) {
        const phoneNumber = selectedCustomer?.phone || selectedCustomer?.mobileNumber
        const defaultName = phoneNumber ? 'Registered Customer' : posSettings.guestCustomerName
        const customerResponse = await apiService.createCustomer({
          phone: phoneNumber || 'Guest10001',
          name: selectedCustomer?.name || defaultName,
          firstName: selectedCustomer?.name || defaultName,
          companyName: selectedCustomer?.companyName || customerFormData.companyName,
          vatNumber: selectedCustomer?.vatNumber || customerFormData.vatNumber,
          isActive: true
        })
        if (customerResponse.success && customerResponse.data) {
          customerId = (customerResponse.data as any).id
        }
      }
      
      // Primary payment method determined by split payment logic (used in order data below)

      // Calculate total change for split payments (cash portions only)
      const totalSplitChange = isSplitPayment 
        ? splitPayments.reduce((sum, p) => sum + (p.change_amount || 0), 0)
        : 0
      
      // Prepare order data
      const orderData = {
        customer_id: customerId,
        customer_name: selectedCustomer?.name || selectedCustomer?.firstName || 'Guest',
        customer_phone: (selectedCustomer as any)?.phone_number || selectedCustomer?.mobileNumber || (selectedCustomer as any)?.phone,
        customer_company_name: selectedCustomer?.companyName || customerFormData.companyName,
        customer_vat_number: selectedCustomer?.vatNumber || customerFormData.vatNumber,
        order_type: orderType === 'dine-in' ? 'dine_in' : orderType === 'catering' ? 'catering' : 'takeaway',
        table_number: selectedTable || null,
        table_id: selectedTableId || null,
        delivery_date: orderType === 'catering' ? deliveryDate : null,
        delivery_time: orderType === 'catering' ? deliveryTime : null,
        delivery_address: orderType === 'catering' ? deliveryAddress || null : null,
        subtotal: getSubtotal(),
        tax_amount: getTax(),
        service_charge: 0, // Add service charge if needed
        discount_amount: getDiscountAmount(),
        discount_type: discountType,
        discount_percentage: discount > 0 && discountType === 'percentage' ? discount : null,
        discount_authorized_by: discountAuthorizedBy, // User ID who authorized the discount
        discount_authorized_by_secret_id: discountAuthorizedBySecret?.id || null, // TOTP secret ID that authorized the discount
        discount_authorized_by_secret_name: discountAuthorizedBySecret?.name || null, // TOTP secret name (e.g., "Manager", "Supervisor")
        total_amount: getTotal(),
        payment_method: isSplitPayment ? 'split' : paymentMethod,
        payment_status: paymentMethod === 'credit' ? 'pending' : 'paid',
        paid_amount: isSplitPayment ? getSplitPaymentsTotal() : (paymentMethod === 'credit' ? 0 : getTotal()),
        change_amount: isSplitPayment ? totalSplitChange : (paymentMethod === 'cash' ? parseFloat(receivedAmount) - getTotal() : 0),
        is_split_payment: isSplitPayment,
        split_payments: isSplitPayment ? splitPayments.map(sp => ({
          payment_method: sp.payment_method,
          amount: sp.amount,
          received_amount: sp.received_amount,
          change_amount: sp.change_amount,
          reference_number: sp.reference_number
        })) : null,
        split_payment_details: isSplitPayment ? {
          total_payments: splitPayments.length,
          cash_amount: splitPayments.filter(p => p.payment_method === 'cash').reduce((sum, p) => sum + p.amount, 0),
          card_amount: splitPayments.filter(p => p.payment_method === 'card').reduce((sum, p) => sum + p.amount, 0),
          digital_amount: splitPayments.filter(p => p.payment_method === 'digital').reduce((sum, p) => sum + p.amount, 0),
          total_change: totalSplitChange,
          payments: splitPayments
        } : null,
        applied_offers: appliedOffers,
        applied_visit_based_offers: appliedVisitBasedOffers,
        applied_upsells: appliedUpsells,
        notes: orderNotes,
        preparation_time: calculatePreparationTime(),
        ...(posSettings.creditDinnerEnabled && coFinal
          ? (() => {
              const sub = getSubtotal()
              const disc = getDiscountAmount()
              const fullComp =
                (discountType === 'percentage' && discount >= 99.99) ||
                (sub > 0 && disc >= sub - 0.05)
              return fullComp
                ? {
                    credit_dinner: true,
                    credit_dinner_co_name: coFinal,
                    credit_dinner_customer_display:
                      custFinal ||
                      selectedCustomer?.name ||
                      (selectedCustomer as any)?.firstName ||
                      'Guest',
                    credit_dinner_amount: getSubtotal()
                  }
                : {}
            })()
          : {}),
        items: cart.map(item => ({
          menu_item_id: item.product.id,
          item_code: item.product.code || null,
          item_name: item.name,
          item_description: item.description,
          base_price: item.basePrice,
          variant_name: item.selectedVariant?.name,
          variant_price_modifier: item.selectedVariant?.priceModifier || 0,
          addons: item.selectedAddons || [],
          kds_station_id: (item as any).kdsStationId || ((item.selectedAddons?.[0] as any)?.kdsStationId),
          quantity: item.quantity,
          unit_price: item.finalPrice,
          total_price: item.finalPrice * item.quantity,
          notes: item.notes
        }))
      }

      // Create order in database
      const response = await apiService.createOrder(orderData)
      
      if (response.success) {
        
        // Record visit-based offer usage
        if (appliedVisitBasedOffers.length > 0 && selectedCustomer?.id) {
          try {
            for (const offer of appliedVisitBasedOffers) {
              await apiService.useVisitBasedOffer(offer.id, selectedCustomer.id, (response.data as any).id)
            }
          } catch (error) {
            console.error('Error recording visit-based offer usage:', error)
            // Don't fail the order if recording fails
          }
        }
        
        // Clear cart and close modals first
        setCart([])
        setSelectedTable('')
        setSelectedTableId('')
        setDiscount(0)
        setCreditDinnerCoName('')
        setCreditDinnerCustomerName('')
        setDiscountAuthorizedBy(null) // Reset discount authorization
        setDiscountAuthorizedBySecret(null) // Reset discount authorization secret
        setOrderNotes('')
        setAppliedOffers([])
        setAppliedVisitBasedOffers([]) // Clear applied visit-based offers
        setAppliedUpsells([]) // Clear applied upsells
        setShowPaymentModal(false)
        setShowUpsellModal(false)
        setReceivedAmount('')
        setShowCashKeypad(false)
        setPaymentMethod('')
        resetSplitPayment() // Reset split payment state
        
        // Refresh visit count and available visit-based offers for registered customers after order placement (before clearing selection)
        if (!isGuestCustomer && selectedCustomer?.id) {
          try {
            const visitResponse = await apiService.getCustomerVisitCount(selectedCustomer.id)
            if (visitResponse.success && visitResponse.data !== undefined && visitResponse.data !== null) {
              setCustomerVisitCount((visitResponse.data as any).visitCount || 0)
            }
            
            // Refresh available visit-based offers
            const offersResponse = await apiService.getAvailableVisitBasedOffers(selectedCustomer.id)
            if (offersResponse.success && offersResponse.data) {
              setAvailableVisitBasedOffers(Array.isArray(offersResponse.data) ? offersResponse.data : [])
            }
          } catch (error) {
            console.error('Error refreshing visit count and offers:', error)
          }
        }
        
        // Refresh register status if payment was cash (to update expected cash)
        if (paymentMethod === 'cash') {
          try {
            await checkRegisterStatus()
          } catch (error) {
            console.error('Error refreshing register status:', error)
          }
        }
        
        // If it's a guest customer, increment the guest number for next order
        if (isGuestCustomer && posSettings.autoIncrementGuestNumber) {
          await initializeGuestCustomer()
        } else {
          // Clear customer selection for registered customers
          setSelectedCustomer(null)
          setIsGuestCustomer(false)
          setCustomerVisitCount(null)
        }
        
        // Extract token number if available
        const orderData = response.data as any
        if (orderData.token_number) {
          setLastOrderToken(orderData.token_number)
        }
        
        // Show success toast with token if available
        const toastMessage = orderData.token_number 
          ? `Order #${orderData.order_number} created successfully! Token #${orderData.token_number}`
          : `Order #${orderData.order_number} created successfully!`
        setToast({ message: toastMessage, type: 'success' })
        
        // Handle receipt printing
        if (orderData) {
          // Check if auto print is enabled
          const enableAutoPrint = posSettings?.enableAutoPrint === true
          const printerType = posSettings?.printerType || 'system'
          const isTcpIp = printerType === 'tcp_ip'
          const isSystem = printerType === 'system'
          const hasPrinterIp = posSettings?.printerIp && posSettings.printerIp.trim() !== ''
          const hasPrinterName = posSettings?.printerName && posSettings.printerName.trim() !== ''
          
          const shouldAutoPrintTcpIp = enableAutoPrint && isTcpIp && hasPrinterIp
          const shouldAutoPrintSystem = enableAutoPrint && isSystem && hasPrinterName
          
          if (shouldAutoPrintTcpIp) {
            // Print directly to TCP/IP printer via backend (NO DIALOG)
            const printerConfig = {
              printerIp: posSettings.printerIp!,
              printerPort: posSettings.printerPort || 9100,
              printerName: posSettings.printerName,
              printerModel: posSettings.printerModel
            }
            
            // IMPORTANT: Do NOT set receiptData - this prevents the print dialog
            apiService.printReceipt(
              orderData, 
              restaurantSettings, 
              printerConfig,
              posSettings.receiptLayout,
              posSettings.printerWidth || 80,
              posSettings.receiptLogoUrl
            )
              .then((printResponse) => {
                if (!printResponse.success) {
                  console.error('Failed to print receipt:', printResponse.error)
                  // Don't show print dialog even on failure - just show error toast
                  setToast({ 
                    message: `Order created but print failed: ${printResponse.error}`, 
                    type: 'error' 
                  })
                }
              })
              .catch((error) => {
                console.error('Print error:', error)
                // Don't show print dialog even on error - just show error toast
                setToast({ 
                  message: `Order created but print failed: ${error.message}`, 
                  type: 'error' 
                })
              })
          } else if (shouldAutoPrintSystem) {
            // Print to system printer using browser Print API (silent mode attempt)
            // Generate ZATCA QR code if enabled
            let zatcaQRCode = null
            if (restaurantSettings?.zatca_phase1_enabled) {
              try {
                const qrResponse = await apiService.generateZATCAQRCode(orderData, restaurantSettings)
                if (qrResponse.success && qrResponse.data) {
                  zatcaQRCode = qrResponse.data
                } else {
                  console.error('❌ ZATCA QR code generation failed:', qrResponse.error)
                }
              } catch (error) {
                console.error('❌ Error generating ZATCA QR code:', error)
              }
            }
            // Set receiptData with autoPrint flag - ThermalReceipt will handle printing
            setReceiptData({ ...orderData, _autoPrint: true, zatcaQRCode })
          } else {
            // Auto print disabled or not configured - show browser print dialog
            // Generate ZATCA QR code if enabled
            let zatcaQRCode = null
            if (restaurantSettings?.zatca_phase1_enabled) {
              try {
                const qrResponse = await apiService.generateZATCAQRCode(orderData, restaurantSettings)
                if (qrResponse.success && qrResponse.data) {
                  zatcaQRCode = qrResponse.data
                } else {
                  console.error('❌ ZATCA QR code generation failed:', qrResponse.error)
                }
              } catch (error) {
                console.error('❌ Error generating ZATCA QR code:', error)
              }
            }
            setReceiptData({ ...orderData, zatcaQRCode })
          }
        }
      } else {
        console.error('Failed to create order:', response.error)
        setToast({ message: 'Failed to create order. Please try again.', type: 'error' })
      }
    } catch (error) {
      console.error('Error creating order:', error)
      setToast({ message: 'Error creating order. Please try again.', type: 'error' })
    } finally {
      setIsProcessingPayment(false)
    }
  }

  const applyOffer = (offer: Offer) => {
    if (!appliedOffers.find(o => o.id === offer.id)) {
      if (offer.type === 'combo') {
        // Handle combo deal application
        applyComboOffer(offer)
      } else if (offer.type === 'bogo') {
        // Handle BOGO deal application
        applyBOGOOffer(offer)
      } else {
        // Handle regular offers (percentage, fixed) - apply discount to applicable items
        applyDiscountOffer(offer)
      }
    }
  }

  const applyDiscountOffer = (offer: Offer) => {
    if (offer.type !== 'percentage' && offer.type !== 'fixed') {
      return // Only handle percentage and fixed discount offers
    }
    
    const newCartItems: CartItem[] = cart.map(item => {
      // Check if this item is applicable for the offer
      if (offer.applicableItems && offer.applicableItems.includes(item.product.id)) {
        // Get the base price (main item + variant only, excluding addons)
        let mainItemPrice = item.product.basePrice
        if (item.selectedVariant) {
          mainItemPrice += item.selectedVariant.priceModifier
        }
        
        // Calculate addon prices separately (not discounted)
        const addonPrice = item.selectedAddons 
          ? item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
          : 0
        
        // Original price is main item + addons (for display purposes)
        const originalPrice = mainItemPrice + addonPrice
        
        // Apply discount only to the main item price (not addons)
        let discountedMainItemPrice = mainItemPrice
        
        if (offer.type === 'percentage') {
          // Calculate percentage discount on main item only
          const discountAmount = (mainItemPrice * offer.value) / 100
          const maxDiscount = offer.maxDiscount ? (mainItemPrice * offer.maxDiscount) / 100 : discountAmount
          discountedMainItemPrice = mainItemPrice - Math.min(discountAmount, maxDiscount)
        } else if (offer.type === 'fixed') {
          // Apply fixed discount to main item only
          discountedMainItemPrice = Math.max(0, mainItemPrice - offer.value)
        }
        
        // Final price = discounted main item + full addon price
        const finalPrice = discountedMainItemPrice + addonPrice
        
        return {
          ...item,
          discountOfferId: offer.id,
          discountOriginalPrice: originalPrice,
          discountType: offer.type as 'percentage' | 'fixed',
          discountValue: offer.value,
          finalPrice: finalPrice
        } as CartItem
      }
      return item
    })
    
    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const applyComboOffer = (offer: Offer) => {
    if (!(offer as any).comboItems || (offer as any).comboItems.length === 0) return

    const newCartItems: CartItem[] = Array.isArray(cart) ? [...cart] : [];
    // const cartProductIds = newCartItems.map(item => item.product.id)
    
    // Add missing combo items to cart
    (offer as any).comboItems.forEach((comboItem: any) => {
      const itemId = comboItem.menuItemId || comboItem.id || comboItem.productId
      const existingItemIndex = newCartItems.findIndex(item => item.product.id === itemId)
      
      if (existingItemIndex >= 0) {
        // Item already in cart - mark as combo item and adjust price
        newCartItems[existingItemIndex] = {
          ...newCartItems[existingItemIndex],
          isComboItem: true,
          comboPrice: comboItem.price || comboItem.comboPrice || 0,
          originalPrice: newCartItems[existingItemIndex].product.basePrice
        }
      } else {
        // Item not in cart - add it
        const product = products.find(p => p.id === itemId)
        if (product) {
          newCartItems.push({
            id: product.id,
            name: product.name,
            description: product.description,
            basePrice: product.basePrice,
            finalPrice: comboItem.price || comboItem.comboPrice || product.basePrice,
            kdsStationId: (product as any).kdsStationId || undefined,
            product,
            quantity: 1,
            selectedVariant: undefined,
            selectedAddons: [],
            notes: '',
            isComboItem: true,
            comboPrice: comboItem.price || comboItem.comboPrice || 0,
            originalPrice: product.basePrice
          })
        }
      }
    })

    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const applyBOGOOffer = (offer: Offer) => {
    if (!(offer as any).bogoItems || (offer as any).bogoItems.length === 0) return

    const newCartItems: CartItem[] = Array.isArray(cart) ? [...cart] : [];

    // Process each BOGO item
    (offer as any).bogoItems.forEach((bogoItem: any) => {
      const buyItemId = bogoItem.buyItemId || bogoItem.menuItemId || bogoItem.id || bogoItem.productId
      const getItemId = bogoItem.getItemId || bogoItem.menuItemId || bogoItem.id || bogoItem.productId
      
      // Find the buy item in cart
      const buyItemIndex = newCartItems.findIndex(item => item.product.id === buyItemId)
      
      if (buyItemIndex >= 0) {
        const buyItem = newCartItems[buyItemIndex]
        
        // For BOGO, ensure we have at least 2 items (1 paid + 1 free)
        let finalQuantity = buyItem.quantity
        if (finalQuantity < 2) {
          finalQuantity = 2 // Auto-add to make BOGO work
        }
        
        // Calculate BOGO quantities
        const paidQuantity = Math.floor(finalQuantity / 2) // Every 2nd item is free
        const freeQuantity = Math.floor(finalQuantity / 2)
        
        // Update the buy item quantity and mark as BOGO
        newCartItems[buyItemIndex] = {
          ...buyItem,
          quantity: finalQuantity,
          isBOGOItem: true,
          bogoDiscountType: bogoItem.discountType || 'free',
          bogoDiscountValue: bogoItem.discountValue || 0,
          originalPrice: buyItem.product.basePrice,
          paidQuantity: paidQuantity,
          freeQuantity: freeQuantity
        }
        
        // If get item is different from buy item, add it to cart
        if (getItemId !== buyItemId) {
          const getItemExists = newCartItems.find(item => item.product.id === getItemId)
          if (!getItemExists) {
            const getProduct = products.find(p => p.id === getItemId)
            if (getProduct) {
              newCartItems.push({
                id: getProduct.id,
                name: getProduct.name,
                description: getProduct.description,
                basePrice: getProduct.basePrice,
                finalPrice: bogoItem.discountType === 'free' ? 0 : getProduct.basePrice - (bogoItem.discountValue || 0),
                kdsStationId: getProduct.kdsStationId,
                product: getProduct,
                quantity: freeQuantity,
                selectedVariant: undefined,
                selectedAddons: [],
                notes: '',
                isBOGOItem: true,
                bogoDiscountType: bogoItem.discountType || 'free',
                bogoDiscountValue: bogoItem.discountValue || 0,
                originalPrice: getProduct.basePrice,
                paidQuantity: 0,
                freeQuantity: freeQuantity
              })
            }
          }
        }
      }
    })

    setCart(newCartItems)
    setAppliedOffers([...appliedOffers, offer])
  }

  const removeOffer = (offerId: string) => {
    const offer = appliedOffers.find(o => o.id === offerId)
    if (offer) {
      if (offer.type === 'combo') {
        // Remove combo pricing and restore original pricing
        const newCartItems = cart.map(item => {
          if (item.isComboItem) {
            return {
              ...item,
              isComboItem: false,
              comboPrice: undefined,
              originalPrice: undefined
            }
          }
          return item
        })
        setCart(newCartItems)
      } else if (offer.type === 'bogo') {
        // Remove BOGO pricing and restore original pricing
        const newCartItems = cart.map(item => {
          if (item.isBOGOItem) {
            return {
              ...item,
              isBOGOItem: false,
              bogoDiscountType: undefined,
              bogoDiscountValue: undefined,
              originalPrice: undefined,
              paidQuantity: undefined,
              freeQuantity: undefined
            }
          }
          return item
        })
        setCart(newCartItems)
      } else if (offer.type === 'percentage' || offer.type === 'fixed') {
        // Remove discount pricing and restore original pricing
        const newCartItems = cart.map(item => {
          if (item.discountOfferId === offerId) {
            // Restore original price
            let basePrice = item.product.basePrice
            if (item.selectedVariant) {
              basePrice += item.selectedVariant.priceModifier
            }
            if (item.selectedAddons) {
              basePrice += item.selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
            }
            return {
              ...item,
              discountOfferId: undefined,
              discountOriginalPrice: undefined,
              discountType: undefined,
              discountValue: undefined,
              finalPrice: basePrice
            }
          }
          return item
        })
        setCart(newCartItems)
      }
    }
    setAppliedOffers(appliedOffers.filter(o => o.id !== offerId))
  }

  // Get applicable offers based on cart contents
  const getApplicableOffers = () => {
    if (cart.length === 0) return []
    
    const cartProductIds = cart.map(item => item.product.id)
    const subtotal = getSubtotal()
    
    return availableOffers.filter(offer => {
      // Check if offer is active
      if (!offer.isActive) {
        return false
      }
      
      // Check if cart has applicable items
      let hasApplicableItems = false
      
      if (offer.type === 'combo') {
        // For combo deals, check if cart contains any of the combo items
        const comboItemIds = (offer as any).comboItems?.map((item: any) => item.menuItemId || item.id || item.productId) || []
        hasApplicableItems = comboItemIds.some((itemId: string) => cartProductIds.includes(itemId))
      } else if (offer.type === 'bogo') {
        // For BOGO deals, check if cart contains any of the BOGO items
        const bogoItemIds = (offer as any).bogoItems?.map((item: any) => {
          // Try multiple possible field names including BOGO-specific fields
          return item.buyItemId || item.getItemId || item.menuItemId || item.id || item.productId || item.itemId || item.product_id || item.menu_item_id
        }) || []
        hasApplicableItems = bogoItemIds.some((itemId: string) => cartProductIds.includes(itemId))
      } else {
        // For percentage/fixed offers, check applicable items
        hasApplicableItems = offer.applicableItems.some(itemId => cartProductIds.includes(itemId))
      }
      
      if (!hasApplicableItems) {
        return false
      }
      
      // Check minimum order amount
      if (offer.minOrderAmount && subtotal < offer.minOrderAmount) {
        return false
      }
      
      // Check if offer is already applied
      if (appliedOffers.find(o => o.id === offer.id)) {
        return false
      }
      return true
    })
  }

  // const productCategories = [...new Set(products.map(p => p.categoryId))]
  
  // Helper function to get all category IDs including subcategories
  const getCategoryAndSubcategoryIds = (categoryId: string): string[] => {
    if (categoryId === 'all') return []
    
    const category = getCategoryById(categoryId)
    if (!category) return [categoryId]
    
    // Get all subcategory IDs
    const subcategoryIds = category.children?.map(child => child.id) || []
    
    // Return the main category ID plus all subcategory IDs
    return [categoryId, ...subcategoryIds]
  }
  
  // Helper function to calculate item count for a category
  const getCategoryItemCount = (categoryId: string): number => {
    const categoryIds = getCategoryAndSubcategoryIds(categoryId)
    return products.filter(p => categoryIds.includes(p.categoryId) && p.isActive).length
  }
  
  const filteredProducts = (selectedCategoryFilter === 'all' 
    ? products.filter(p => p.isActive)
    : products.filter(p => {
        const categoryIds = getCategoryAndSubcategoryIds(selectedCategoryFilter)
        return categoryIds.includes(p.categoryId) && p.isActive
      })
  ).filter(p => {
    if (!productSearchTerm.trim()) return true
    const searchLower = productSearchTerm.toLowerCase()
    const category = getCategoryById(p.categoryId)
    return p.code?.toLowerCase().includes(searchLower) ||
           p.name.toLowerCase().includes(searchLower) ||
           p.description?.toLowerCase().includes(searchLower) ||
           category?.name?.toLowerCase().includes(searchLower)
  })
  .sort((a, b) => compareProductCode(a.code, b.code))

  // Infinite scroll: initial 15, load 15 more when scrolled near bottom
  useEffect(() => {
    setProductsToShowCount(15)
  }, [selectedCategoryFilter, productSearchTerm])

  const productsToShow = filteredProducts.slice(0, productsToShowCount)
  const hasMoreProducts = productsToShowCount < filteredProducts.length

  const handleProductsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200
    if (nearBottom && hasMoreProducts) {
      setProductsToShowCount(prev => Math.min(prev + 15, filteredProducts.length))
    }
  }

  // const subtotal = getSubtotal()
  // const discountAmount = getDiscountAmount()
  // const tax = getTax()
  // const total = getTotal()

  // Memoize bill request lookup for discount modal to prevent recalculation on every keystroke
  const billRequestDiscountModalData = useMemo(() => {
    if (!showBillRequestDiscountModal) return null

    const requestId = showBillRequestDiscountModal
    const request = billRequests.find(r => r.id === requestId)
    const order = request?.orders || {}
    const displayLineItems =
      Array.isArray(order.order_items_for_modification) && order.order_items_for_modification.length > 0
        ? order.order_items_for_modification
        : deduplicateOrderItems(order.order_items || [], false, false)
    let pendingAdditionalItems: any[] = []
    const rawPending = order.pending_additional_items
    if (rawPending != null) {
      if (typeof rawPending === 'string') {
        try {
          pendingAdditionalItems = JSON.parse(rawPending)
        } catch {
          pendingAdditionalItems = []
        }
      } else if (Array.isArray(rawPending)) {
        pendingAdditionalItems = rawPending
      }
    }
    const hasAnyBillLines = displayLineItems.length > 0 || pendingAdditionalItems.length > 0
    const linesSum = hasAnyBillLines
      ? displayLineItems.reduce((s: number, it: any) => s + getBillRequestLineTotal(it), 0) +
        pendingAdditionalItems.reduce((s: number, it: any) => s + getBillRequestLineTotal(it), 0)
      : null
    const subtotal = linesSum != null ? linesSum : Number(order.subtotal) || 0

    return {
      requestId,
      request,
      subtotal
    }
  }, [showBillRequestDiscountModal, billRequests])

  return (
    <div className="h-full flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Left Side - Products */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Fixed Header Section */}
        <div className="flex-shrink-0">
          {/* Customer/Order Info Section - Full Width with Background and Border */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 -mt-0">
            <div className="flex items-center justify-between space-x-4 px-4 lg:px-6 pb-3 pt-3 min-h-[3rem]">
            {(posSettings?.allowDineInFromPOS || posSettings?.allowCateringOrders) && (
              <>
                <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  {posSettings?.allowDineInFromPOS && (
                    <button
                      onClick={() => setOrderType('dine-in')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        orderType === 'dine-in'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Dine In
                    </button>
                  )}
                  <button
                    onClick={() => setOrderType('takeaway')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      orderType === 'takeaway'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Takeaway
                  </button>
                  {posSettings?.allowCateringOrders && (
                    <button
                      onClick={() => setOrderType('catering')}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        orderType === 'catering'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Catering
                    </button>
                  )}
                </div>
                <div className="self-stretch w-px bg-gray-300 dark:bg-gray-600 -my-3"></div>
              </>
            )}
            <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div>
                  {isGuestCustomer ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">Guest</span>
                      {(selectedCustomer?.mobileNumber || selectedCustomer?.phone) && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {selectedCustomer.mobileNumber || selectedCustomer.phone}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedCustomer ? 
                          selectedCustomer.name || 
                          `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || 
                          (selectedCustomer.mobileNumber || selectedCustomer.phone) : 
                          'No customer selected'
                        }
                      </div>
                      {(selectedCustomer?.mobileNumber || selectedCustomer?.phone) && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedCustomer.mobileNumber || selectedCustomer.phone}
                          </span>
                          {!isGuestCustomer && selectedCustomer?.id && customerVisitCount !== null && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              • {customerVisitCount} {customerVisitCount === 1 ? 'visit' : 'visits'}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  // Clear all form data when opening modal for new customer
                  setCustomerFormData({})
                  setShowCustomerModal(true)
                  setShowPhoneKeypad(true)
                }}
                className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-md transition-colors"
              >
                <UserIcon className="h-3.5 w-3.5 mr-1" />
                Add Details
              </button>
            </div>
            <div className="self-stretch w-px bg-gray-300 dark:bg-gray-600 -my-3"></div>
            {/* Table Selector - Only for Dine In */}
            {orderType === 'dine-in' && (
              <>
                <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="3" y1="9" x2="21" y2="9"></line>
                      <line x1="9" y1="21" x2="9" y2="9"></line>
                    </svg>
                    <div>
                      {selectedTable ? (
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Table {selectedTable}</span>
                      ) : (
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">No table selected</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTableSelector(true)}
                    className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-md transition-colors"
                  >
                    {selectedTable ? 'Change' : 'Select'}
                  </button>
                </div>
                <div className="self-stretch w-px bg-gray-300 dark:bg-gray-600 -my-3"></div>
              </>
            )}
            {/* Search Bar */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                  className={`w-full py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    productSearchTerm ? 'pl-10 pr-10' : 'pl-10 pr-4'
                  }`}
                />
                {productSearchTerm && (
                  <button
                    onClick={() => setProductSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Clear search"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="self-stretch w-px bg-gray-300 dark:bg-gray-600 -my-3"></div>
            {/* Action Icons */}
            <div className="flex items-center space-x-2">
              {draftOrdersCount > 0 && (
                <button
                  onClick={() => setShowDraftOrdersModal(true)}
                  className="relative p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  title="Waiter Orders"
                >
                  <UserIcon className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {draftOrdersCount}
                  </span>
                </button>
              )}
              {pendingOrdersCount > 0 && (
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="relative p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Pending Orders"
                >
                  <ClipboardDocumentCheckIcon className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingOrdersCount}
                  </span>
                </button>
              )}
              {modificationRequestsCount > 0 && (
                <button
                  onClick={() => setShowModificationRequestsModal(true)}
                  className="relative p-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  title="Modification Requests"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {modificationRequestsCount}
                  </span>
                </button>
              )}
              <button
                onClick={() => setShowCancellationRequestsModal(true)}
                className={`relative p-2 rounded-lg transition-colors ${
                  cancellationRequestsCount > 0 || cancellationRequests.length > 0
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
                title="Cancellation Requests"
              >
                <XMarkIcon className="h-5 w-5" />
                {(cancellationRequestsCount > 0 || cancellationRequests.length > 0) && (
                  <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cancellationRequestsCount || cancellationRequests.length}
                  </span>
                )}
              </button>
              {billRequestsCount > 0 && (
                <button
                  onClick={() => setShowBillRequestsModal(true)}
                  className="relative p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  title="Bill Requests"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {billRequestsCount}
                  </span>
                </button>
              )}
              {/* TODO: Enable Pending Payments button in future
              <button
                onClick={async () => {
                  setShowPendingPayments(true)
                  await loadPendingPaymentOrders()
                }}
                className={`relative p-2 rounded-md transition-colors ${
                  pendingPaymentOrders.length > 0
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                    : 'bg-gray-400 text-white hover:bg-gray-500'
                }`}
                title="Pending Payments"
              >
                <CreditCardIcon className="h-5 w-5" />
                {pendingPaymentOrders.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingPaymentOrders.length}
                  </span>
                )}
              </button>
              */}
              <button
                onClick={() => setShowReturnsModal(true)}
                className="p-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                title="Refund"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
              {!registerStatus?.isOpen && (
                <button
                  onClick={() => {
                    setShowOpeningModal(true)
                  }}
                  className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  title="Open Register"
                >
                  <BanknotesIcon className="h-5 w-5" />
                </button>
              )}
              {registerStatus?.isOpen && (
                <button
                  onClick={async () => {
                    // Refresh register status to get latest expected cash
                    setIsRefreshingRegisterStatus(true)
                    try {
                      await checkRegisterStatus()
                      setShowClosingModal(true)
                    } finally {
                      setIsRefreshingRegisterStatus(false)
                    }
                  }}
                  disabled={isRefreshingRegisterStatus}
                  className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Close Register"
                >
                  {isRefreshingRegisterStatus ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <BanknotesIcon className="h-5 w-5" />
                  )}
                </button>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Category Filter - Main Categories Only (compact, two lines) */}
        <div className="mb-3 px-4 lg:px-6 pt-2">
          <div className="overflow-x-auto thin-scrollbar">
            <div className="grid grid-rows-2 grid-flow-col gap-2 min-w-max auto-cols-max">
              <button 
                onClick={() => setSelectedCategoryFilter('all')}
                className={`flex items-center gap-2 py-1.5 pl-2 pr-2.5 rounded-lg border-2 transition-all duration-200 shrink-0 ${
                  selectedCategoryFilter === 'all'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <span className="text-base leading-none">📋</span>
                <span className="font-medium text-xs whitespace-nowrap">All Items</span>
                <span className={`min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold flex items-center justify-center shrink-0 ${
                  selectedCategoryFilter === 'all' ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {products.filter(p => p.isActive).length}
                </span>
              </button>
              {categories.filter(cat => (cat.parentId === null || cat.parentId === undefined || cat.parentId === '') && cat.isActive).map(category => {
                const count = getCategoryItemCount(category.id)
                return (
                <button 
                  key={category.id}
                  onClick={() => setSelectedCategoryFilter(category.id)}
                  className={`flex items-center gap-2 py-1.5 pl-2 pr-2.5 rounded-lg border-2 transition-all duration-200 shrink-0 ${
                    selectedCategoryFilter === category.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <span className="text-base leading-none shrink-0">{category.icon || '📁'}</span>
                  <span className="font-medium text-xs whitespace-nowrap max-w-[90px] truncate">{category.name}</span>
                  <span className={`min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-semibold flex items-center justify-center shrink-0 ${
                    selectedCategoryFilter === category.id ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}>
                    {count}
                  </span>
                </button>
              )})}
            </div>
          </div>
        </div>

        {/* Scrollable Products Grid */}
        <div
          className="flex-1 overflow-y-auto min-h-0 px-4 lg:px-6 pb-4 lg:pb-6"
          onScroll={handleProductsScroll}
        >
          {loading ? (
            <div className="grid grid-cols-5 gap-3">
              {Array.from({ length: 15 }).map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-3">
                    <div className="aspect-square rounded-lg mb-2 shimmer" />
                    <div className="h-4 rounded mb-2 w-3/4 shimmer" />
                    <div className="h-4 rounded mb-1 w-1/2 shimmer" />
                    <div className="h-5 rounded mt-2 w-2/5 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-3">
              {productsToShow.map(product => (
                <div 
                key={product.id}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow relative ${
                  registerStatus?.isOpen && product.isActive
                    ? 'hover:shadow-md cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (registerStatus?.isOpen && product.isActive) {
                    addToCart(product)
                  } else if (!registerStatus?.isOpen) {
                    setToast({ 
                      message: 'Please open the cash register before adding items to cart', 
                      type: 'error' 
                    })
                  }
                }}
              >
                {product.code && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs font-mono font-semibold px-2 py-1 rounded-md z-10">
                    {product.code}
                  </div>
                )}
                {product.isPopular && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center z-10">
                    <StarIcon className="h-3 w-3 mr-1" />
                    Popular
                  </div>
                )}
                <div className="p-3">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ShoppingCartIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm mb-1 line-clamp-2 min-h-[2.5rem]">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(product.basePrice)}
                    </span>
                    {!product.isActive && (
                      <span className="text-xs text-red-500">Unavailable</span>
                    )}
                  </div>
                  {(product.calories != null || product.stepsToBurn != null) && (
                    <div className="flex items-center justify-between gap-2 mt-1.5 text-xs text-gray-500 dark:text-gray-400 min-h-[1.25rem]">
                      {product.calories != null && (
                        <span className="flex items-center shrink-0">
                          <FireIcon className="h-3.5 w-3.5 mr-1 shrink-0 text-orange-500" aria-hidden />
                          {product.calories} cal
                        </span>
                      )}
                      {product.stepsToBurn != null && (
                        <span className="flex items-center shrink-0 ml-auto">
                          <span className="mr-1 text-base leading-none select-none" aria-hidden>🚶‍♀️</span>
                          <span>{product.stepsToBurn} steps</span>
                        </span>
                      )}
                    </div>
                  )}
                  {product.allergens && product.allergens.length > 0 && (
                    <div className="mt-1.5">
                      <AllergenIcons allergenIds={product.allergens} allergenMap={allergenMap} size={18} />
                    </div>
                  )}
                  {(product.variants && product.variants.length > 0) && (
                    <div className="mt-1.5 text-xs text-blue-600 dark:text-blue-400">
                      {product.variants.length} variants
                    </div>
                  )}
                  {(product.addons && product.addons.length > 0) && (
                    <div className="mt-1 text-xs text-purple-600 dark:text-purple-400">
                      {product.addons.length} addons
                    </div>
                  )}
                </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Cart */}
      <div className="w-full lg:w-96 bg-white dark:bg-gray-800 lg:border-l border-gray-200 dark:border-gray-700 flex flex-col h-full">
        {/* Fixed Order Summary Header */}
        <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                <ShoppingCartIcon className="h-5 w-5 mr-2" />
                Order Summary
              </h3>
              {lastOrderToken !== null && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Last Token: <span className="font-medium text-gray-900 dark:text-white">#{lastOrderToken}</span>
                </p>
              )}
            </div>
            {cart.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {cart.length} {cart.length === 1 ? 'item' : 'items'}
                </span>
                <button
                  onClick={() => setShowClearCartModal(true)}
                  className="inline-flex items-center justify-center p-2 rounded-full bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800 transition-colors"
                  title="Clear cart"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {cart.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
              <ShoppingCartIcon className="h-12 w-12 mx-auto mb-4" />
              <p>Your cart is empty</p>
              <p className="text-sm">Add items to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                      {item.product.name}
                    </h4>
                    {item.selectedVariant && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {item.selectedVariant.name}
                      </p>
                    )}
                    {item.selectedAddons && item.selectedAddons.length > 0 && (
                      <div className="text-xs text-purple-600 dark:text-purple-400">
                        +{item.selectedAddons.map(a => a.name).join(', ')}
                      </div>
                    )}
                    {(() => {
                      const ids = [...(item.product.allergens || []), ...(item.selectedVariant?.allergen_ids || []), ...(item.selectedAddons || []).flatMap(a => a.allergen_ids || [])]
                      const combined = [...new Set(ids)]
                      return combined.length > 0 ? (
                        <div className="mt-1">
                          <AllergenIcons allergenIds={combined} allergenMap={allergenMap} size={14} />
                        </div>
                      ) : null
                    })()}
                    <p className="text-gray-500 dark:text-gray-400 text-xs">
                      {item.isComboItem ? (
                        <span className="flex items-center">
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {formatCurrency(getItemPrice(item))}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">(combo)</span>
                          {item.originalPrice && (
                            <span className="text-xs text-gray-400 ml-1 line-through">
                              {formatCurrency(item.originalPrice)}
                            </span>
                          )}
                        </span>
                      ) : item.isBOGOItem ? (
                        <span className="flex items-center">
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {formatCurrency(getItemPrice(item))}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">(BOGO)</span>
                          {item.paidQuantity && item.freeQuantity && (
                            <span className="text-xs text-gray-400 ml-1">
                              ({item.paidQuantity} paid, {item.freeQuantity} free)
                            </span>
                          )}
                        </span>
                      ) : item.discountOfferId && item.discountOriginalPrice ? (
                        <span className="flex items-center">
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {formatCurrency(getItemPrice(item))}
                          </span>
                          <span className="text-xs text-gray-400 ml-1 line-through">
                            {formatCurrency(item.discountOriginalPrice)}
                          </span>
                        </span>
                      ) : (
                        `${formatCurrency(getItemPrice(item))} each`
                      )}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      disabled={!registerStatus?.isOpen}
                      className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium">
                      {item.quantity}
                    </span>
                    <button 
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      disabled={!registerStatus?.isOpen}
                      className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => removeFromCart(index)}
                      disabled={!registerStatus?.isOpen}
                      className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900 text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed Bottom Section - Order Notes, Offers, Totals, Checkout */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
          {/* Order Notes - Collapsible */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setOrderNotesExpanded(prev => !prev)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center space-x-2 min-w-0">
                <DocumentTextIcon className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Order Notes</span>
              </div>
              {orderNotesExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500 shrink-0" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500 shrink-0" />
              )}
            </button>
            {orderNotesExpanded && (
              <div className="px-4 pb-4 pt-0">
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  placeholder="Special instructions..."
                />
              </div>
            )}
          </div>

          {/* Available Offers */}
          {getApplicableOffers().length > 0 && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Available Offers
            </h4>
            <div className="space-y-2">
              {getApplicableOffers().map(offer => (
                <div key={offer.id} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900 rounded-md p-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      {offer.name}
                    </p>
                    {offer.description && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {offer.description}
                      </p>
                    )}
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                      {offer.type === 'percentage' && `${offer.value}% off`}
                      {offer.type === 'fixed' && `${formatCurrency(offer.value)} off`}
                      {offer.type === 'combo' && (
                        <div>
                          <div className="text-xs font-medium">Combo deal</div>
                          {(offer as any).comboItems && (offer as any).comboItems.length > 0 && (
                            <div className="mt-1">
                              <div className="text-xs text-blue-500 dark:text-blue-300">
                                Total: {formatCurrency((offer as any).comboItems.reduce((sum: number, item: any) => sum + (item.price || item.comboPrice || 0), 0))}
                              </div>
                              <div className="text-xs text-blue-500 dark:text-blue-300 mt-0.5">
                                Includes: {(offer as any).comboItems.map((item: any) => {
                                  const product = products.find(p => p.id === (item.menuItemId || item.id || item.productId))
                                  return product ? product.name : 'Unknown Item'
                                }).join(', ')}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {offer.type === 'bogo' && (
                        <div>
                          <div className="text-xs font-medium">Buy One Get One</div>
                          {(offer as any).bogoItems && (offer as any).bogoItems.length > 0 && (
                            <div className="mt-1">
                    <div className="text-xs text-blue-500 dark:text-blue-300">
                      {(offer as any).bogoItems.map((item: any) => {
                        const product = products.find(p => p.id === (item.buyItemId || item.getItemId || item.menuItemId || item.id || item.productId))
                        const discountText = item.discountType === 'free' ? 'Free' : 
                                          item.discountType === 'percentage' ? `${item.discountValue}% off` :
                                          `${formatCurrency(item.discountValue)} off`
                        return product ? `${product.name}: ${discountText}` : 'Unknown Item'
                      }).join(', ')}
                    </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => applyOffer(offer)}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2.5 py-1 rounded-md font-medium"
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Applied Offers */}
          {appliedOffers.length > 0 && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Applied Offers
            </h4>
            <div className="space-y-2">
              {appliedOffers.map(offer => (
                <div key={offer.id} className="flex items-center justify-between bg-green-50 dark:bg-green-900 rounded-md p-2">
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">
                      {offer.name}
                    </p>
                    {offer.description && (
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {offer.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeOffer(offer.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Discount - Collapsible */}
          {posSettings.enableDiscountSystem && (
            <div className="border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setDiscountExpanded(prev => !prev)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center space-x-2 min-w-0">
                  <ReceiptPercentIcon className="h-4 w-4 text-gray-500 shrink-0" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Discount</span>
                  {discount > 0 && (
                    <span className="text-xs text-green-600 dark:text-green-400 truncate">
                      {discountType === 'percentage' ? `${discount}%` : `${restaurantSettings.currency_symbol}${discount.toFixed(2)}`}
                    </span>
                  )}
                </div>
                {discountExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 shrink-0 ml-2" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500 shrink-0 ml-2" />
                )}
              </button>
              {discountExpanded && (
                <div className="px-4 pb-4 pt-0">
                  <div className="flex space-x-2">
                    <div 
                      onClick={() => setShowDiscountModal(true)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                    >
                      {discount > 0 ? (
                        discountType === 'percentage' ? `${discount}%` : `${restaurantSettings.currency_symbol}${discount.toFixed(2)}`
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Click to add discount</span>
                      )}
                    </div>
                    <select
                      value={discountType}
                      onChange={(e) => {
                        setDiscountType(e.target.value as 'percentage' | 'fixed')
                        setDiscount(0)
                      }}
                      className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    >
                      <option value="percentage">%</option>
                      <option value="fixed">{restaurantSettings.currency_symbol}</option>
                    </select>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Max: {discountType === 'percentage' ? `${posSettings.maxDiscountPercentage}%` : `${restaurantSettings.currency_symbol}${posSettings.maxDiscountAmount}`}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Totals with VAT Breakdown */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-1">
          {(() => {
            const totals = getCartTotals()
            return (
              <>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal (Excl. VAT):</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(totals.subtotalNet)}</span>
                </div>
                {/* Show discount in summary if there's a manual discount or combo/BOGO discounts
                    Item-wise discount offers (percentage/fixed) are already reflected in item prices, 
                    so they're not included in discountAmount */}
                {getDiscountAmount() > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                    <span className="text-red-600 dark:text-red-400">-{formatCurrency(getDiscountAmount())}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">VAT ({restaurantSettings.tax_rate || 15}%):</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(totals.vatTotal)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1">
                  <span className="text-gray-900 dark:text-white">Total (Incl. VAT):</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(totals.grossTotal)}</span>
                </div>
              </>
            )
          })()}
        </div>

          {/* Checkout Button */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            {!registerStatus?.isOpen && (
              <div className="mb-2.5 p-2.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                <p className="text-xs text-yellow-800 dark:text-yellow-200 text-center">
                  ⚠️ Cash register is closed. Please open the register to place orders.
                </p>
              </div>
            )}
            <button
              onClick={handleCheckout}
              disabled={cart.length === 0 || !registerStatus?.isOpen}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              {registerStatus?.isOpen 
                ? `Process Payment (${formatCurrency(getTotal())})`
                : 'Register Closed'
              }
            </button>
          </div>
        </div>
      </div>

      {/* Customization Modal */}
      {showCustomizationModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Customize {selectedProduct.name}
            </h3>
            
            {/* Variants */}
            {selectedProduct.variants && selectedProduct.variants.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  {selectedProduct.variants.map(variant => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                        selectedVariant?.id === variant.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-medium text-sm mb-1">{variant.name}</div>
                        {variant.priceModifier > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            +{formatCurrency(variant.priceModifier)}
                          </div>
                        )}
                        {variant.priceModifier === 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            No extra charge
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Addons */}
            {selectedProduct.addons && selectedProduct.addons.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Add-ons</h4>
                <div className="grid grid-cols-2 gap-3">
                  {selectedProduct.addons.map(addon => (
                    <button
                      key={addon.id}
                      onClick={() => {
                        if (selectedAddons.some(a => a.id === addon.id)) {
                          setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id))
                        } else {
                          setSelectedAddons([...selectedAddons, addon])
                        }
                      }}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                        selectedAddons.some(a => a.id === addon.id)
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-medium text-sm mb-1">{addon.name}</div>
                        {addon.price > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            +{formatCurrency(addon.price)}
                          </div>
                        )}
                        {addon.price === 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Free
                          </div>
                        )}
                        {addon.isRequired && (
                          <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Required
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowCustomizationModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  addItemToCart(selectedProduct, selectedVariant || undefined, selectedAddons)
                  setShowCustomizationModal(false)
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upsell Modal */}
      {showUpsellModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <SparklesIcon className="h-5 w-5 mr-2 text-yellow-500" />
              Special Offers
            </h3>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                You qualify for these special offers!
              </p>
              
              <div className="space-y-3">
                {products.flatMap(p => p.upsells || [])
                  .filter(upsell => getSubtotal() >= upsell.triggerAmount)
                  .map(upsell => {
                    const isApplied = appliedUpsells.some(au => au.id === upsell.id)
                    return (
                      <div key={upsell.id} className={`rounded-lg p-4 ${isApplied ? 'bg-green-50 dark:bg-green-900 border-2 border-green-500' : 'bg-yellow-50 dark:bg-yellow-900'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1 flex items-center gap-2">
                              {upsell.name}
                              {isApplied && <CheckIcon className="w-4 h-4 text-green-600" />}
                            </h4>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">
                              Save {formatCurrency(upsell.discount)} on this order!
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Order value: {formatCurrency(getSubtotal())} / {formatCurrency(upsell.triggerAmount)} required
                            </p>
                          </div>
                        </div>
                        {!isApplied ? (
                          <button
                            onClick={() => {
                              setAppliedUpsells([...appliedUpsells, upsell])
                              setToast({
                                message: `${upsell.name} applied! You saved ${formatCurrency(upsell.discount)}`,
                                type: 'success'
                              })
                            }}
                            className="mt-3 w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                          >
                            Apply Discount
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setAppliedUpsells(appliedUpsells.filter(au => au.id !== upsell.id))
                              setToast({
                                message: `${upsell.name} removed`,
                                type: 'success'
                              })
                            }}
                            className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                          >
                            Remove Discount
                          </button>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowUpsellModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Skip
              </button>
              <button
                onClick={() => {
                  setShowUpsellModal(false)
                  setShowPaymentModal(true)
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offers Modal */}
      {showOffersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Available Offers
            </h3>
            
            <div className="space-y-4">
              {/* Visit-Based Offers Section */}
              {availableVisitBasedOffers.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <StarIcon className="w-5 h-5 text-yellow-500" />
                    Visit-Based Rewards ({customerVisitCount || 0} visits)
                  </h4>
                  <div className="space-y-3">
                    {availableVisitBasedOffers.map(offer => {
                      const isApplied = appliedVisitBasedOffers.some(ao => ao.id === offer.id)
                      return (
                        <div key={offer.id} className={`border-2 rounded-lg p-4 ${isApplied ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-orange-200 dark:border-orange-700'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                {offer.name}
                                {isApplied && <CheckIcon className="w-4 h-4 text-green-600" />}
                              </h5>
                              {offer.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{offer.description}</p>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Requires {offer.visit_count} visit{offer.visit_count !== 1 ? 's' : ''}
                              </p>
                            </div>
                            {!isApplied && (
                              <button
                                onClick={() => handleApplyVisitBasedOffer(offer)}
                                className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-3 py-1 rounded transition-colors"
                              >
                                Apply
                              </button>
                            )}
                            {isApplied && (
                              <span className="text-green-600 dark:text-green-400 text-sm font-medium">Applied</span>
                            )}
                          </div>
                          
                          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                            {offer.offer_type === 'free_item' && offer.free_item && (
                              <div className="flex items-center gap-2">
                                <span>Free Item:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{offer.free_item.name}</span>
                              </div>
                            )}
                            {offer.offer_type === 'percentage_discount' && (
                              <div>Discount: {offer.discount_value}% off</div>
                            )}
                            {offer.offer_type === 'fixed_discount' && (
                              <div>Discount: {restaurantSettings.currency_symbol || 'SAR'} {offer.discount_value} off</div>
                            )}
                            {offer.min_order_amount > 0 && (
                              <div>Min order: {formatCurrency(offer.min_order_amount)}</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              
              {/* Regular Offers Section */}
              {availableOffers.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Regular Offers</h4>
                </div>
              )}
              
              {availableOffers.map(offer => {
                const applicableProducts = products.filter(product => 
                  offer.applicableItems.includes(product.id)
                )
                
                return (
                  <div key={offer.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">{offer.name}</h4>
                        {offer.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{offer.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => applyOffer(offer)}
                        className="text-blue-600 hover:text-blue-500 text-sm font-medium ml-2"
                      >
                        Apply
                      </button>
                    </div>
                    
                    {/* Applicable Products */}
                    {applicableProducts.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Applicable Products:</p>
                        <div className="space-y-1">
                          {applicableProducts.map(product => (
                            <div key={product.id} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-400">{product.name}</span>
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                {formatCurrency(product.basePrice)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Offer Details */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      {offer.minOrderAmount && (
                        <div>Min order: {formatCurrency(offer.minOrderAmount)}</div>
                      )}
                      {offer.maxDiscount && (
                        <div>Max discount: {formatCurrency(offer.maxDiscount)}</div>
                      )}
                      {offer.type === 'percentage' && (
                        <div>Discount: {offer.value}% off</div>
                      )}
                      {offer.type === 'fixed' && (
                        <div>Discount: {formatCurrency(offer.value)} off</div>
                      )}
                      {offer.type === 'combo' && (offer as any).comboItems && (offer as any).comboItems.length > 0 && (
                        <div>Combo deal: {formatCurrency((offer as any).comboItems.reduce((sum: number, item: any) => sum + (item.price || 0), 0))}</div>
                      )}
                      {offer.type === 'bogo' && (offer as any).bogoItems && (offer as any).bogoItems.length > 0 && (
                        <div>Buy One Get One: {(offer as any).bogoItems[0].discountType === 'free' ? 'Free' : `${(offer as any).bogoItems[0].discountValue}% off`}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowOffersModal(false)}
                className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Details Modal (for Catering Orders) */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delivery Details
            </h3>
            
            <div className="space-y-4">
              {/* Delivery Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delivery Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Delivery Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delivery Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={deliveryTime}
                  onChange={(e) => setDeliveryTime(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Delivery Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delivery Address (Optional)
                </label>
                <textarea
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  rows={3}
                  placeholder="Enter delivery address..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDeliveryModal(false)
                  setDeliveryDate('')
                  setDeliveryTime('')
                  setDeliveryAddress('')
                }}
                className="bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeliveryDetailsSubmit}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 w-full mx-4 max-h-[90vh] overflow-y-auto flex flex-col ${isSplitPayment ? 'max-w-xl' : 'max-w-4xl'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isSplitPayment ? 'Split Payment' : 'Payment Method'}
            </h3>
              {/* Split Payment Toggle */}
              <button
                onClick={() => {
                  if (isSplitPayment) {
                    resetSplitPayment()
                  } else {
                    setIsSplitPayment(true)
                    setPaymentMethod('')
                    setShowCashKeypad(false)
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  isSplitPayment
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {isSplitPayment ? '← Single Payment' : 'Split Payment →'}
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Order Total Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Order Total:</span>
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(getTotalAmount())}
                  </span>
                </div>
                {isSplitPayment && splitPayments.length > 0 && (
                  <>
                    <div className="flex justify-between items-center mt-2 text-green-600 dark:text-green-400">
                      <span>Paid:</span>
                      <span className="font-medium">{formatCurrency(getSplitPaymentsTotal())}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-gray-600 dark:text-gray-400">Remaining:</span>
                      <span className={`font-bold ${getRemainingAmount() > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                        {formatCurrency(getRemainingAmount())}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Split Payment Mode */}
              {isSplitPayment ? (
                <div className="space-y-4">
                  {/* Added Split Payments List */}
                  {splitPayments.length > 0 && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Payment Breakdown
                      </label>
                      {splitPayments.map((payment, index) => (
                        <div 
                          key={payment.id}
                          className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-green-700 dark:text-green-300">
                              #{index + 1}
                            </span>
                            <div className="flex items-center gap-2">
                              {payment.payment_method === 'cash' && <BanknotesIcon className="h-5 w-5 text-green-600" />}
                              {payment.payment_method === 'card' && <CreditCardIcon className="h-5 w-5 text-blue-600" />}
                              {payment.payment_method === 'digital' && <DevicePhoneMobileIcon className="h-5 w-5 text-purple-600" />}
                              <span className="capitalize text-gray-700 dark:text-gray-300">{payment.payment_method}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(payment.amount)}
                            </span>
                            {payment.change_amount && payment.change_amount > 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                (Change: {formatCurrency(payment.change_amount)})
                              </span>
                            )}
                            <button
                              onClick={() => removeSplitPayment(payment.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Split Payment */}
                  {getRemainingAmount() > 0 && (
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Add Payment
                      </label>
                      
                      {/* Payment Method Selection for Split */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <button
                          onClick={() => setCurrentSplitMethod('cash')}
                          className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                            currentSplitMethod === 'cash'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700'
                              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          <BanknotesIcon className="h-6 w-6 mb-1" />
                          <span className="text-xs font-medium">Cash</span>
                        </button>
                        <button
                          onClick={() => setCurrentSplitMethod('card')}
                          className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center ${
                            currentSplitMethod === 'card'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700'
                              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          <CreditCardIcon className="h-6 w-6 mb-1" />
                          <span className="text-xs font-medium">Card</span>
                        </button>
                      </div>

                      {/* Amount Input */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Amount</label>
                          <div className="flex gap-1.5">
                            <input
                              type="text"
                              value={currentSplitAmount}
                              onChange={(e) => setCurrentSplitAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                              onFocus={() => {
                                setSplitKeypadTarget('amount')
                                setShowSplitKeypad(true)
                              }}
                              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium"
                              placeholder="0.00"
                            />
                            <button
                              onClick={() => setCurrentSplitAmount(getRemainingAmount().toFixed(2))}
                              className="px-2 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium hover:bg-blue-200 dark:hover:bg-blue-900/50"
                            >
                              Full
                            </button>
                          </div>
                        </div>
                        {currentSplitMethod === 'cash' && (
                          <div>
                            <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Received</label>
                            <input
                              type="text"
                              value={currentSplitReceived}
                              onChange={(e) => setCurrentSplitReceived(e.target.value.replace(/[^0-9.]/g, ''))}
                              onFocus={() => {
                                setSplitKeypadTarget('received')
                                setShowSplitKeypad(true)
                              }}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium"
                              placeholder="0.00"
                            />
                          </div>
                        )}
                      </div>

                      {/* Split Keypad */}
                      {showSplitKeypad && (
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                            <button
                              key={key}
                              onClick={() => handleSplitKeypadInput(key)}
                              className={`p-3 rounded-lg border transition-all font-medium ${
                                key === 'backspace'
                                  ? 'border-red-300 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {key === 'backspace' ? '⌫' : key}
                            </button>
                          ))}
                          <button
                            onClick={() => handleSplitKeypadInput('clear')}
                            className="p-3 rounded-lg border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            C
                          </button>
                        </div>
                      )}

                      {/* Quick Amount Buttons */}
                      <div className="grid grid-cols-4 gap-2 mb-4">
                        <button
                          onClick={() => setCurrentSplitAmount((getRemainingAmount() / 2).toFixed(2))}
                          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          50%
                        </button>
                        <button
                          onClick={() => setCurrentSplitAmount((getRemainingAmount() / 3).toFixed(2))}
                          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          1/3
                        </button>
                        <button
                          onClick={() => setCurrentSplitAmount((getRemainingAmount() / 4).toFixed(2))}
                          className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          25%
                        </button>
                        <button
                          onClick={() => setCurrentSplitAmount(getRemainingAmount().toFixed(2))}
                          className="p-2 rounded-lg border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          Rest
                        </button>
                      </div>

                      {/* Add Payment Button */}
                      <button
                        onClick={addSplitPayment}
                        disabled={!currentSplitAmount || parseFloat(currentSplitAmount) <= 0}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                        Add {currentSplitMethod.charAt(0).toUpperCase() + currentSplitMethod.slice(1)} Payment
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Single Payment Mode */
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Select Payment Method
                </label>
                <div className={`grid gap-3 ${orderType === 'catering' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <button
                    onClick={() => {
                      setPaymentMethod('cash')
                      setShowCashKeypad(true)
                    }}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center ${
                      paymentMethod === 'cash'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <BanknotesIcon className="h-8 w-8 mb-2" />
                    <span className="font-medium">Cash</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setPaymentMethod('card')
                      setShowCashKeypad(false)
                    }}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center ${
                      paymentMethod === 'card'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <CreditCardIcon className="h-8 w-8 mb-2" />
                    <span className="font-medium">Card</span>
                  </button>
                  
                  {orderType === 'catering' && (
                    <button
                      onClick={() => {
                        setPaymentMethod('credit')
                        setShowCashKeypad(false)
                      }}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center ${
                        paymentMethod === 'credit'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <ClockIcon className="h-8 w-8 mb-2" />
                      <span className="font-medium">Credit</span>
                    </button>
                  )}
                </div>
              </div>
              )}

              {/* Cash Payment Keypad - floating on the right */}
              {paymentMethod === 'cash' && showCashKeypad && (
                <div className="flex gap-6 flex-col lg:flex-row">
                  {/* Left: Amount display */}
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Amount</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatCurrency(getTotalAmount())}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Received Amount</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white">
                            {receivedAmount ? formatCurrency(parseFloat(receivedAmount)) : '0.00'}
                          </p>
                        </div>
                      </div>
                      {receivedAmount && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                          <div className="text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Balance to Return</p>
                            <p className={`text-2xl font-bold ${
                              getBalanceAmount() >= 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {formatCurrency(Math.abs(getBalanceAmount()))}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Floating keypad panel */}
                  <div className="shrink-0 w-full lg:w-auto">
                    <div className="rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/80 p-4 shadow-lg lg:min-w-[280px]">
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                          <button
                            key={key}
                            onClick={() => handleKeypadInput(key)}
                            className={`p-3 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                              key === 'backspace'
                                ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {key === 'backspace' ? '⌫' : key}
                          </button>
                        ))}
                        <button
                          onClick={() => handleKeypadInput('clear')}
                          className="p-3 rounded-lg border-2 border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400 font-medium text-lg transition-all duration-200 col-span-2"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <button
                          onClick={() => setReceivedAmount(getTotalAmount().toString())}
                          className="p-3 rounded-lg border-2 border-blue-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-500 text-blue-600 dark:text-blue-400 font-medium transition-all duration-200"
                        >
                          Exact
                        </button>
                        <button
                          onClick={() => setReceivedAmount((getTotalAmount() + 5).toString())}
                          className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200"
                        >
                          +5
                        </button>
                        <button
                          onClick={() => setReceivedAmount((getTotalAmount() + 10).toString())}
                          className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200"
                        >
                          +10
                        </button>
                        <button
                          onClick={() => setReceivedAmount((getTotalAmount() + 20).toString())}
                          className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200"
                        >
                          +20
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowPaymentModal(false)
                    setReceivedAmount('')
                    setShowCashKeypad(false)
                    setPaymentMethod('')
                    resetSplitPayment()
                  }}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (isSplitPayment) {
                      // Validate split payments
                      if (splitPayments.length === 0) {
                        setToast({ message: 'Please add at least one payment.', type: 'error' })
                        return
                      }
                      if (getRemainingAmount() > 0.01) {
                        setToast({ message: `Please pay the remaining ${formatCurrency(getRemainingAmount())}`, type: 'error' })
                        return
                      }
                      processPayment()
                    } else {
                      // Single payment validation
                    if (!paymentMethod) {
                      setToast({ message: 'Please select a payment method.', type: 'error' })
                      return
                    }
                    if (paymentMethod === 'cash' && (!receivedAmount || parseFloat(receivedAmount) < getTotalAmount())) {
                      setToast({ message: 'Please enter a valid received amount that covers the total.', type: 'error' })
                      return
                    }
                    processPayment()
                    }
                  }}
                  disabled={isProcessingPayment || (isSplitPayment && getRemainingAmount() > 0.01)}
                  className={`flex-1 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center ${
                    isProcessingPayment || (isSplitPayment && getRemainingAmount() > 0.01)
                      ? 'bg-gray-400 cursor-not-allowed text-gray-200' 
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isProcessingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      {isSplitPayment ? 'Complete Split Payment' : 'Complete Payment'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {isGuestCustomer ? 'Add Customer Details' : 'Update Customer Details'}
            </h3>
            
            <div className="space-y-4">
              {/* Mobile Number Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mobile Number {orderType === 'catering' ? '*' : ''}
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={customerFormData.mobileNumber || customerFormData.phone || ''}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '')
                      handleCustomerFormChange('mobileNumber', value)
                      handleCustomerFormChange('phone', value)
                      if (value.length >= 10) {
                        handleCustomerSearch(value)
                      }
                    }}
                    onFocus={() => setShowPhoneKeypad(true)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter mobile number"
                    required={orderType === 'catering'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPhoneKeypad(!showPhoneKeypad)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded text-gray-700 dark:text-gray-300"
                    title="Toggle keypad"
                  >
                    ⌨️
                  </button>
                </div>
                {showPhoneKeypad && (
                  <div className="mt-3">
                    <NumericKeypad
                      value={customerFormData.mobileNumber || customerFormData.phone || ''}
                      onChange={(value) => {
                        handleCustomerFormChange('mobileNumber', value)
                        handleCustomerFormChange('phone', value)
                        if (value.length >= 10) {
                          handleCustomerSearch(value)
                        }
                      }}
                      onClose={() => setShowPhoneKeypad(false)}
                      maxLength={15}
                    />
                  </div>
                )}
              </div>

              {/* Company Name and VAT - Only for Catering Orders */}
              {orderType === 'catering' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={customerFormData.companyName || ''}
                      onChange={(e) => handleCustomerFormChange('companyName', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={customerFormData.vatNumber || ''}
                      onChange={(e) => handleCustomerFormChange('vatNumber', e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter VAT number"
                    />
                  </div>
                </>
              )}

              {/* Dynamic Customer Fields */}
              {customerFields
                .filter(field => field.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(field => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {field.fieldLabel}
                      {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {field.fieldType === 'text' && (
                      <input
                        type="text"
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                      />
                    )}
                    
                    {field.fieldType === 'email' && (
                      <input
                        type="email"
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                      />
                    )}
                    
                    {field.fieldType === 'number' && (
                      <input
                        type="number"
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, parseInt(e.target.value) || '')}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                      />
                    )}
                    
                    {field.fieldType === 'date' && (
                      <input
                        type="date"
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    )}
                    
                    {field.fieldType === 'select' && field.options && (
                      <select
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, e.target.value)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select {field.fieldLabel.toLowerCase()}</option>
                        {field.options.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    )}
                    
                    {field.fieldType === 'textarea' && (
                      <textarea
                        value={String(customerFormData[field.fieldName as keyof Customer] || '')}
                        onChange={(e) => handleCustomerFormChange(field.fieldName, e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                      />
                    )}
                  </div>
                ))}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCustomerModal(false)
                  setShowPhoneKeypad(true)
                  setCustomerFormData({})
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCustomer}
                disabled={orderType === 'catering' && !(customerFormData.mobileNumber || customerFormData.phone || selectedCustomer?.mobileNumber || selectedCustomer?.phone)}
                className={`flex-1 font-medium py-2 px-4 rounded-lg transition-colors ${
                  orderType === 'catering' && !(customerFormData.mobileNumber || customerFormData.phone || selectedCustomer?.mobileNumber || selectedCustomer?.phone)
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isGuestCustomer ? 'Save Details' : 'Update Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Selector Modal */}
      {showTableSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Select Table
              </h3>
              <button
                onClick={() => setShowTableSelector(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Table Grid */}
            <div className="grid grid-cols-4 gap-3">
              {tables.length === 0 ? (
                <div className="col-span-4 text-center py-8 text-gray-500 dark:text-gray-400">
                  No tables available. Please add tables in settings.
                </div>
              ) : (
                tables.map((table: any) => (
                  <button
                    key={table.id}
                    onClick={() => {
                      setSelectedTable(table.number)
                      setSelectedTableId(table.id)
                      setShowTableSelector(false)
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedTable === table.number
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : table.status === 'occupied'
                        ? 'border-red-300 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="text-lg font-bold">{table.number}</div>
                    <div className="text-xs mt-1">
                      {table.status === 'occupied' ? 'Occupied' : 'Available'}
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowTableSelector(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-[9999]">
          <div className={`w-80 bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden ${
            toast.type === 'success' ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
          }`}>
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {toast.type === 'success' ? (
                    <CheckIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <XMarkIcon className="h-6 w-6 text-red-500" />
                  )}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className={`text-base font-medium ${
                    toast.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                  }`}>
                    {toast.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    className="bg-white dark:bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => setToast(null)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Register Opening Modal */}
      {showOpeningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Open Cash Register</h2>
              <button
                onClick={() => {
                  // Prevent closing - register must be opened
                  if (registerStatus?.needsOpening) {
                    setToast({ message: 'You must open the register to continue', type: 'error' })
                    return
                  }
                  setShowOpeningModal(false)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                disabled={registerStatus?.needsOpening}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Opening Cash Amount
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {openingCash ? formatCurrency(parseFloat(openingCash)) : '0.00'}
                  </p>
                </div>
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-4 gap-3">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleRegisterKeypadInput(key, false)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                      key === 'backspace'
                        ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {key === 'backspace' ? '⌫' : key}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleRegisterKeypadInput('clear', false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={handleOpenRegister}
                  disabled={(() => {
                    if (isProcessingRegister || !openingCash) return true
                    const amount = parseFloat(openingCash)
                    if (isNaN(amount)) return true
                    return posSettings?.allowZeroCashRegisterOpening ? amount < 0 : amount <= 0
                  })()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isProcessingRegister ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Opening...
                    </span>
                  ) : (
                    'Open Register'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Register Closing Modal */}
      {showClosingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Close Cash Register</h2>
              <button
                onClick={() => setShowClosingModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {registerStatus?.session && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Opening Cash:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(registerStatus.session.opening_cash || 0)}
                  </span>
                </div>
                {registerStatus.session.expected_cash && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Expected Cash:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(registerStatus.session.expected_cash)}
                    </span>
                  </div>
                )}
                {registerStatus.session.payment_breakdown && (
                  <>
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-2 mt-2">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Payment Summary:</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600 dark:text-gray-400">Cash Sales:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(registerStatus.session.payment_breakdown.cash?.sales || 0)} ({registerStatus.session.payment_breakdown.cash?.count || 0} txns)
                          </span>
                        </div>
                        {registerStatus.session.payment_breakdown.cash?.refunds > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">Cash Refunds:</span>
                            <span className="font-medium text-red-600 dark:text-red-400">
                              -{formatCurrency(registerStatus.session.payment_breakdown.cash?.refunds || 0)}
                            </span>
                          </div>
                        )}
                        {registerStatus.session.payment_breakdown.cash?.expenses > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">Cash Expenses:</span>
                            <span className="font-medium text-orange-600 dark:text-orange-400">
                              -{formatCurrency(registerStatus.session.payment_breakdown.cash?.expenses || 0)} ({registerStatus.session.payment_breakdown.cash?.expense_count || 0} items)
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-xs font-semibold">
                          <span className="text-gray-700 dark:text-gray-300">Card Payments:</span>
                          <span className="text-blue-600 dark:text-blue-400">
                            {formatCurrency(registerStatus.session.payment_breakdown.card?.payments || 0)}
                          </span>
                        </div>
                        {registerStatus.session.payment_breakdown.card?.refunds > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">Card Refunds:</span>
                            <span className="font-medium text-red-600 dark:text-red-400">
                              -{formatCurrency(registerStatus.session.payment_breakdown.card?.refunds || 0)}
                            </span>
                          </div>
                        )}
                        {registerStatus.session.payment_breakdown.card?.count > 0 && (
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Card Transactions:</span>
                            <span>{registerStatus.session.payment_breakdown.card?.count || 0}</span>
                          </div>
                        )}
                        {/* Split Payment Breakdown */}
                        {registerStatus.session.payment_breakdown.split && 
                         registerStatus.session.payment_breakdown.split.total_payments > 0 && (
                          <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                                Split Payments
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({registerStatus.session.payment_breakdown.split.total_orders} orders)
                              </span>
                            </div>
                            {registerStatus.session.payment_breakdown.split.cash_amount > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600 dark:text-gray-400">└ Cash (from split):</span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  {formatCurrency(registerStatus.session.payment_breakdown.split.cash_amount)}
                                </span>
                              </div>
                            )}
                            {registerStatus.session.payment_breakdown.split.card_amount > 0 && (
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600 dark:text-gray-400">└ Card (from split):</span>
                                <span className="font-medium text-blue-600 dark:text-blue-400">
                                  {formatCurrency(registerStatus.session.payment_breakdown.split.card_amount)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Closing Cash Amount
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {closingCash ? formatCurrency(parseFloat(closingCash)) : '0.00'}
                  </p>
                </div>
                {registerStatus?.session?.expected_cash && closingCash && (
                  <div className="mt-2 text-center">
                    <p className={`text-sm font-medium ${
                      parseFloat(closingCash) - (registerStatus.session.expected_cash || 0) >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {parseFloat(closingCash) - (registerStatus.session.expected_cash || 0) >= 0 ? 'Over: ' : 'Short: '}
                      {formatCurrency(Math.abs(parseFloat(closingCash) - (registerStatus.session.expected_cash || 0)))}
                    </p>
                  </div>
                )}
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-4 gap-3">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleRegisterKeypadInput(key, true)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                      key === 'backspace'
                        ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {key === 'backspace' ? '⌫' : key}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handlePrintClosingReport}
                  disabled={isPrintingClosingReport || isProcessingRegister}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isPrintingClosingReport ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent shrink-0" />
                      <span>Printing...</span>
                    </>
                  ) : (
                    <>
                      <PrinterIcon className="h-5 w-5 shrink-0" aria-hidden />
                      <span>Print</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleRegisterKeypadInput('clear', true)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                >
                  <TrashIcon className="h-5 w-5 shrink-0" aria-hidden />
                  <span>Clear</span>
                </button>
                <button
                  onClick={handleCloseRegister}
                  disabled={isProcessingRegister || !closingCash || parseFloat(closingCash) < 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isProcessingRegister ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent shrink-0" />
                      <span>Closing...</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-5 w-5 shrink-0" aria-hidden />
                      <span>Close</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Entry Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Enter Discount</h2>
              <button
                onClick={() => {
                  setShowDiscountModal(false)
                  setDiscountInputValue('')
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Discount Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discount Type
              </label>
              <select
                value={discountType}
                onChange={(e) => {
                  setDiscountType(e.target.value as 'percentage' | 'fixed')
                  setDiscountInputValue('')
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ({restaurantSettings.currency_symbol})</option>
              </select>
            </div>

            {/* Discount Amount Display */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Discount Amount
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {discountInputValue ? (
                    discountType === 'percentage' 
                      ? `${discountInputValue}%`
                      : `${restaurantSettings.currency_symbol}${discountInputValue}`
                  ) : (
                    '0' + (discountType === 'percentage' ? '%' : ` ${restaurantSettings.currency_symbol}`)
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Max: {discountType === 'percentage' 
                    ? `${posSettings.maxDiscountPercentage}%` 
                    : `${restaurantSettings.currency_symbol}${posSettings.maxDiscountAmount}`}
                </p>
              </div>
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                <button
                  key={key}
                  onClick={() => handleDiscountKeypadInput(key)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                    key === 'backspace'
                      ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {key === 'backspace' ? '⌫' : key}
                </button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDiscountModal(false)
                  setDiscountInputValue('')
                }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyDiscount}
                disabled={!discountInputValue || parseFloat(discountInputValue) <= 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Authorization Modal */}
      {showDiscountAuthModal && pendingDiscount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Supervisor Authorization Required</h2>
              <button
                onClick={() => {
                  setShowDiscountAuthModal(false)
                  setPendingDiscount(null)
                  setAuthCode('')
                  setDiscount(0)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Discount Details */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Discount Type:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {pendingDiscount.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Discount Value:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {pendingDiscount.type === 'percentage' 
                    ? `${pendingDiscount.value}%`
                    : `${restaurantSettings.currency_symbol}${pendingDiscount.value.toFixed(2)}`
                  }
                </span>
              </div>
              {pendingDiscount.type === 'percentage' && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Discount Amount:</span>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {formatCurrency((getSubtotal() * pendingDiscount.value) / 100)}
                  </span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Supervisor Authorization Required:</strong> Enter your Google Authenticator code to apply this discount.
                </p>
              </div>
            </div>

            {/* Verification Code Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter 6-Digit Verification Code
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div
                      key={index}
                      className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                        index < authCode.length
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {authCode[index] || ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map((key) => (
                <button
                  key={key}
                  onClick={() => handleAuthKeypadInput(key)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                    key === 'backspace'
                      ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                      : key === 'clear'
                      ? 'border-yellow-300 dark:border-yellow-600 hover:border-yellow-400 dark:hover:border-yellow-500 text-yellow-600 dark:text-yellow-400'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                </button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDiscountAuthModal(false)
                  setShowDiscountModal(true)
                  setPendingDiscount(null)
                  setAuthCode('')
                }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleVerifyDiscountAuthorization}
                disabled={isVerifyingAuth || authCode.length !== 6}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isVerifyingAuth ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </span>
                ) : (
                  'Authorize & Apply'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreditDinnerModal
        open={showCreditDinnerModal}
        initialCoName={creditDinnerCoName}
        initialCustomerName={creditDinnerCustomerName}
        currencySymbol={restaurantSettings.currency_symbol}
        onCancel={() => {
          setShowCreditDinnerModal(false)
          setCreditDinnerModalContext('payment')
          setPendingDuplicateRequestId(null)
          setPendingFinalRequestId(null)
          setPendingFinalInvoicePayload(null)
        }}
        onConfirm={({ coName, customerName }) => {
          setCreditDinnerCoName(coName)
          setCreditDinnerCustomerName(customerName)
          setShowCreditDinnerModal(false)
          const ctx = creditDinnerModalContext
          const dupId = pendingDuplicateRequestId
          const finId = pendingFinalRequestId
          const finPay = pendingFinalInvoicePayload
          setCreditDinnerModalContext('payment')
          setPendingDuplicateRequestId(null)
          setPendingFinalRequestId(null)
          setPendingFinalInvoicePayload(null)
          if (ctx === 'payment') {
            void processPayment({ coName, customerName })
          } else if (ctx === 'bill_duplicate' && dupId) {
            void handleGenerateDuplicateInvoice(dupId, { coName, customerName })
          } else if (ctx === 'bill_final' && finId) {
            void handleGenerateFinalInvoice(finId, finPay ?? undefined, { coName, customerName })
          }
        }}
      />

      {/* Returns/Refunds Modal */}
      {showReturnsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Returns & Refunds</h2>
              <button
                onClick={() => {
                  setShowReturnsModal(false)
                  setOrderSearchInput('')
                  setSearchedOrder(null)
                  setSelectedReturnItems([])
                  setReturnReason('')
                  setCurrentReturnId(null)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {!searchedOrder ? (
              // Order Search Section
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter Order ID
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center mb-4">
                    <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white">
                      {orderSearchInput || '------'}
                    </div>
                  </div>
                </div>

                {/* Keypad */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map((key) => (
                    <button
                      key={key}
                      onClick={() => handleOrderSearchKeypadInput(key)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                        key === 'backspace'
                          ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                          : key === 'clear'
                          ? 'border-yellow-300 dark:border-yellow-600 hover:border-yellow-400 dark:hover:border-yellow-500 text-yellow-600 dark:text-yellow-400'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleSearchOrder}
                  disabled={isSearchingOrder || !orderSearchInput.trim()}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isSearchingOrder ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Searching...
                    </span>
                  ) : (
                    'Search Order'
                  )}
                </button>
              </div>
            ) : (
              // Order Details and Return Items Selection
              <div className="space-y-6">
                {/* Order Information */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Order Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Order Number:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{searchedOrder.order_number}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Date:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {new Date(searchedOrder.order_time || searchedOrder.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {searchedOrder.customer_name || searchedOrder.customer?.name || 'Guest'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {formatCurrency(parseFloat(searchedOrder.total_amount) || 0)}
                      </span>
                    </div>
                    {searchedOrder.payment_method && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Payment Method:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                          {searchedOrder.payment_method.replace('_', ' ')}
                        </span>
                      </div>
                    )}
                    {searchedOrder.payment_status && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Payment Status:</span>
                        <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          searchedOrder.payment_status === 'paid'
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : searchedOrder.payment_status === 'pending'
                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                            : searchedOrder.payment_status === 'failed'
                            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}>
                          {searchedOrder.payment_status === 'paid' && '✓ '}
                          <span className="capitalize">{searchedOrder.payment_status}</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-3">Select Items to Return</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(searchedOrder.order_items_for_modification || searchedOrder.order_items)?.map((item: any) => {
                      const isSelected = selectedReturnItems.some(ri => ri.order_item_id === item.id)
                      const refundCalc = calculateItemRefundAmount(item, searchedOrder)
                      
                      // Check if item is already refunded
                      const alreadyRefunded = refundedItemsMap.get(item.id) || 0
                      const itemQuantity = item.quantity || 1
                      const availableQuantity = itemQuantity - alreadyRefunded
                      const isFullyRefunded = availableQuantity <= 0
                      const isPartiallyRefunded = alreadyRefunded > 0 && availableQuantity > 0
                      const hasOffer = isItemInOffer(item)
                      // For items with offers, always use full available quantity
                      const returnQty = isSelected ? (hasOffer ? availableQuantity : (returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1)) : availableQuantity
                      
                      return (
                        <div
                          key={item.id}
                          onClick={() => !isFullyRefunded && handleToggleReturnItem(item)}
                          className={`p-3 rounded-lg border-2 transition-colors ${
                            isFullyRefunded
                              ? 'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                              : isSelected
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {formatItemName(item)}
                                </p>
                                {hasOffer && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
                                    Offer Applied
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Qty: {item.quantity} × {formatCurrency(parseFloat(item.unit_price) || 0)}
                                {isPartiallyRefunded && (
                                  <span className="ml-2 text-orange-600 dark:text-orange-400">
                                    ({alreadyRefunded} already refunded, {availableQuantity} available)
                                  </span>
                                )}
                                {isFullyRefunded && (
                                  <span className="ml-2 text-red-600 dark:text-red-400 font-semibold">
                                    (Fully Refunded)
                                  </span>
                                )}
                                {hasOffer && !isFullyRefunded && (
                                  <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                                    (All quantities must be returned)
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Item total (incl. VAT): {formatCurrency(refundCalc.basePrice)}
                                {refundCalc.discount > 0 && ` − Discount: ${formatCurrency(refundCalc.discount)}`}
                                {refundCalc.vat > 0 && ` → of which VAT: ${formatCurrency(refundCalc.vat)}`}
                              </p>
                              <p className={`text-sm font-semibold mt-1 ${
                                isFullyRefunded 
                                  ? 'text-red-600 dark:text-red-400' 
                                  : 'text-blue-600 dark:text-blue-400'
                              }`}>
                                {isFullyRefunded 
                                  ? 'Already Refunded' 
                                  : `Refund: ${formatCurrency((refundCalc.refundAmount / itemQuantity) * returnQty)}`}
                              </p>
                              {isSelected && !isFullyRefunded && (
                                <div className="mt-2 flex items-center gap-2">
                                  <label className="text-xs text-gray-600 dark:text-gray-400">Return Quantity:</label>
                                  {/* Show quantity chooser ONLY if item does NOT have an offer */}
                                  {!hasOffer ? (
                                    availableQuantity === 1 ? (
                                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        1
                                      </span>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            const currentQty = returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1
                                            if (currentQty > 1) {
                                              handleReturnQuantityChange(item.id, currentQty - 1, availableQuantity)
                                            }
                                          }}
                                          disabled={(returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1) <= 1}
                                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          -
                                        </button>
                                        <input
                                          type="number"
                                          min="1"
                                          max={availableQuantity}
                                          value={returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1}
                                          onChange={(e) => {
                                            const val = parseInt(e.target.value) || 1
                                            handleReturnQuantityChange(item.id, val, availableQuantity)
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-12 h-6 text-center text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            const currentQty = returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1
                                            if (currentQty < availableQuantity) {
                                              handleReturnQuantityChange(item.id, currentQty + 1, availableQuantity)
                                            }
                                          }}
                                          disabled={(returnQuantities.get(item.id) || selectedReturnItems.find(ri => ri.order_item_id === item.id)?.quantity || 1) >= availableQuantity}
                                          className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                          +
                                        </button>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                          / {availableQuantity}
                                        </span>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                                      {availableQuantity} (All - Promotion Item)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {isSelected && <CheckIcon className="h-4 w-4 text-white" />}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {selectedReturnItems.length > 0 && (
                  <>
                    {/* Return Reason */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Return Reason (Optional)
                      </label>
                      <textarea
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter reason for return..."
                      />
                    </div>

                    {/* Refund Method */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Refund Method
                      </label>
                      <select
                        value={refundMethod}
                        onChange={(e) => setRefundMethod(e.target.value as any)}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="store_credit">Store Credit</option>
                      </select>
                    </div>

                    {/* Return Summary */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2">Return Summary</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Items Selected:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{selectedReturnItems.length}</span>
                        </div>
                        {selectedReturnItems.length > 0 && (
                          <>
                            {(() => {
                              // Calculate totals by iterating through selected items
                              let totalBase = 0
                              let totalVAT = 0
                              let totalDiscount = 0
                              let totalRefund = 0
                              
                              selectedReturnItems.forEach(item => {
                                const orderItem = (searchedOrder.order_items_for_modification || searchedOrder.order_items)?.find((oi: any) => oi.id === item.order_item_id)
                                if (orderItem) {
                                  const refundCalc = calculateItemRefundAmount(orderItem, searchedOrder)
                                  const itemQty = orderItem.quantity || 1
                                  const returnQty = item.quantity || 1
                                  const scale = itemQty > 0 ? returnQty / itemQty : 1
                                  totalBase += refundCalc.basePrice * scale
                                  totalVAT += refundCalc.vat * scale
                                  totalDiscount += refundCalc.discount * scale
                                  totalRefund += refundCalc.refundAmount * scale
                                }
                              })
                              
                              return (
                                <>
                                  <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
                                    <span className="text-gray-600 dark:text-gray-400">Subtotal (incl. VAT):</span>
                                    <span className="text-gray-900 dark:text-white">{formatCurrency(totalBase)}</span>
                                  </div>
                                  {totalDiscount > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                                      <span className="text-red-600 dark:text-red-400">-{formatCurrency(totalDiscount)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
                                    <span className="text-gray-600 dark:text-gray-400 font-semibold">Total Refund Amount:</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">
                                      {formatCurrency(totalRefund)}
                                    </span>
                                  </div>
                                  {totalVAT > 0 && (
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-500 dark:text-gray-500">of which VAT:</span>
                                      <span className="text-gray-600 dark:text-gray-400">{formatCurrency(totalVAT)}</span>
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Return to Inventory Checkbox */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={returnToInventory}
                          onChange={(e) => setReturnToInventory(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Return to Inventory
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                        When checked, inventory will be increased when this return is processed. Only works if "Link Inventory with Products" is enabled in Settings.
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSearchedOrder(null)
                          setOrderSearchInput('')
                          setSelectedReturnItems([])
                          setReturnReason('')
                          setReturnToInventory(false)
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Back to Search
                      </button>
                      <button
                        onClick={handleCreateReturn}
                        disabled={isProcessingReturn}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isProcessingReturn ? 'Processing...' : 'Create Return & Request Authorization'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refund Authorization Modal */}
      {showRefundAuthModal && currentReturnId && (() => {
        // Calculate actual total refund amount (VAT-inclusive, after discount) scaled by return quantity
        let totalRefundAmount = 0
        if (searchedOrder) {
          selectedReturnItems.forEach(item => {
            const orderItem = (searchedOrder.order_items_for_modification || searchedOrder.order_items)?.find((oi: any) => oi.id === item.order_item_id)
            if (orderItem) {
              const refundCalc = calculateItemRefundAmount(orderItem, searchedOrder)
              const itemQty = orderItem.quantity || 1
              const returnQty = item.quantity || 1
              const scale = itemQty > 0 ? returnQty / itemQty : 1
              totalRefundAmount += refundCalc.refundAmount * scale
            }
          })
        }
        
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Supervisor Authorization Required</h2>
                <button
                  onClick={() => {
                    setShowRefundAuthModal(false)
                    setRefundAuthCode('')
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Refund Details */}
              <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Refund Method:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{refundMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Refund Amount:</span>
                  <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency(totalRefundAmount)}
                  </span>
                </div>
                <div className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-700">
                  <p className="text-xs text-orange-800 dark:text-orange-200">
                    <strong>Supervisor Authorization Required:</strong> Enter your Google Authenticator code to authorize this refund.
                  </p>
                </div>
              </div>

            {/* Verification Code Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enter 6-Digit Verification Code
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div
                      key={index}
                      className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                        index < refundAuthCode.length
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {refundAuthCode[index] || ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map((key) => (
                <button
                  key={key}
                  onClick={() => handleRefundAuthKeypadInput(key)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                    key === 'backspace'
                      ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                      : key === 'clear'
                      ? 'border-yellow-300 dark:border-yellow-600 hover:border-yellow-400 dark:hover:border-yellow-500 text-yellow-600 dark:text-yellow-400'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                </button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRefundAuthModal(false)
                  setRefundAuthCode('')
                }}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAuthorizeRefund}
                disabled={isVerifyingRefundAuth || refundAuthCode.length !== 6}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isVerifyingRefundAuth ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </span>
                ) : (
                  'Authorize Refund'
                )}
              </button>
            </div>
          </div>
        </div>
        )
      })()}

      {/* Order Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Pending Orders Approval ({pendingOrdersCount})
              </h2>
              <button
                onClick={() => {
                  setShowApprovalModal(false)
                  setSelectedOrderForApproval(null)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {pendingOrders.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardDocumentCheckIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No pending orders</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Order {order.order_number}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Table: {order.table_number || 'N/A'} • {order.customer_name || 'Guest'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Created: {new Date(order.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {restaurantSettings.currency_symbol || 'SAR'} {(parseFloat(order.total_amount) || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Order Items:</h4>
                      <div className="space-y-2">
                        {order.order_items?.map((item: any, index: number) => (
                          <div
                            key={item.id || index}
                            className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatItemName(item)} {item.variant_name && `(${item.variant_name})`}
                              </p>
                              {item.addons && item.addons.length > 0 && (
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  Addons: {item.addons.map((a: any) => a.name || a).join(', ')}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Qty: {item.quantity} • Priority: {item.priority || 1}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {restaurantSettings.currency_symbol || 'SAR'} {Number(item.total_price || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.notes && (
                      <div className="mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Notes:</strong> {order.notes}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      {/* TODO: Enable Edit Order button in future
                      <button
                        onClick={() => {
                          setSelectedOrderForApproval(order)
                          setShowEditOrderModal(true)
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Edit Order
                      </button>
                      */}
                      <button
                        onClick={() => handleRejectOrder(order.id)}
                        disabled={isApprovingOrder}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApproveOrder(order.id)}
                        disabled={isApprovingOrder}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {isApprovingOrder ? 'Processing...' : 'Approve'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Waiter Draft Orders Modal */}
      {showDraftOrdersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Waiter Orders ({draftOrdersCount})
              </h2>
              <button
                onClick={() => {
                  setShowDraftOrdersModal(false)
                  setSelectedDraftOrder(null)
                  setIsEditingDraft(false)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {draftOrders.length === 0 ? (
              <div className="text-center py-12">
                <UserIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No waiter orders pending approval</p>
              </div>
            ) : (
              <div className="space-y-4">
                {draftOrders.map((order) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {order.order_number || `Draft-${order.id.slice(0, 8)}`}
                          </h3>
                          {order.has_pending_additional_items && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              Additional Items
                            </span>
                          )}
                          {order.is_draft && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              New Order
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Table: {order.table_number || 'N/A'} • {order.customer_name || 'Guest'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Phone: {order.customer_phone || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Created: {new Date(order.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {restaurantSettings.currency_symbol || 'SAR'} {(parseFloat(order.total_amount) || 0).toFixed(2)}
                        </p>
                        {order.has_pending_additional_items && order.pending_additional_items && (
                          <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                            +{restaurantSettings.currency_symbol || 'SAR'} {
                              (order.pending_additional_items || []).reduce((sum: number, item: any) => 
                                sum + (parseFloat(item.total_price || item.unit_price * item.quantity || 0)), 0
                              ).toFixed(2)
                            } (pending)
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Show existing order items if this is an additional items request */}
                    {order.has_pending_additional_items && order.order_items && order.order_items.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Existing Order Items:</h4>
                        <div className="space-y-2">
                          {order.order_items.map((item: any, index: number) => (
                            <div key={item.id || index} className="flex items-start justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatItemName(item)}
                                </p>
                                {item.variant_name && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    Variant: {item.variant_name}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Qty: {item.quantity}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {restaurantSettings.currency_symbol || 'SAR'} {Number(item.total_price || 0).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Show pending additional items or draft items */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {order.has_pending_additional_items ? 'Pending Additional Items:' : 'Order Items:'}
                      </h4>
                      <div className="space-y-2">
                        {(order.has_pending_additional_items ? order.pending_additional_items : order.draft_items || []).map((item: any, index: number) => (
                          <div key={index} className="flex items-start justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatItemName(item)}
                              </p>
                              {item.variant_name && (
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  Variant: {item.variant_name}
                                </p>
                              )}
                              {item.addons && item.addons.length > 0 && (
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  Addons: {item.addons.map((a: any) => a.name || a).join(', ')}
                                </p>
                              )}
                              {item.notes && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  Note: {item.notes}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Qty: {item.quantity} • Priority: {item.priority || 1}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {restaurantSettings.currency_symbol || 'SAR'} {(item.total_price || item.unit_price * item.quantity || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {order.notes && (
                      <div className="mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Notes:</strong> {order.notes}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      {/* TODO: Enable Edit Order button in future
                      {!order.has_pending_additional_items && (
                        <button
                          onClick={() => handleEditDraftOrder(order)}
                          className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        >
                          Edit Order
                        </button>
                      )}
                      */}
                      {order.has_pending_additional_items ? (
                        <button
                          onClick={async () => {
                            setIsApprovingOrder(true)
                            try {
                              const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
                              const response = await fetch(`${apiUrl}/orders/${order.id}/reject-additional-items`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' }
                              })
                              const data = await response.json()
                              if (data.success) {
                                setToast({ message: 'Additional items request rejected', type: 'success' })
                                await loadDraftOrders()
                              } else {
                                setToast({ message: data.error || 'Failed to reject', type: 'error' })
                              }
                            } catch (error) {
                              console.error('Error rejecting additional items:', error)
                              setToast({ message: 'Failed to reject', type: 'error' })
                            } finally {
                              setIsApprovingOrder(false)
                            }
                          }}
                          disabled={isApprovingOrder}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRejectDraftOrder(order.id)}
                          disabled={isApprovingOrder}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                      )}
                      <button
                        onClick={() => handleApproveDraftOrder(order.id)}
                        disabled={isApprovingOrder}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {isApprovingOrder ? 'Processing...' : 'Approve'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modification Requests Modal */}
      {showModificationRequestsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Modification Requests ({modificationRequestsCount})
              </h2>
              <button
                onClick={() => setShowModificationRequestsModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {modificationRequests.length === 0 ? (
              <div className="text-center py-12">
                <SparklesIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No modification requests pending</p>
              </div>
            ) : (
              <div className="space-y-4">
                {modificationRequests.map((request) => {
                  const order = request.orders || {}
                  const requestedChanges = request.requested_changes || {}
                  const rawRequestedCartItems = Array.isArray(requestedChanges.cartItems)
                    ? requestedChanges.cartItems
                    : []
                  const rawSnapshotItems = Array.isArray(requestedChanges.currentItems)
                    ? requestedChanges.currentItems
                    : []

                  // Deduplicate by line (same item_name + variant + addons + unit_price). When sumQuantities=true, merge quantities for display; when false, keep first (per-line for snapshot/live).
                  const mergeDuplicateLineItems = (items: any[], sumQuantities = true) => {
                    if (!sumQuantities) {
                      return (items || []).map((item) => {
                        const qty = Number(item?.quantity ?? 1)
                        const unit = Number(item?.unit_price ?? item?.unitPrice ?? 0)
                        return {
                          ...item,
                          quantity: qty,
                          unit_price: unit,
                          unitPrice: unit,
                          total_price: Number(item?.total_price ?? item?.totalPrice ?? (qty * unit)),
                          totalPrice: Number(item?.totalPrice ?? item?.total_price ?? (qty * unit))
                        }
                      })
                    }
                    const byKey = new Map<string, any>()
                    for (const item of items) {
                      const name = (item?.item_name || item?.itemName || '').toString().trim()
                      const variant = (item?.variant_name || item?.variantName || '').toString()
                      const addons = JSON.stringify(item?.addons || item?.addons || [])
                      const unit = Number(item?.unit_price ?? item?.unitPrice ?? 0)
                      const key = `${name}|${variant}|${addons}|${unit}`
                      const qty = Number(item?.quantity ?? 1)
                      if (byKey.has(key)) {
                        if (sumQuantities) {
                          const existing = byKey.get(key)
                          existing.quantity = (Number(existing.quantity) || 0) + qty
                          existing.total_price = (existing.quantity || 0) * (Number(existing.unit_price ?? existing.unitPrice) || 0)
                          existing.totalPrice = existing.total_price
                        }
                        // else: keep first occurrence (per-line), skip duplicate
                      } else {
                        const merged = { ...item, quantity: qty, unit_price: unit, unitPrice: unit }
                        merged.total_price = qty * unit
                        merged.totalPrice = merged.total_price
                        byKey.set(key, merged)
                      }
                    }
                    return Array.from(byKey.values())
                  }
                  const requestedCartItems = mergeDuplicateLineItems(rawRequestedCartItems)
                  // Snapshot: per-line (no sum) so "Requested Changes" reflects what waiter actually had (1 each, not 2 each)
                  const snapshotItems = mergeDuplicateLineItems(rawSnapshotItems, false)

                  const normalizeName = (name?: string) => name?.toLowerCase().trim() || ''
                  // const getItemKey = (item: any, idx: number, prefix: string) =>
                  //   item?.menu_item_id ||
                  //   item?.menuItemId ||
                  //   normalizeName(item?.item_name || item?.itemName) ||
                  //   `${prefix}-${idx}`

                  const snapshotList = snapshotItems.map((item: any, idx: number) => ({
                    raw: item,
                    name: normalizeName(item?.item_name || item?.itemName),
                    menuId: item?.menu_item_id || item?.menuItemId || null,
                    quantity: item?.quantity ?? 0,
                    id: item?.id || `snapshot-${idx}`,
                    matched: false
                  }))

                  const proposedList = requestedCartItems.map((item: any, idx: number) => ({
                    raw: item,
                    name: normalizeName(item?.item_name || item?.itemName),
                    menuId: item?.menu_item_id || item?.menuItemId || null,
                    quantity: item?.quantity ?? 0,
                    id: item?.id || `proposed-${idx}`,
                    matchedSnapshot: null as any
                  }))

                  proposedList.forEach((proposed: any) => {
                    let match =
                      proposed.menuId &&
                      snapshotList.find(
                        (snap: any) => !snap.matched && snap.menuId && snap.menuId === proposed.menuId
                      )

                    if (!match && proposed.name) {
                      match = snapshotList.find(
                        (snap: any) =>
                          !snap.matched &&
                          snap.name &&
                          snap.name === proposed.name
                      )
                    }

                    if (match) {
                      match.matched = true
                      proposed.matchedSnapshot = match
                    }
                  })

                  const comparisonEntries: {
                    key: string
                    status: 'added' | 'removed' | 'increased' | 'decreased'
                    snapshotItem?: any
                    proposedItem?: any
                    snapshotQty: number
                    proposedQty: number
                    diff: number
                    displayName: string
                  }[] = []

                  proposedList.forEach((proposed: any) => {
                    if (proposed.matchedSnapshot) {
                      const snapshotItem = proposed.matchedSnapshot
                      if (snapshotItem.quantity !== proposed.quantity) {
                        comparisonEntries.push({
                          key: `${snapshotItem.id}-${proposed.id}`,
                          status: proposed.quantity > snapshotItem.quantity ? 'increased' : 'decreased',
                          snapshotItem: snapshotItem.raw,
                          proposedItem: proposed.raw,
                          snapshotQty: snapshotItem.quantity,
                          proposedQty: proposed.quantity,
                          diff: proposed.quantity - snapshotItem.quantity,
                          displayName:
                            proposed.raw?.item_name ||
                            proposed.raw?.itemName ||
                            snapshotItem.raw?.item_name ||
                            snapshotItem.raw?.itemName ||
                            'Item'
                        })
                      }
                    } else {
                      comparisonEntries.push({
                        key: proposed.id,
                        status: 'added',
                        proposedItem: proposed.raw,
                        snapshotQty: 0,
                        proposedQty: proposed.quantity,
                        diff: proposed.quantity,
                        displayName: proposed.raw?.item_name || proposed.raw?.itemName || 'Item'
                      })
                    }
                  })

                  snapshotList
                    .filter((snap: any) => !snap.matched)
                    .forEach((snap: any) => {
                      comparisonEntries.push({
                        key: snap.id,
                        status: 'removed',
                        snapshotItem: snap.raw,
                        snapshotQty: snap.quantity,
                        proposedQty: 0,
                        diff: -snap.quantity,
                        displayName: snap.raw?.item_name || snap.raw?.itemName || 'Item'
                      })
                    })
                  return (
                    <div
                      key={request.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Order {order.order_number || request.order_id?.slice(0, 8)}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Table: {order.table_number || 'N/A'} • {order.customer_name || 'Guest'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Requested at: {new Date(request.requested_at).toLocaleString()}
                          </p>
                          {request.reason && (
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                              <strong>Reason:</strong> {request.reason}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                            {(Number(order.total_amount) || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {(requestedCartItems.length > 0 || snapshotItems.length > 0) && (
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-3">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            Requested Changes
                          </p>

                          {comparisonEntries.length > 0 ? (
                            <div className="space-y-2">
                              {comparisonEntries.map((entry) => {
                                const statusStyles =
                                  entry.status === 'added'
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : entry.status === 'removed'
                                    ? 'bg-red-50 border border-red-200 text-red-800'
                                    : entry.status === 'increased'
                                    ? 'bg-green-50 border border-green-200 text-green-800'
                                    : 'bg-red-50 border border-red-200 text-red-800'

                                const diffBadge =
                                  entry.status === 'added'
                                    ? `+${entry.proposedQty}`
                                    : entry.status === 'removed'
                                    ? `-${entry.snapshotQty}`
                                    : entry.status === 'increased'
                                    ? `+${Math.abs(entry.diff)}`
                                    : `-${Math.abs(entry.diff)}`

                                return (
                                  <div
                                    key={entry.key}
                                    className={`flex items-center justify-between rounded-md px-3 py-2 ${statusStyles}`}
                                  >
                                    <div>
                                      <p
                                        className={`text-sm font-semibold ${
                                          entry.status === 'removed' ? 'line-through' : ''
                                        }`}
                                      >
                                        {entry.displayName}
                                      </p>
                                      <p className="text-xs opacity-80">
                                        {entry.status === 'added'
                                          ? 'New item requested'
                                          : entry.status === 'removed'
                                          ? 'Item removed'
                                          : entry.status === 'increased'
                                          ? 'Quantity increased'
                                          : 'Quantity decreased'}
                                      </p>
                                    </div>
                                    <div className="text-right text-sm font-semibold">
                                      {entry.status === 'added' && (
                                        <span>+{entry.proposedQty} qty</span>
                                      )}
                                      {entry.status === 'removed' && (
                                        <span>-{entry.snapshotQty} qty</span>
                                      )}
                                      {entry.status === 'increased' && (
                                        <span>
                                          {entry.snapshotQty} → {entry.proposedQty}{' '}
                                          <span className="text-xs ml-1">(+{entry.diff})</span>
                                        </span>
                                      )}
                                      {entry.status === 'decreased' && (
                                        <span>
                                          {entry.snapshotQty} → {entry.proposedQty}{' '}
                                          <span className="text-xs ml-1">({diffBadge})</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              No differences between the request and the snapshot.
                            </p>
                          )}

                          {requestedCartItems.length > 0 && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                                Proposed Cart Items
                              </p>
                              <div className="space-y-2">
                                {requestedCartItems.map((item: any, idx: number) => (
                                  <div
                                    key={`${request.id}-requested-${idx}`}
                                    className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2"
                                  >
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {formatItemName(item)}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Qty: {item.quantity || 0} • Unit:{' '}
                                        {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(item.unit_price || item.unitPrice || 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(
                                          (item.quantity || 0) *
                                          (item.unit_price || item.unitPrice || 0)
                                        ).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {snapshotItems.length > 0 && (
                            <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                                Snapshot When Request Was Sent
                              </p>
                              <div className="space-y-2">
                                {snapshotItems.map((item: any, idx: number) => (
                                  <div
                                    key={`${request.id}-snapshot-${idx}`}
                                    className="flex items-center justify-between rounded border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2"
                                  >
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {formatItemName(item)}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Qty: {item.quantity || 0} • Status: {item.status || 'pending'}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(item.totalPrice ||
                                          item.total_price ||
                                          (item.quantity || 0) *
                                            (item.unitPrice || item.unit_price || 0)
                                        ).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {(() => {
                        // Use actual current order items from API (per-line so qty matches what was ordered)
                        const rawLiveItems = Array.isArray(order.order_items)
                          ? order.order_items
                          : []
                        const liveItems =
                          Array.isArray((order as any).order_items_for_modification) &&
                          (order as any).order_items_for_modification.length > 0
                            ? (order as any).order_items_for_modification
                            : mergeDuplicateLineItems(rawLiveItems, false)

                        if (liveItems.length === 0) {
                          return null
                        }

                        return (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                              Current Order Items (Live)
                            </h4>
                            <div className="space-y-2">
                              {liveItems.map((item: any, idx: number) => (
                                <div
                                  key={item.id || `live-${idx}`}
                                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      {formatItemName(item)}{' '}
                                      {item.variant_name && `(${item.variant_name})`}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Qty: {item.quantity ?? 0}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                      {(
                                        Number(item.total_price ?? item.totalPrice) ||
                                        (Number(item.quantity ?? 0) * Number(item.unit_price ?? item.unitPrice ?? 0))
                                      ).toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })()}

                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleRejectRequest('modification', request.id)}
                          disabled={isProcessingRequest}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleApproveModificationRequest(request.id)}
                          disabled={isProcessingRequest}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {isProcessingRequest ? 'Processing...' : 'Approve'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancellation Requests Modal */}
      {showCancellationRequestsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Cancellation Requests ({cancellationRequestsCount})
              </h2>
              <button
                onClick={() => setShowCancellationRequestsModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {cancellationRequests.length === 0 ? (
              <div className="text-center py-12">
                <BellIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No cancellation requests pending</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cancellationRequests.map((request) => {
                  const order = request.orders || {}
                  return (
                    <div
                      key={request.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Order {order.order_number || request.order_id?.slice(0, 8)}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Table: {order.table_number || 'N/A'} • {order.customer_name || 'Guest'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Requested at: {new Date(request.requested_at).toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                            <strong>Reason:</strong> {request.cancellation_reason}
                          </p>
                          {request.reason_details && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {request.reason_details}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                            {(Number(order.total_amount) || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {order.order_items && order.order_items.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                            Order Items
                          </h4>
                          <div className="space-y-2">
                            {consolidateOrderItems(order.order_items, { sumQuantities: false }).map((item: any, idx: number) => (
                              <div
                                key={item.id || `cancel-${idx}`}
                                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatItemName(item)}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Qty: {item.quantity} • Status: {item.status}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                    {(Number(item.total_price) || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => handleRejectRequest('cancellation', request.id)}
                          disabled={isProcessingRequest}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleApproveCancellationRequest(request.id)}
                          disabled={isProcessingRequest}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {isProcessingRequest ? 'Processing...' : 'Approve'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bill Requests Modal */}
      {showBillRequestsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Bill Requests ({billRequestsCount})
              </h2>
              <button
                onClick={() => {
                  setShowBillRequestsModal(false)
                  setBillRequestSearchTerm('')
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by table number..."
                  value={billRequestSearchTerm}
                  onChange={(e) => setBillRequestSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {(() => {
              // Filter bill requests by table number
              const filteredRequests = billRequests.filter((request) => {
                if (!billRequestSearchTerm.trim()) return true
                const tableNumber = (request.table_number || request.tables?.number || '').toString().toLowerCase()
                const searchTerm = billRequestSearchTerm.toLowerCase().trim()
                return tableNumber.includes(searchTerm)
              })

              if (filteredRequests.length === 0) {
                return (
                  <div className="text-center py-12">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {billRequestSearchTerm ? 'No bill requests found for this table number' : 'No bill requests pending'}
                    </p>
                  </div>
                )
              }

              return (
                <div className="space-y-4">
                  {filteredRequests.map((request) => {
                    const order = request.orders || {}
                    const table = request.tables || {}
                    const tableNumber = request.table_number || table.number || 'N/A'
                    const displayLineItems =
                      Array.isArray(order.order_items_for_modification) && order.order_items_for_modification.length > 0
                        ? order.order_items_for_modification
                        : deduplicateOrderItems(order.order_items || [], false, false)
                    let pendingAdditionalItems: any[] = []
                    const rawPending = order.pending_additional_items
                    if (rawPending != null) {
                      if (typeof rawPending === 'string') {
                        try {
                          pendingAdditionalItems = JSON.parse(rawPending)
                        } catch {
                          pendingAdditionalItems = []
                        }
                      } else if (Array.isArray(rawPending)) {
                        pendingAdditionalItems = rawPending
                      }
                    }
                    const showPendingAdditional =
                      order.pending_additional_items_status === 'pending_approval' && pendingAdditionalItems.length > 0
                    const hasAnyBillLines =
                      displayLineItems.length > 0 || pendingAdditionalItems.length > 0
                    const linesSubtotalInclVat = hasAnyBillLines
                      ? displayLineItems.reduce((s: number, it: any) => s + getBillRequestLineTotal(it), 0) +
                        pendingAdditionalItems.reduce((s: number, it: any) => s + getBillRequestLineTotal(it), 0)
                      : undefined
                    const billTotalsOpts =
                      linesSubtotalInclVat !== undefined ? { linesSubtotalInclVat } : undefined
                    const cardTotals = calculateBillRequestTotals(order, request.id, billTotalsOpts)
                    return (
                      <div
                        key={request.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Order {order.order_number || request.order_id?.slice(0, 8)}
                              </h3>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                Table {tableNumber}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {order.customer_name || 'Guest'}
                            </p>
                            {order.customer_phone && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Phone: {order.customer_phone}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Requested at: {new Date(request.requested_at).toLocaleString()}
                            </p>
                            {request.notes && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Notes: {request.notes}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                              {(Number(cardTotals.total) || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>

                      {(displayLineItems.length > 0 || showPendingAdditional) && (
                        <div>
                          {displayLineItems.length > 0 && (
                            <>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                Order Items
                              </h4>
                              <div className="space-y-2">
                                {displayLineItems.map((item: any, idx: number) => (
                                  <div
                                    key={item.id || idx}
                                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                                  >
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {formatItemName(item)} {item.variant_name && `(${item.variant_name})`}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Qty: {item.quantity} × {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(Number(item.unit_price) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(Number(item.total_price) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {showPendingAdditional && (
                            <div className={displayLineItems.length > 0 ? 'mt-4' : ''}>
                              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                                Pending approval (additional items)
                              </h4>
                              <div className="space-y-2">
                                {pendingAdditionalItems.map((item: any, idx: number) => (
                                  <div
                                    key={item.id || `pending-${idx}`}
                                    className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded"
                                  >
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {formatItemName(item)} {item.variant_name && `(${item.variant_name})`}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Qty: {item.quantity ?? 1} × {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(Number(item.unit_price) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                        {(Number(item.total_price) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {order.pending_additional_items_notes && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                  Notes: {order.pending_additional_items_notes}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {(order.subtotal != null || hasAnyBillLines) && (() => {
                        const totals = calculateBillRequestTotals(order, request.id, billTotalsOpts)
                        const discount = billRequestDiscounts[request.id]
                        return (
                          <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600 dark:text-gray-400">Subtotal (incl. VAT):</span>
                              <span className="text-gray-900 dark:text-white">
                                {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                {(Number(totals.subtotal) || 0).toFixed(2)}
                              </span>
                            </div>
                            {(Number(totals.discountAmount) > 0 || discount) && (
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Discount {discount && discount.type === 'percentage' ? `(${discount.value}%)` : ''}:
                                </span>
                                <span className="text-red-600 dark:text-red-400 font-medium">
                                  -{restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                  {(Number(totals.discountAmount) || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                            {Number(totals.taxAmount) > 0 && (
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-500 dark:text-gray-400">of which VAT:</span>
                                <span className="text-gray-900 dark:text-white">
                                  {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                  {(Number(totals.taxAmount) || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                            {Number(totals.serviceCharge) > 0 && (
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600 dark:text-gray-400">Service Charge:</span>
                                <span className="text-gray-900 dark:text-white">
                                  {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                  {(Number(totals.serviceCharge) || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <span className="text-gray-900 dark:text-white">Total:</span>
                              <span className="text-gray-900 dark:text-white">
                                {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                                {(Number(totals.total) || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )
                      })()}

                      {/* Invoice Status Display */}
                      {request.invoice_status && request.invoice_status !== 'pending' && (
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Invoice Status:</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              request.invoice_status === 'duplicate_generated'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : request.invoice_status === 'final_generated'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}>
                              {request.invoice_status === 'duplicate_generated' ? 'Duplicate Generated' : 'Final Generated'}
                            </span>
                          </div>
                          {request.duplicate_invoice_number && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Duplicate Invoice: <span className="font-medium">{request.duplicate_invoice_number}</span>
                            </p>
                          )}
                          {request.final_invoice_number && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Final Invoice: <span className="font-medium">{request.final_invoice_number}</span>
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-3">
                        {/* Discount Button - Only show if invoice not yet generated */}
                        {request.invoice_status === 'pending' || request.invoice_status === 'duplicate_generated' ? (
                          <button
                            onClick={() => {
                              setShowBillRequestDiscountModal(request.id)
                              if (!billRequestDiscountTypes[request.id]) {
                                setBillRequestDiscountTypes(prev => ({ ...prev, [request.id]: 'percentage' }))
                              }
                            }}
                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                              billRequestDiscounts[request.id]
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            <ReceiptPercentIcon className="h-5 w-5" />
                            {billRequestDiscounts[request.id] ? 'Edit Discount' : 'Apply Discount'}
                          </button>
                        ) : null}

                        {(request.invoice_status === 'pending' ||
                          request.invoice_status === 'duplicate_generated' ||
                          request.invoice_status == null) && (
                          <button
                            type="button"
                            onClick={() => handleCancelBillRequest(request.id)}
                            disabled={isProcessingBill}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isProcessingBill ? 'Cancelling...' : 'Cancel request'}
                          </button>
                        )}
                        
                        {/* Dual Invoicing Flow */}
                        {restaurantSettings.dual_invoicing_enabled ? (
                          <>
                            {request.invoice_status === 'pending' && (
                              <button
                                onClick={() => handleGenerateDuplicateInvoice(request.id)}
                                disabled={isProcessingBill}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessingBill ? 'Generating...' : 'Generate Duplicate Invoice'}
                              </button>
                            )}
                            {request.invoice_status === 'duplicate_generated' && (
                              <button
                                onClick={() => {
                                  const totals = calculateBillRequestTotals(order, request.id, billTotalsOpts)
                                  const orderTotal = Number(totals.total) || 0
                                  setFinalInvoicePaymentModal({ requestId: request.id, orderTotal, tableNumber: request.table_number || request.tables?.number })
                                  setFinalInvoicePaymentMethod('card')
                                  setFinalInvoicePaidAmount(orderTotal > 0 ? orderTotal.toFixed(2) : '')
                                  setFinalInvoiceSplitPayments([])
                                  setFinalInvoiceSplitCurrentAmount('')
                                  setFinalInvoiceSplitCurrentReceived('')
                                }}
                                disabled={isProcessingBill}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessingBill ? 'Generating...' : 'Generate Final Invoice'}
                              </button>
                            )}
                            {request.invoice_status === 'final_generated' && (
                              <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg">
                                Invoice Completed
                              </span>
                            )}
                          </>
                        ) : (
                          /* Single Invoicing Flow */
                          <>
                            {(request.invoice_status === 'pending' || request.invoice_status == null) && (
                              <button
                                onClick={() => {
                                  const totals = calculateBillRequestTotals(order, request.id, billTotalsOpts)
                                  const orderTotal = Number(totals.total) || 0
                                  setFinalInvoicePaymentModal({ requestId: request.id, orderTotal, tableNumber: request.table_number || request.tables?.number })
                                  setFinalInvoicePaymentMethod('card')
                                  setFinalInvoicePaidAmount(orderTotal > 0 ? orderTotal.toFixed(2) : '')
                                  setFinalInvoiceSplitPayments([])
                                  setFinalInvoiceSplitCurrentAmount('')
                                  setFinalInvoiceSplitCurrentReceived('')
                                }}
                                disabled={isProcessingBill}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessingBill ? 'Generating...' : 'Generate Final Invoice'}
                              </button>
                            )}
                            {request.invoice_status === 'final_generated' && (
                              <span className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg">
                                Invoice Completed
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Final Invoice Payment Method Modal - touch-friendly, same as POS Process Payment */}
      {finalInvoicePaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Complete Payment & Final Invoice</h2>
              <button
                onClick={() => {
                  setFinalInvoicePaymentModal(null)
                  setFinalInvoicePaidAmount('')
                  setFinalInvoiceSplitPayments([])
                  setFinalInvoiceSplitCurrentAmount('')
                  setFinalInvoiceSplitCurrentReceived('')
                }}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {finalInvoicePaymentModal.tableNumber ? `Table ${finalInvoicePaymentModal.tableNumber} · ` : ''}
              Total: {restaurantSettings?.currency_symbol || 'SAR'} {finalInvoicePaymentModal.orderTotal.toFixed(2)}
            </p>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Payment Method</label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <button
                type="button"
                onClick={() => {
                  setFinalInvoicePaymentMethod('card')
                  setFinalInvoicePaidAmount(finalInvoicePaymentModal.orderTotal.toFixed(2))
                }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center min-h-[72px] touch-manipulation ${
                  finalInvoicePaymentMethod === 'card'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <CreditCardIcon className="h-8 w-8 mb-2" />
                <span className="font-medium">Card</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFinalInvoicePaymentMethod('cash')
                  setFinalInvoicePaidAmount('')
                }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center min-h-[72px] touch-manipulation ${
                  finalInvoicePaymentMethod === 'cash'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <BanknotesIcon className="h-8 w-8 mb-2" />
                <span className="font-medium">Cash</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFinalInvoicePaymentMethod('split')
                  setFinalInvoicePaidAmount('')
                }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center min-h-[72px] touch-manipulation ${
                  finalInvoicePaymentMethod === 'split'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Squares2X2Icon className="h-8 w-8 mb-2" />
                <span className="font-medium">Split</span>
              </button>
            </div>

            {finalInvoicePaymentMethod === 'cash' && (
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Amount</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(finalInvoicePaymentModal.orderTotal)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Received Amount</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {finalInvoicePaidAmount ? formatCurrency(parseFloat(finalInvoicePaidAmount)) : '0.00'}
                        </p>
                      </div>
                    </div>
                    {finalInvoicePaidAmount && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {(parseFloat(finalInvoicePaidAmount) || 0) >= finalInvoicePaymentModal.orderTotal
                              ? 'Balance to Return'
                              : 'Short'}
                          </p>
                          <p className={`text-2xl font-bold ${
                            (parseFloat(finalInvoicePaidAmount) || 0) >= finalInvoicePaymentModal.orderTotal
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {formatCurrency(Math.abs((parseFloat(finalInvoicePaidAmount) || 0) - finalInvoicePaymentModal.orderTotal))}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="shrink-0 w-full sm:w-auto">
                  <div className="rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/80 p-4 shadow-lg sm:min-w-[280px]">
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleFinalInvoiceKeypadInput(key)}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 font-medium text-lg touch-manipulation ${
                            key === 'backspace'
                              ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {key === 'backspace' ? '⌫' : key}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleFinalInvoiceKeypadInput('clear')}
                        className="p-3 rounded-lg border-2 border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400 font-medium text-lg transition-all duration-200 col-span-2 touch-manipulation"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        type="button"
                        onClick={() => setFinalInvoicePaidAmount(finalInvoicePaymentModal.orderTotal.toString())}
                        className="p-3 rounded-lg border-2 border-blue-300 dark:border-blue-600 hover:border-blue-400 dark:hover:border-blue-500 text-blue-600 dark:text-blue-400 font-medium transition-all duration-200 touch-manipulation"
                      >
                        Exact
                      </button>
                      <button
                        type="button"
                        onClick={() => setFinalInvoicePaidAmount((finalInvoicePaymentModal.orderTotal + 5).toString())}
                        className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200 touch-manipulation"
                      >
                        +5
                      </button>
                      <button
                        type="button"
                        onClick={() => setFinalInvoicePaidAmount((finalInvoicePaymentModal.orderTotal + 10).toString())}
                        className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200 touch-manipulation"
                      >
                        +10
                      </button>
                      <button
                        type="button"
                        onClick={() => setFinalInvoicePaidAmount((finalInvoicePaymentModal.orderTotal + 20).toString())}
                        className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-medium transition-all duration-200 touch-manipulation"
                      >
                        +20
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Split payment: list + add form */}
            {finalInvoicePaymentMethod === 'split' && (
              <div className="mb-4 space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total due:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(finalInvoicePaymentModal.orderTotal)}</span>
                </div>
                {finalInvoiceSplitPayments.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                    {finalInvoiceSplitPayments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-1">
                        <span className="capitalize text-gray-700 dark:text-gray-300">{p.payment_method}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(p.amount)}</span>
                        <button
                          type="button"
                          onClick={() => setFinalInvoiceSplitPayments(prev => prev.filter(x => x.id !== p.id))}
                          className="p-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          aria-label="Remove"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between text-sm font-medium">
                      <span>Total paid:</span>
                      <span>{formatCurrency(finalInvoiceSplitPayments.reduce((s, p) => s + p.amount, 0))}</span>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex gap-2 flex-wrap">
                    {(['cash', 'card', 'digital'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setFinalInvoiceSplitCurrentMethod(m)}
                        className={`px-3 py-2 rounded-lg border-2 text-sm font-medium ${
                          finalInvoiceSplitCurrentMethod === m
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {m === 'cash' && <BanknotesIcon className="h-4 w-4 inline mr-1" />}
                        {m === 'card' && <CreditCardIcon className="h-4 w-4 inline mr-1" />}
                        {m === 'digital' && <DevicePhoneMobileIcon className="h-4 w-4 inline mr-1" />}
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Amount"
                    value={finalInvoiceSplitCurrentAmount}
                    onChange={(e) => setFinalInvoiceSplitCurrentAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {finalInvoiceSplitCurrentMethod === 'cash' && (
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Received (optional)"
                      value={finalInvoiceSplitCurrentReceived}
                      onChange={(e) => setFinalInvoiceSplitCurrentReceived(e.target.value.replace(/[^0-9.]/g, ''))}
                      className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const amount = parseFloat(finalInvoiceSplitCurrentAmount) || 0
                      const total = finalInvoicePaymentModal.orderTotal
                      const soFar = finalInvoiceSplitPayments.reduce((s, p) => s + p.amount, 0)
                      const remaining = total - soFar
                      if (amount <= 0) {
                        setToast({ message: 'Enter a valid amount', type: 'error' })
                        return
                      }
                      if (amount > remaining + 0.02) {
                        setToast({ message: 'Amount exceeds remaining balance', type: 'error' })
                        return
                      }
                      const payAmount = Math.min(amount, remaining)
                      const received = finalInvoiceSplitCurrentMethod === 'cash' && finalInvoiceSplitCurrentReceived
                        ? parseFloat(finalInvoiceSplitCurrentReceived) || payAmount
                        : payAmount
                      const change = finalInvoiceSplitCurrentMethod === 'cash' ? Math.max(0, received - payAmount) : 0
                      setFinalInvoiceSplitPayments(prev => [...prev, {
                        id: `fi-split-${Date.now()}`,
                        payment_method: finalInvoiceSplitCurrentMethod,
                        amount: payAmount,
                        received_amount: finalInvoiceSplitCurrentMethod === 'cash' ? received : undefined,
                        change_amount: finalInvoiceSplitCurrentMethod === 'cash' ? change : undefined
                      }])
                      setFinalInvoiceSplitCurrentAmount('')
                      setFinalInvoiceSplitCurrentReceived('')
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setFinalInvoicePaymentModal(null)
                  setFinalInvoicePaidAmount('')
                  setFinalInvoiceSplitPayments([])
                  setFinalInvoiceSplitCurrentAmount('')
                  setFinalInvoiceSplitCurrentReceived('')
                }}
                className="flex-1 py-3 px-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { requestId, orderTotal: total } = finalInvoicePaymentModal
                  if (finalInvoicePaymentMethod === 'split') {
                    const totalPaid = finalInvoiceSplitPayments.reduce((s, p) => s + p.amount, 0)
                    if (Math.abs(totalPaid - total) > 0.02) {
                      setToast({ message: 'Split total must equal order total', type: 'error' })
                      return
                    }
                    const totalChange = finalInvoiceSplitPayments.reduce((s, p) => s + (p.change_amount || 0), 0)
                    const splitPaymentDetails = {
                      total_payments: finalInvoiceSplitPayments.length,
                      cash_amount: finalInvoiceSplitPayments.filter(p => p.payment_method === 'cash').reduce((s, p) => s + p.amount, 0),
                      card_amount: finalInvoiceSplitPayments.filter(p => p.payment_method === 'card').reduce((s, p) => s + p.amount, 0),
                      digital_amount: finalInvoiceSplitPayments.filter(p => p.payment_method === 'digital').reduce((s, p) => s + p.amount, 0),
                      total_change: totalChange,
                      payments: finalInvoiceSplitPayments
                    }
                    setFinalInvoicePaymentModal(null)
                    setFinalInvoicePaidAmount('')
                    setFinalInvoiceSplitPayments([])
                    setFinalInvoiceSplitCurrentAmount('')
                    setFinalInvoiceSplitCurrentReceived('')
                    await handleGenerateFinalInvoice(requestId, {
                      payment_method: 'split',
                      paid_amount: total,
                      change_amount: totalChange,
                      is_split_payment: true,
                      split_payments: finalInvoiceSplitPayments.map(p => ({
                        payment_method: p.payment_method,
                        amount: p.amount,
                        received_amount: p.received_amount,
                        change_amount: p.change_amount
                      })),
                      split_payment_details: splitPaymentDetails
                    })
                    return
                  }
                  const paid = finalInvoicePaymentMethod === 'cash'
                    ? (finalInvoicePaidAmount ? parseFloat(finalInvoicePaidAmount) : total)
                    : total
                  if (finalInvoicePaymentMethod === 'cash' && paid < total) {
                    setToast({ message: 'Amount received cannot be less than total', type: 'error' })
                    return
                  }
                  const change = finalInvoicePaymentMethod === 'cash' ? Math.max(0, paid - total) : 0
                  setFinalInvoicePaymentModal(null)
                  setFinalInvoicePaidAmount('')
                  setFinalInvoiceSplitPayments([])
                  setFinalInvoiceSplitCurrentAmount('')
                  setFinalInvoiceSplitCurrentReceived('')
                  await handleGenerateFinalInvoice(requestId, {
                    payment_method: finalInvoicePaymentMethod,
                    paid_amount: paid,
                    change_amount: change
                  })
                }}
                disabled={
                  isProcessingBill ||
                  (finalInvoicePaymentMethod === 'split' && (
                    finalInvoiceSplitPayments.length === 0 ||
                    Math.abs(finalInvoiceSplitPayments.reduce((s, p) => s + p.amount, 0) - finalInvoicePaymentModal.orderTotal) > 0.02
                  ))
                }
                className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium touch-manipulation"
              >
                {isProcessingBill ? 'Processing...' : 'Confirm & Generate Final Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Request Discount Modal - Optimized to prevent recalculation on every keystroke */}
      {billRequestDiscountModalData ? (() => {
        const { requestId, subtotal } = billRequestDiscountModalData
        const discountType = billRequestDiscountTypes[requestId] || 'percentage'
        const discountInput = billRequestDiscountInputs[requestId] || ''
        const discount = billRequestDiscounts[requestId]
        
        // Calculate amount only when needed - parseFloat is fast, but we avoid it when not needed
        const calculatedAmount = discountType === 'percentage' && discountInput && subtotal > 0
          ? ((subtotal * parseFloat(discountInput)) / 100).toFixed(2)
          : null
        
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Apply Discount</h2>
                <button
                  onClick={() => {
                    setShowBillRequestDiscountModal(null)
                    setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Discount Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Discount Type
                </label>
                <select
                  value={discountType}
                  onChange={(e) => {
                    setBillRequestDiscountTypes(prev => ({ ...prev, [requestId]: e.target.value as 'percentage' | 'fixed' }))
                    setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
                  }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ({restaurantSettings.currency_symbol})</option>
                </select>
              </div>

              {/* Discount Amount Display */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Discount Amount
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {discountInput ? (
                      discountType === 'percentage' 
                        ? `${discountInput}%`
                        : `${restaurantSettings.currency_symbol}${discountInput}`
                    ) : (
                      '0' + (discountType === 'percentage' ? '%' : ` ${restaurantSettings.currency_symbol}`)
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Max: {discountType === 'percentage' 
                      ? `${posSettings.maxDiscountPercentage}%` 
                      : `${restaurantSettings.currency_symbol}${posSettings.maxDiscountAmount}`}
                  </p>
                  {calculatedAmount && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Amount: {restaurantSettings.currency_symbol}{calculatedAmount}
                    </p>
                  )}
                </div>
              </div>

              {/* Keypad - Memoize keypad buttons to avoid re-rendering */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleBillRequestDiscountKeypadInput(requestId, key)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                      key === 'backspace'
                        ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {key === 'backspace' ? '⌫' : key}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {discount && (
                  <button
                    onClick={() => {
                      const newDiscounts = { ...billRequestDiscounts }
                      delete newDiscounts[requestId]
                      setBillRequestDiscounts(newDiscounts)
                      setShowBillRequestDiscountModal(null)
                      setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Remove Discount
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowBillRequestDiscountModal(null)
                    setBillRequestDiscountInputs(prev => ({ ...prev, [requestId]: '' }))
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApplyBillRequestDiscount(requestId)}
                  disabled={!discountInput || parseFloat(discountInput) <= 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )
      })() : null}

      {/* Bill Request Discount Authorization Modal */}
      {showBillRequestDiscountAuthModal && pendingBillRequestDiscount && (() => {
        const requestId = pendingBillRequestDiscount.requestId
        const request = billRequests.find(r => r.id === requestId)
        const order = request?.orders || {}
        const subtotal = order.subtotal || 0
        const discountAmount = pendingBillRequestDiscount.type === 'percentage'
          ? (subtotal * pendingBillRequestDiscount.value) / 100
          : pendingBillRequestDiscount.value
        
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Supervisor Authorization Required</h2>
                <button
                  onClick={() => {
                    setShowBillRequestDiscountAuthModal(null)
                    setPendingBillRequestDiscount(null)
                    setBillRequestAuthCode('')
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Discount Details */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Discount Type:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {pendingBillRequestDiscount.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Discount Value:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {pendingBillRequestDiscount.type === 'percentage' 
                      ? `${pendingBillRequestDiscount.value}%`
                      : `${restaurantSettings.currency_symbol}${pendingBillRequestDiscount.value.toFixed(2)}`
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Discount Amount:</span>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {formatCurrency(discountAmount)}
                  </span>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Supervisor Authorization Required:</strong> Enter your Google Authenticator code to apply this discount.
                  </p>
                </div>
              </div>

              {/* Verification Code Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Enter 6-Digit Verification Code
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold ${
                          index < billRequestAuthCode.length
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {billRequestAuthCode[index] || ''}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Keypad */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleBillRequestAuthKeypadInput(key)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 font-medium text-lg ${
                      key === 'backspace'
                        ? 'border-red-300 dark:border-red-600 hover:border-red-400 dark:hover:border-red-500 text-red-600 dark:text-red-400'
                        : key === 'clear'
                        ? 'border-yellow-300 dark:border-yellow-600 hover:border-yellow-400 dark:hover:border-yellow-500 text-yellow-600 dark:text-yellow-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBillRequestDiscountAuthModal(null)
                    setShowBillRequestDiscountModal(requestId)
                    setPendingBillRequestDiscount(null)
                    setBillRequestAuthCode('')
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleVerifyBillRequestDiscountAuthorization}
                  disabled={billRequestAuthCode.length !== 6 || isVerifyingBillRequestAuth}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isVerifyingBillRequestAuth ? 'Verifying...' : 'Verify & Apply'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Receipt Print */}
      {receiptData && (() => {
        const zatcaQRCode = (receiptData as any)?.zatcaQRCode || null
        return (
          <div className="receipt-print-area">
            <ThermalReceipt
              order={receiptData}
              restaurantSettings={restaurantSettings}
              receiptLayout={posSettings?.receiptLayout}
              receiptLogoUrl={posSettings?.receiptLogoUrl}
              printerWidth={posSettings?.printerWidth || 80}
              autoPrint={!!(receiptData as any)?._autoPrint}
              onPrintComplete={() => {
                setReceiptData(null);
              }}
              zatcaQRCode={zatcaQRCode}
            />
          </div>
        )
      })()}

      {/* Pending Payments Modal */}
      {showPendingPayments && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Pending Payments ({pendingPaymentOrders.length})
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={loadPendingPaymentOrders}
                  disabled={loadingPendingPayments}
                  className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <ArrowPathIcon className={`h-5 w-5 ${loadingPendingPayments ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowPendingPayments(false)}
                  className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {loadingPendingPayments ? (
              <div className="text-center py-12">
                <ArrowPathIcon className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
                <p className="text-gray-500 dark:text-gray-400">Loading pending payments...</p>
              </div>
            ) : pendingPaymentOrders.length === 0 ? (
              <div className="text-center py-12">
                <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No pending payments</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingPaymentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Order #{order.order_number || order.id.slice(0, 8)}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {order.customer_name || 'Guest'} • {order.customer_phone || 'No phone'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {order.order_type?.replace('_', ' ').toUpperCase()} • {new Date(order.order_time).toLocaleString()}
                        </p>
                        {order.delivery_date && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Delivery: {new Date(order.delivery_date).toLocaleDateString()} {order.delivery_time}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {restaurantSettings?.currency_symbol || 'SAR'} {order.total_amount?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                          Payment Pending
                        </p>
                      </div>
                    </div>

                    {order.order_items && order.order_items.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Items:</p>
                        <div className="space-y-1">
                          {order.order_items.slice(0, 3).map((item: any, idx: number) => (
                            <p key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                              {item.quantity}x {formatItemName(item)} - {restaurantSettings?.currency_symbol || 'SAR'} {Number(item.total_price || 0).toFixed(2)}
                            </p>
                          ))}
                          {order.order_items.length > 3 && (
                            <p className="text-sm text-gray-500 dark:text-gray-500">
                              +{order.order_items.length - 3} more items
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={() => {
                          setSelectedPendingOrder(order)
                          setPendingPaymentMethod('card')
                          setPendingPaymentReceivedAmount((parseFloat(order.total_amount) || 0).toFixed(2))
                          setShowPendingPaymentKeypad(false)
                          setShowPendingPaymentModal(true)
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                      >
                        <CreditCardIcon className="h-4 w-4" />
                        <span>Mark as Paid (Card)</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPendingOrder(order)
                          setPendingPaymentMethod('cash')
                          setPendingPaymentReceivedAmount((parseFloat(order.total_amount) || 0).toFixed(2))
                          setShowPendingPaymentKeypad(true)
                          setShowPendingPaymentModal(true)
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                      >
                        <BanknotesIcon className="h-4 w-4" />
                        <span>Mark as Paid (Cash)</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Payment Modal */}
      {showPendingPaymentModal && selectedPendingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Mark Payment as Received
            </h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Order Number</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  #{selectedPendingOrder.order_number || selectedPendingOrder.id.slice(0, 8)}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {restaurantSettings?.currency_symbol || 'SAR'} {(parseFloat(selectedPendingOrder.total_amount) || 0).toFixed(2)}
                </p>
              </div>

              {pendingPaymentMethod === 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Received Amount
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={pendingPaymentReceivedAmount}
                      readOnly
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg font-semibold text-right"
                    />
                  </div>

                  {parseFloat(pendingPaymentReceivedAmount || '0') >= selectedPendingOrder.total_amount && (
                    <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-md">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Change: {restaurantSettings?.currency_symbol || 'SAR'}{' '}
                        {(parseFloat(pendingPaymentReceivedAmount || '0') - (parseFloat(selectedPendingOrder.total_amount) || 0)).toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {pendingPaymentMethod === 'card' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Payment will be marked as received via Card. Amount: {restaurantSettings?.currency_symbol || 'SAR'} {(parseFloat(selectedPendingOrder.total_amount) || 0).toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPendingPaymentModal(false)
                  setSelectedPendingOrder(null)
                  setPendingPaymentReceivedAmount('')
                  setShowPendingPaymentKeypad(false)
                }}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const receivedAmount = parseFloat(pendingPaymentReceivedAmount || '0')
                  if (pendingPaymentMethod === 'cash' && (isNaN(receivedAmount) || receivedAmount < selectedPendingOrder.total_amount)) {
                    setToast({
                      message: `Received amount must be at least ${restaurantSettings?.currency_symbol || 'SAR'} ${(parseFloat(selectedPendingOrder.total_amount) || 0).toFixed(2)}`,
                      type: 'error'
                    })
                    return
                  }
                  handleMarkPaymentReceived(
                    selectedPendingOrder.id,
                    pendingPaymentMethod,
                    pendingPaymentMethod === 'card' ? selectedPendingOrder.total_amount : receivedAmount
                  )
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Mark as Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Keypad for Pending Payment (Cash) */}
      {showPendingPaymentKeypad && selectedPendingOrder && (
        <div className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] animate-slide-in-right">
          <div className="flex items-center justify-between mb-4 px-4">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              Enter Amount
            </h4>
            <button
              onClick={() => {
                setShowPendingPaymentKeypad(false)
              }}
              className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="scale-150 origin-right pr-4">
            <NumericKeypad
              value={pendingPaymentReceivedAmount}
              onChange={setPendingPaymentReceivedAmount}
              onClose={() => {}}
            />
          </div>
        </div>
      )}

      {/* Clear Cart Confirmation Modal */}
      {showClearCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Clear Cart
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to clear the entire cart? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowClearCartModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setCart([])
                  setOrderNotes('')
                  setDiscount(0)
                  setAppliedOffers([])
                  setAppliedVisitBasedOffers([])
                  setShowClearCartModal(false)
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default POS
